import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, compressImage, formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Layers, Smartphone, Sparkles, Bell, BellRing, FileText, Map as MapIcon, ShieldCheck, Building2,
  Palette, Save, ArrowUp, ArrowDown, Upload, X, Settings, SlidersHorizontal, Gauge,
  Volume2, VolumeX, Mic2, Monitor, Radio, RefreshCw, Clock3, CheckCircle2, XCircle,
  Search, UserPlus, AlertCircle, Eye, Loader2, ImageIcon,
  Building as BuildingIcon, Factory as FactoryIcon, MapPinned as MapPinnedIcon,
} from "lucide-react";

const APP_FEATURE_LABELS = {
  ia: "Inteligência Artificial", geoespacial: "Geoespacial", salas_eletricas: "Salas Elétricas",
  pga: "Pragas em Grãos", double_check: "Double Check", gps: "GPS",
  captura_automatica: "Captura automática de fotos", ocr: "OCR (leitura de documentos)",
  qr_code: "Leitura de QR Code", assinatura_digital: "Assinatura digital",
};
const EVENT_LABELS = {
  ocorrencia_criada: "Nova ocorrência", sla_vencido: "SLA vencido", aprovacao_pendente: "Aprovação pendente",
  plano_acao: "Plano de ação", sala_eletrica_nc: "Sala Elétrica — NC / Captura", relatorio_agendado: "Relatório gerencial agendado",
};
const MENU_LABELS = {
  dashboard: "Dashboard", ocorrencias: "Ocorrências", pendencias: "Pendências", monitoramentos: "Monitoramentos",
  salas_eletricas: "Salas Elétricas", geoespacial: "Geoespacial", planos: "Planos de Ação", ia: "ASTRAL AI", relatorios: "Relatórios",
};

const ROLE_CANONICAL_ALIASES = {
  admin: "admin",
  administrador: "admin",
  technician: "tecnico",
  tecnico: "tecnico",
  supervisor: "supervisor",
  coordinator: "coordenador",
  coordenador: "coordenador",
  rt: "rt",
  responsavel_tecnico: "rt",
  client: "cliente",
  cliente: "cliente",
  gestor: "gestor",
  auditor: "auditor",
};

const ROLE_ORDER = [
  "admin",
  "gestor",
  "coordenador",
  "supervisor",
  "rt",
  "tecnico",
  "cliente",
  "auditor",
];

const canonicalRoleKey = (role) => {
  const key = String(role || "").trim().toLowerCase();
  return ROLE_CANONICAL_ALIASES[key] || key;
};

const rolePreferenceScore = (role) => {
  const canonical = canonicalRoleKey(role);
  const index = ROLE_ORDER.indexOf(canonical);
  const exactCanonical = String(role || "").trim().toLowerCase() === canonical;
  return (index < 0 ? ROLE_ORDER.length : index) * 2 + (exactCanonical ? 0 : 1);
};

const buildRoleOptions = (roles) => {
  const source = Array.isArray(roles) ? roles : Object.keys(roles || {});
  const byCanonical = new Map();

  source.forEach((rawRole) => {
    const value = String(rawRole || "").trim().toLowerCase();
    if (!value) return;
    const canonical = canonicalRoleKey(value);
    const current = byCanonical.get(canonical);
    if (!current || rolePreferenceScore(value) < rolePreferenceScore(current.value)) {
      byCanonical.set(canonical, { value, canonical });
    }
  });

  return Array.from(byCanonical.values()).sort(
    (a, b) => rolePreferenceScore(a.value) - rolePreferenceScore(b.value),
  );
};
const AI_MODULE_LABELS = {
  chat: "Chat ASTRAL AI", evidencias: "Validação de Evidências", planos_acao: "Planos de Ação",
  salas_eletricas: "Salas Elétricas", relatorios: "Relatórios", cockpit: "Cockpit 360",
};

const PERM_ACTION_LABELS = {
  view: "Visualizar",
  create: "Criar",
  edit: "Editar",
  update: "Atualizar",
  delete: "Excluir",
  review: "Revisar",
  approve: "Aprovar",
  reject: "Reprovar",
  assign: "Atribuir",
  delegate: "Delegar",
  activate: "Ativar",
  deactivate: "Desativar",
  export: "Exportar",
  import: "Importar",
  execute: "Executar",
  manage: "Gerenciar",
  configure: "Configurar",
  publish: "Publicar",
  close: "Encerrar",
  reopen: "Reabrir",
  filter: "Filtrar",
  drilldown: "Detalhar",
  share: "Compartilhar",
  complete: "Concluir",
  cancel: "Cancelar",
  capture: "Capturar",
  track: "Rastrear",
  locate: "Localizar",
  use: "Usar",
  train: "Treinar",
  validate: "Validar",
  schedule: "Agendar",
  monitor: "Monitorar",
  analyze: "Analisar",
  fumigate: "Fumigar",
  send: "Enviar",
  dismiss: "Dispensar",
  audit: "Auditar",
  search: "Pesquisar",
  purge: "Limpar",
  rollback: "Reverter",
  reset_password: "Redefinir senha",
  block: "Bloquear",
};

const getActionLabel = (action) =>
  PERM_ACTION_LABELS[String(action)] ||
  String(action)
    .replaceAll("_", " ")
    .replace(/^./, (c) => c.toUpperCase());

function ImageField({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        {value ? (
          <div className="relative group">
            <img src={value} alt={label} className="h-12 w-12 rounded-md object-cover border border-border" />
            <button onClick={() => onChange("")} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
          </div>
        ) : (
          <div className="h-12 w-12 rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground"><Upload size={14} /></div>
        )}
        <label className="text-xs text-primary cursor-pointer hover:underline">
          Enviar imagem
          <input type="file" accept="image/*" className="hidden" data-testid={testId}
            onChange={async (e) => { const f = e.target.files?.[0]; if (f) onChange(await compressImage(f)); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, desc = "", children, onSave = null, saving = false, testId = "section-card" }) {
  return (
    <Card className="p-5 border-border bg-card space-y-4" data-testid={testId}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Icon size={15} className="text-primary" /> {title}</h3>
          {desc && <p className="text-xs text-muted-foreground mt-1 max-w-2xl">{desc}</p>}
        </div>
        {onSave && (
          <Button size="sm" className="h-8 text-xs" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
            <Save size={13} className="mr-1.5" /> {saving ? "Salvando..." : "Salvar"}
          </Button>
        )}
      </div>
      {children}
    </Card>
  );
}


function RobustCommandPanel({ title, description, children, onSave, saving, testId }) {
  return (
    <Card className="p-5 border-border bg-card space-y-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div><h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Gauge size={15} className="text-primary" />{title}</h3><p className="text-xs text-muted-foreground mt-1 max-w-3xl leading-relaxed">{description}</p></div>
        {onSave && <Button size="sm" onClick={onSave} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>}
      </div>
      {children}
    </Card>
  );
}

function SettingGroup({ title, description = "", children }) {
  return <div className="rounded-xl border border-border p-4 space-y-3"><div><p className="text-sm font-semibold">{title}</p>{description && <p className="text-[11px] text-muted-foreground mt-1">{description}</p>}</div>{children}</div>;
}

function BoolSetting({ label, description = "", checked, onChange, disabled = false }) {
  return <div className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-2.5"><div className="min-w-0"><p className="text-xs font-medium">{label}</p>{description && <p className="text-[10px] text-muted-foreground mt-0.5">{description}</p>}</div><Switch checked={!!checked} disabled={disabled} onCheckedChange={onChange} /></div>;
}

function BulkSelectBar({ label = "Seleção em massa", count = 0, total = 0, onSelectAll, onClearAll, disabled = false }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-dashed border-border px-3 py-2 bg-muted/20">
      <p className="text-[10px] text-muted-foreground">
        {label}: <span className="font-semibold text-foreground">{count}/{total}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" onClick={onSelectAll} disabled={disabled || total === 0}>
          Marcar todos
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" onClick={onClearAll} disabled={disabled || total === 0}>
          Desmarcar todos
        </Button>
      </div>
    </div>
  );
}


function JsonSummary({ value, maxHeight = 360, title = "Dados técnicos" }) {
  const serialized = (() => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return String(value ?? "");
    }
  })();

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b border-border">
        <p className="text-xs font-semibold">{title}</p>
        <Badge variant="outline" className="text-[9px]">JSON</Badge>
      </div>
      <pre
        className="p-3 text-[10px] leading-relaxed overflow-auto whitespace-pre-wrap"
        style={{ maxHeight }}
      >
        {serialized}
      </pre>
    </div>
  );
}

function CommandField({ label, value, onChange, type = "text", placeholder = "", min, max, step, description }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {description && (
        <p className="text-[10px] text-muted-foreground">{description}</p>
      )}
      <Input
        value={value ?? ""}
        type={type}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
      />
    </div>
  );
}

function CommandSelect({ label, value, onChange, options, description, placeholder = "Selecione..." }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {description && (
        <p className="text-[10px] text-muted-foreground">{description}</p>
      )}
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options
            .filter(([optionValue]) => optionValue !== null && optionValue !== undefined && String(optionValue).trim() !== "")
            .map(([optionValue, optionLabel]) => (
              <SelectItem key={String(optionValue)} value={String(optionValue)}>
                {optionLabel}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CommandArrayField({ label, value, onChange, description, placeholder = "item1, item2, item3" }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {description && (
        <p className="text-[10px] text-muted-foreground">{description}</p>
      )}
      <Input
        value={Array.isArray(value) ? value.join(", ") : ""}
        placeholder={placeholder}
        onChange={(e) => onChange(
          e.target.value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
        )}
      />
    </div>
  );
}

function CommandBadge({ label, value, tone = "default" }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <Badge variant={tone} className="mt-1 text-[10px]">
        {value}
      </Badge>
    </div>
  );
}

const REPORT_CATEGORY_LABELS = {
  ocorrencias: "Ocorrências",
  sala_eletrica: "Sala Elétrica",
  pga: "PGA / Grãos",
  auditoria: "Auditoria",
  gerencial: "Gerencial",
  personalizado: "Personalizado",
};

const ADVANCED_REPORT_SECTIONS = [
  "cover",
  "summary",
  "identification",
  "period",
  "scope",
  "activities",
  "devices",
  "applications",
  "rodent_control",
  "nonconformities",
  "evidence",
  "photos",
  "analysis",
  "indicators",
  "recommendations",
  "signatures",
  "appendix",
];

const ADVANCED_MODULE_CAPABILITIES = {
  dashboard: ["view", "filter", "export", "drilldown", "configure", "share"],
  ocorrencias: ["view", "create", "edit", "close", "approve", "reopen", "export", "delete"],
  pendencias: ["view", "create", "assign", "edit", "complete", "cancel", "export"],
  monitoramentos: ["view", "create", "edit", "complete", "approve", "export", "delete"],
  salas_eletricas: ["view", "create", "edit", "capture", "approve", "export", "delete"],
  geoespacial: ["view", "track", "locate", "edit", "export", "configure"],
  planos: ["view", "create", "edit", "assign", "approve", "close", "export"],
  ia: ["view", "use", "train", "validate", "configure", "export"],
  relatorios: ["view", "create", "edit", "schedule", "approve", "export", "delete"],
  pga: ["view", "monitor", "capture", "analyze", "fumigate", "approve", "export"],
  notificacoes: ["view", "send", "configure", "publish", "dismiss", "audit"],
  usuarios: ["view", "create", "edit", "delegate", "reset_password", "block", "delete"],
  auditoria: ["view", "search", "export", "purge", "configure"],
  configuracoes: ["view", "edit", "publish", "rollback"],
};

const ADVANCED_PROFILE_DESCRIPTIONS = {
  admin: "Administrador global da plataforma. Acesso integral aos módulos e ações oficiais.",
  technician: "Operação técnica de campo, evidências, monitoramentos e execução de OS.",
  tecnico: "Alias compatível do perfil Técnico.",
  supervisor: "Supervisão operacional, validação, acompanhamento e controle de equipe.",
  coordinator: "Coordenação de operação, indicadores, equipes, clientes e aprovação.",
  coordenador: "Alias compatível do perfil Coordenador.",
  rt: "Responsável Técnico, com acesso técnico, conformidade e documentos sob seu escopo.",
  client: "Usuário do cliente, limitado ao escopo e aos módulos contratados.",
  cliente: "Alias compatível do perfil Cliente.",
};

const COMMAND_PRIORITY_OPTIONS = [
  ["critical", "Crítica"],
  ["high", "Alta"],
  ["medium", "Média"],
  ["low", "Baixa"],
];

const COMMAND_SCOPE_OPTIONS = [
  ["global", "Global"],
  ["company", "Empresa"],
  ["client", "Cliente"],
  ["unit", "Unidade"],
  ["area", "Área"],
  ["subarea", "Subárea"],
  ["user", "Usuário"],
];

export default function PlatformAdmin() {
  const [cfg, setCfg] = useState(null);
  const [configError, setConfigError] = useState("");
  const [saving, setSaving] = useState("");
  const [appKey, setAppKey] = useState("tecnico");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [clientCfg, setClientCfg] = useState(null);
  const [clientScopeDraft, setClientScopeDraft] = useState({
    company_ids: [],
    client_ids: [],
    unit_ids: [],
    area_ids: [],
    subarea_ids: [],
  });
  const [clientScopeCatalog, setClientScopeCatalog] = useState({
    company: [],
    client: [],
    unit: [],
    area: [],
    subarea: [],
  });
  const [clientHierarchyLoading, setClientHierarchyLoading] = useState(false);
  const [reportCategory, setReportCategory] = useState("gerencial");
  const [commandTab, setCommandTab] = useState("governanca");
  const [runtimeCommandSearch, setRuntimeCommandSearch] = useState("");
  const [moduleRegistryModule, setModuleRegistryModule] = useState("");
  const [notificationEvent, setNotificationEvent] = useState(Object.keys(EVENT_LABELS)[0] || "");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [notificationPreview, setNotificationPreview] = useState(null);

  const [advancedTab, setAdvancedTab] = useState("governanca");
  const [advancedModule, setAdvancedModule] = useState("");
  const [advancedRole, setAdvancedRole] = useState("");
  const [advancedReportCategory, setAdvancedReportCategory] = useState("gerencial");
  const [advancedNotificationEvent, setAdvancedNotificationEvent] = useState("");
  const [advancedPreset, setAdvancedPreset] = useState("operacao_completa");
  const [advancedJsonOpen, setAdvancedJsonOpen] = useState(false);
  const [advancedDirty, setAdvancedDirty] = useState(false);
  const [advancedAuditNote, setAdvancedAuditNote] = useState("");
  const [advancedSearch, setAdvancedSearch] = useState("");


  // ================================================================
  // GESTÃO CENTRALIZADA DE MÓDULOS / USUÁRIOS
  // ================================================================
  //
  // Cliente = teto de módulos.
  // Usuário = acesso individual dentro desse teto.
  // Ações = permissões existentes do usuário.
  // Diagnóstico = explicação da decisão efetiva.
  //
  // Esta camada usa as APIs novas do users.py e não substitui
  // as configurações existentes de cliente, login, relatórios etc.
  // ================================================================
  const [moduleCatalog, setModuleCatalog] = useState({});
  const [moduleCatalogLoading, setModuleCatalogLoading] = useState(false);
  const [accessUsers, setAccessUsers] = useState([]);
  const [accessUsersLoading, setAccessUsersLoading] = useState(false);
  const [selectedAccessUserId, setSelectedAccessUserId] = useState("");
  const [selectedUserAccess, setSelectedUserAccess] = useState(null);
  const [userAccessLoading, setUserAccessLoading] = useState(false);
  const [userAccessSaving, setUserAccessSaving] = useState(false);
  const [userActionsSaving, setUserActionsSaving] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [diagnosticModule, setDiagnosticModule] = useState("");
  const [diagnosticAction, setDiagnosticAction] = useState("");
  const [accessMatrix, setAccessMatrix] = useState(null);
  const [accessMatrixRole, setAccessMatrixRole] = useState("");
  const [accessMatrixLoading, setAccessMatrixLoading] = useState(false);
const [effectiveRolePreviews, setEffectiveRolePreviews] = useState({});
  const [configGroupsCatalog, setConfigGroupsCatalog] = useState({});
  const [accessMatrixSaving, setAccessMatrixSaving] = useState(false);
  const [scopeTree, setScopeTree] = useState([]);
  // Resolve nomes do escopo efetivo do usuário selecionado (Cliente → Unidade → Área → Subárea).
  useEffect(() => {
    let cancelled = false;

    api.get("/hierarchy/tree")
      .then(({ data }) => {
        if (cancelled) return;
        const payload = Array.isArray(data)
          ? data
          : Array.isArray(data?.clients)
            ? data.clients
            : Array.isArray(data?.data?.clients)
              ? data.data.clients
              : [];
        setScopeTree(payload);
      })
      .catch(() => {
        if (!cancelled) setScopeTree([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);


  useEffect(() => {
    let cancelled = false;
    setAccessMatrixLoading(true);

    api.get("/platform/access-matrix")
      .then(({ data }) => {
        if (cancelled) return;

        setAccessMatrix(data?.matrix || null);
        setEffectiveRolePreviews(
          data?.effective_role_previews &&
            typeof data.effective_role_previews === "object"
            ? data.effective_role_previews
            : {},
        );
        setConfigGroupsCatalog(
          data?.config_groups_catalog &&
            typeof data.config_groups_catalog === "object"
            ? data.config_groups_catalog
            : {},
        );

        const roleOptions = buildRoleOptions(
          Array.isArray(data?.roles)
            ? data.roles
            : Object.keys(data?.matrix?.roles || {}),
        );
        const roles = roleOptions.map((item) => item.value);

        setAccessMatrixRole((current) => {
          if (current && roles.some((role) => String(role).toLowerCase() === String(current).toLowerCase())) {
            return current;
          }

          return (
            roles.find((role) => canonicalRoleKey(role) !== "admin") ||
            roles[0] ||
            ""
          );
        });
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            formatApiErrorDetail(
              err.response?.data?.detail,
            ) || "Não foi possível carregar a matriz de permissões.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAccessMatrixLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ================================================================
  // LOGIN PAGE — CARROSSEL
  // ================================================================
  const [loginPageUploading, setLoginPageUploading] = useState(false);
  const [loginPageCfg, setLoginPageCfg] = useState(null);

  const loginPageDefault = {
    enabled: true,
    autoplay: true,
    default_duration_ms: 4500,
    transition: "fade",
    brand_slide: {
      enabled: true,
      duration_ms: 4500,
      use_login_background: true,
      eyebrow: "ASTRAL SAÚDE AMBIENTAL",
      title: "Control Intelligence",
      text: "Tecnologia, operação e saúde ambiental conectadas em uma única plataforma.",
    },
    branding_slide_count: 1,
    branding_slides: [],
    slides: [],
  };

  // ================================================================
  // CENTRAL DE SOLICITAÇÕES DE ACESSO
  // ================================================================
  const [accessRequests, setAccessRequests] = useState([]);
  const [accessSummary, setAccessSummary] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
  });
  const [accessStatus, setAccessStatus] = useState("pending");
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessAction, setAccessAction] = useState("");
  const [resendingAccessLink, setResendingAccessLink] = useState("");
  const [selectedAccessRequest, setSelectedAccessRequest] = useState(null);
  const [rejectingAccessRequest, setRejectingAccessRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [accessSearch, setAccessSearch] = useState("");

  const loadAccessRequests = async (status = accessStatus) => {
    setAccessLoading(true);

    try {
      const [listResponse, summaryResponse] = await Promise.all([
        api.get("/platform/access-requests", {
          params: {
            status: status || undefined,
            page: 1,
            limit: 100,
          },
        }),
        api.get("/platform/access-requests-summary"),
      ]);

      setAccessRequests(listResponse.data?.items || []);
      setAccessSummary({
        pending: Number(summaryResponse.data?.pending || 0),
        approved: Number(summaryResponse.data?.approved || 0),
        rejected: Number(summaryResponse.data?.rejected || 0),
        expired: Number(summaryResponse.data?.expired || 0),
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    api.get("/platform/config")
      .then(({ data }) => {
        if (!cancelled) {
          setCfg(data);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setConfigError(
            error?.response?.data?.detail ||
              "Não foi possível carregar as configurações da plataforma.",
          );
        }
      });
    api.get("/platform/login-page/config")
      .then(({ data }) => setLoginPageCfg(data))
      .catch(() => setLoginPageCfg(loginPageDefault));
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    loadAccessRequests("pending");

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!clientId) {
      setClientCfg(null);
      setClientScopeDraft({
        company_ids: [],
        client_ids: [],
        unit_ids: [],
        area_ids: [],
        subarea_ids: [],
      });
      setClientScopeCatalog({
        company: [],
        client: [],
        unit: [],
        area: [],
        subarea: [],
      });
      setClientHierarchyLoading(false);
      return undefined;
    }

    const loadClientSettings = async () => {
      setClientHierarchyLoading(true);

      const [legacyResult, moduleResult, hierarchyResult] = await Promise.allSettled([
        api.get(`/clients/${clientId}/settings`),
        api.get(`/users/access/clients/${clientId}/modules`),
        api.get("/hierarchy/tree"),
      ]);

      if (cancelled) return;

      const legacy = legacyResult.status === "fulfilled"
        ? legacyResult.value?.data || {}
        : {};
      const moduleData = moduleResult.status === "fulfilled"
        ? moduleResult.value?.data || {}
        : {};
      const hierarchyPayload = hierarchyResult.status === "fulfilled"
        ? hierarchyResult.value?.data
        : null;
      const hierarchy = Array.isArray(hierarchyPayload)
        ? hierarchyPayload
        : Array.isArray(hierarchyPayload?.clients)
          ? hierarchyPayload.clients
          : Array.isArray(hierarchyPayload?.data?.clients)
            ? hierarchyPayload.data.clients
            : [];

      if (legacyResult.status === "rejected" && moduleResult.status === "rejected") {
        toast.error(
          formatApiErrorDetail(
            legacyResult.reason?.response?.data?.detail ||
              moduleResult.reason?.response?.data?.detail,
          ) || "Não foi possível carregar as configurações do cliente.",
        );
        setClientCfg(null);
      } else {
        const centralModules = moduleData.modules || {};
        const catalog =
          moduleCatalog ||
          moduleData.modules_catalog ||
          legacy.modules_catalog ||
          {};

        setClientCfg({
          ...legacy,
          ...moduleData,
          modules_catalog: catalog,
          // REGRA (§12/§13): o estado exibido é EXCLUSIVAMENTE o
          // entitlement persistido do cliente (Central de Acessos →
          // client_settings.modules). Ausência de declaração = OFF.
          // Nunca usar catálogo, perfil, grant ou acesso efetivo como ON.
          modules: centralModules,
          overrides: legacy.overrides || moduleData.overrides || {},
        });
      }

      const selectedTree = hierarchy.find(
        (item) => String(item?.id || "") === String(clientId),
      );
      const units = Array.isArray(selectedTree?.units) ? selectedTree.units : [];
      const areas = units.flatMap((unit) =>
        Array.isArray(unit?.areas) ? unit.areas : [],
      );
      const subareas = areas.flatMap((area) =>
        Array.isArray(area?.subareas) ? area.subareas : [],
      );
      const clientRecord = clients.find((item) => String(item?.id) === String(clientId));
      const companyId = selectedTree?.company_id || clientRecord?.company_id || clientRecord?.companyId || "";

      setClientScopeCatalog({
        company: companyId ? [{ id: String(companyId), name: String(clientRecord?.company_name || "Empresa vinculada") }] : [],
        client: selectedTree ? [selectedTree] : [{ id: String(clientId), name: clientRecord?.name || String(clientId) }],
        unit: units,
        area: areas,
        subarea: subareas,
      });

      const persistedScope = moduleData?.client_scope || legacy?.client_scope || {};
      setClientScopeDraft({
        company_ids: persistedScope.company_ids || (companyId ? [String(companyId)] : []),
        client_ids: persistedScope.client_ids || [String(clientId)],
        unit_ids: persistedScope.unit_ids || [],
        area_ids: persistedScope.area_ids || [],
        subarea_ids: persistedScope.subarea_ids || [],
      });

      if (hierarchyResult.status === "rejected") {
        toast.warning(
          "A estrutura do cliente não pôde ser carregada. Verifique o catálogo cadastral antes de configurar o escopo.",
        );
      }

      setClientHierarchyLoading(false);
    };

    loadClientSettings();

    return () => {
      cancelled = true;
    };
  }, [clientId, moduleCatalog, clients]);

  useEffect(() => {
    let cancelled = false;

    setModuleCatalogLoading(true);
    api.get("/users/access/modules/catalog")
      .then(({ data }) => {
        if (!cancelled) {
          setModuleCatalog(data?.modules || {});
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
          setModuleCatalog({});
        }
      })
      .finally(() => {
        if (!cancelled) setModuleCatalogLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!clientId) {
      setAccessUsers([]);
      setSelectedAccessUserId("");
      setSelectedUserAccess(null);
      return undefined;
    }

    setAccessUsersLoading(true);
    api.get("/users")
      .then(({ data }) => {
        if (cancelled) return;

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : [];

        const visible = list.filter((item) => {
          const ids = [
            item?.client_id,
            ...(Array.isArray(item?.client_ids) ? item.client_ids : []),
          ]
            .filter(Boolean)
            .map(String);

          const role = String(item?.role || "").trim().toLowerCase();
          return role === "admin" || ids.includes(String(clientId));
        });

        setAccessUsers(visible);

        setSelectedAccessUserId((current) => {
          if (current && visible.some((item) => String(item.id) === String(current))) {
            return current;
          }
          return visible[0]?.id ? String(visible[0].id) : "";
        });
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
          setAccessUsers([]);
          setSelectedAccessUserId("");
        }
      })
      .finally(() => {
        if (!cancelled) setAccessUsersLoading(false);
      });

    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedAccessUserId) {
      setSelectedUserAccess(null);
      setDiagnostic(null);
      return undefined;
    }

    setUserAccessLoading(true);
    api.get(`/users/access/${selectedAccessUserId}/modules`)
      .then(({ data }) => {
        if (!cancelled) setSelectedUserAccess(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
          setSelectedUserAccess(null);
        }
      })
      .finally(() => {
        if (!cancelled) setUserAccessLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedAccessUserId]);

  useEffect(() => {
    if (!cfg) return;
    const registry = cfg.sections?.module_registry || {};
    setModuleRegistryModule((current) => current || Object.keys(registry)[0] || "");
    const events = cfg.sections?.notifications_config?.events || {};
    setNotificationEvent((current) => current || Object.keys(events)[0] || Object.keys(EVENT_LABELS)[0] || "");
    if (cfg.sections?.reports_config?.categories?.gerencial && !cfg.sections?.reports_config?.categories?.[reportCategory]) {
      setReportCategory("gerencial");
    }
  }, [cfg, reportCategory]);

  useEffect(() => {
    if (!cfg) return;

    const categories = cfg.sections?.reports_config?.categories || {};
    const keys = Object.keys(categories);

    if (
      keys.length &&
      !categories[reportCategory]
    ) {
      setReportCategory(
        keys.includes("gerencial")
          ? "gerencial"
          : keys[0],
      );
    }
  }, [cfg, reportCategory]);

  useEffect(() => {
    if (!cfg) return;
    loadAccessRequests(accessStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessStatus]);

  const sec = cfg?.sections || {};
  const governance = sec.governance_config || {};
  const loadingCfg = sec.loading_config || {};
  const moduleRegistry = sec.module_registry || {};
  const uiGovernance = sec.ui_governance_config || {};
  const integrationCfg = sec.integration_config || {};
  const commandModuleEntries = Object.entries(moduleCatalog || {}).filter(([key, meta]) => { const q = runtimeCommandSearch.trim().toLowerCase(); return !q || `${key} ${meta?.name || ""} ${meta?.feature || ""}`.toLowerCase().includes(q); });
  const commandModule = moduleRegistry[moduleRegistryModule] || {};
  const patchCommandSection = (section, patch) => setSec(section, { ...(sec[section] || {}), ...(patch || {}) });
  const patchModuleRegistry = (module, patch) => setSec("module_registry", { ...(sec.module_registry || {}), [module]: { ...(sec.module_registry?.[module] || {}), ...(patch || {}) } });
  const previewNotification = async () => { if (!notificationEvent) return; setNotificationPreview(null); setSaving("notification_preview"); try { const { data } = await api.get(`/platform/notifications/preview/${encodeURIComponent(notificationEvent)}`); setNotificationPreview(data); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(""); } };
  const previewLoading = () => { setLoadingPreview(true); window.setTimeout(() => setLoadingPreview(false), 750); };
  const setSec = (section, data) => setCfg((c) => ({ ...c, sections: { ...c.sections, [section]: data } }));
  const updateNestedCommand = (section, path, value) => setCfg((c) => { const next = { ...(c || {}) }; const sections = { ...(next.sections || {}) }; const source = { ...(sections[section] || {}) }; let target = source; const keys = Array.isArray(path) ? path : [path]; keys.forEach((key,index)=>{ if(index===keys.length-1) target[key]=value; else { target[key] = { ...(target[key] || {}) }; target=target[key]; } }); sections[section]=source; next.sections=sections; return next; });

  const commandSaving = saving;
  const moduleMeta = moduleCatalog[moduleRegistryModule] || {};
  const notificationConfig = sec.notifications_config || {};
  const reportConfig = {
    enabled: true,
    output: "pdf",
    categories: {},
    page: { size: "A4", orientation: "portrait" },
    branding: {},
    header: {},
    footer: {},
    photos: {},
    signatures: {},
    ...(sec.reports_config || {}),
  };
  const securityConfig = sec.security || {};
  const geoConfig = sec.geo_config || {};
  const effectiveReportCategories = Object.keys(reportConfig.categories || {}).length
    ? reportConfig.categories
    : Object.fromEntries(
        Object.entries(REPORT_CATEGORY_LABELS).map(([key, label]) => [key, {
          label,
          template: key,
          page: { size: "A4", orientation: key === "sala_eletrica" ? "landscape" : "portrait" },
          branding: {},
          header: {},
          footer: {},
          photos: { enabled: true, max_per_section: 6, columns: 3 },
          signatures: { enabled: true, count: 3 },
          sections_order: ADVANCED_REPORT_SECTIONS,
        }])
      );
  const normalizedReportCategory =
    effectiveReportCategories?.[reportCategory]
      ? reportCategory
      : Object.keys(effectiveReportCategories || {})[0] ||
        "gerencial";

  const normalizedAdvancedReportCategory =
    effectiveReportCategories?.[advancedReportCategory]
      ? advancedReportCategory
      : Object.keys(effectiveReportCategories || {})[0] ||
        "gerencial";

  const advancedReport =
    effectiveReportCategories[normalizedAdvancedReportCategory] ||
    effectiveReportCategories.gerencial ||
    {};

  const normalizedRoleOptions = buildRoleOptions(
    accessMatrix?.roles || {},
  );
  const normalizedRoles = normalizedRoleOptions.map((item) => item.value);
  const canonicalRoles = Array.from(
    new Set(normalizedRoleOptions.map((item) => item.canonical)),
  );
  const selectedAdvancedRole = String(advancedRole || normalizedRoles[0] || "admin").toLowerCase();
  const selectedAdvancedRolePolicy = accessMatrix?.roles?.[selectedAdvancedRole] || {
    modules: {},
    actions: {},
    features: {},
    config_access: {},
  };
  const advancedEvent = advancedNotificationEvent || Object.keys(notificationConfig.events || {})[0] || Object.keys(EVENT_LABELS)[0] || "";
  const advancedEventRule = notificationConfig.events?.[advancedEvent] || {};
  const advancedRouteRule = notificationConfig.routing?.[advancedEvent] || {};

  const markAdvancedDirty = () => setAdvancedDirty(true);

  const notifyAuthorizationConsumers = () => {
    window.dispatchEvent(new Event("astral:authorization-changed"));
    window.dispatchEvent(new Event("astral:modules-changed"));
  };

  const updateAdvanced = (section, path, value) => {
    updateNestedCommand(section, path, value);
    setAdvancedDirty(true);
  };

  const saveCommandSection = async (section, message = "Configuração salva.") => {
    setSaving(`command:${section}`);
    try {
      const { data } = await api.put("/platform/config", {
        section,
        data: sec[section] || {},
      });
      setSec(section, data?.data ?? sec[section] ?? {});
      setAdvancedDirty(false);
      if (["module_registry"].includes(section)) {
        notifyAuthorizationConsumers();
      }
      toast.success(message);
      return data?.data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Não foi possível salvar ${section}.`);
      return null;
    } finally {
      setSaving("");
    }
  };

  const refreshAuthorizationMatrix = async () => {
    if (accessMatrixLoading) return;

    setAccessMatrixLoading(true);

    try {
      const { data } = await api.get(
        "/platform/access-matrix",
      );

      setAccessMatrix(
        data?.matrix || null,
      );
      setEffectiveRolePreviews(
        data?.effective_role_previews &&
          typeof data.effective_role_previews === "object"
          ? data.effective_role_previews
          : {},
      );

      toast.success(
        "Matriz e autorizações efetivas atualizadas.",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail,
        ) || "Não foi possível atualizar a matriz.",
      );
    } finally {
      setAccessMatrixLoading(false);
    }
  };

  const saveAdvancedRoleMatrix = async () => {
    if (!accessMatrix || selectedAdvancedRole === "admin") {
      if (selectedAdvancedRole === "admin") {
        toast.info("O perfil Admin permanece global e não recebe restrições pela matriz.");
      }
      return;
    }
    setAccessMatrixSaving(true);
    try {
      const { data } = await api.put("/platform/access-matrix", accessMatrix);
      setAccessMatrix(data?.matrix || accessMatrix);
      setAdvancedDirty(false);
      notifyAuthorizationConsumers();
      toast.success(`Matriz do perfil ${selectedAdvancedRole} sincronizada.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAccessMatrixSaving(false);
    }
  };

  const toggleAdvancedRoleModule = (module, value) => {
    if (selectedAdvancedRole === "admin") return;
    setAccessMatrix((current) => ({
      ...(current || {}),
      roles: {
        ...(current?.roles || {}),
        [selectedAdvancedRole]: {
          ...(current?.roles?.[selectedAdvancedRole] || {}),
          modules: {
            ...(current?.roles?.[selectedAdvancedRole]?.modules || {}),
            [module]: !!value,
          },
        },
      },
    }));
    markAdvancedDirty();
  };

  const toggleAdvancedRoleAction = (module, action, value) => {
    if (selectedAdvancedRole === "admin") return;
    setAccessMatrix((current) => ({
      ...(current || {}),
      roles: {
        ...(current?.roles || {}),
        [selectedAdvancedRole]: {
          ...(current?.roles?.[selectedAdvancedRole] || {}),
          actions: {
            ...(current?.roles?.[selectedAdvancedRole]?.actions || {}),
            [module]: {
              ...(current?.roles?.[selectedAdvancedRole]?.actions?.[module] || {}),
              [action]: !!value,
            },
          },
        },
      },
    }));
    markAdvancedDirty();
  };

  const toggleAdvancedRoleFeature = (feature, value) => {
    if (selectedAdvancedRole === "admin") return;
    setAccessMatrix((current) => ({
      ...(current || {}),
      roles: {
        ...(current?.roles || {}),
        [selectedAdvancedRole]: {
          ...(current?.roles?.[selectedAdvancedRole] || {}),
          features: {
            ...(current?.roles?.[selectedAdvancedRole]?.features || {}),
            [feature]: !!value,
          },
        },
      },
    }));
    markAdvancedDirty();
  };

  const toggleAdvancedRoleConfig = (group, value) => {
    if (selectedAdvancedRole === "admin") return;
    setAccessMatrix((current) => ({
      ...(current || {}),
      roles: {
        ...(current?.roles || {}),
        [selectedAdvancedRole]: {
          ...(current?.roles?.[selectedAdvancedRole] || {}),
          config_access: {
            ...(current?.roles?.[selectedAdvancedRole]?.config_access || {}),
            [group]: !!value,
          },
        },
      },
    }));
    markAdvancedDirty();
  };

  const patchModuleAdvanced = (path, value) => updateAdvanced("module_registry", [advancedModule, ...path], value);
  const patchAdvancedReport = (path, value) => updateAdvanced("reports_config", ["categories", advancedReportCategory, ...path], value);
  const patchAdvancedNotification = (path, value) => updateAdvanced("notifications_config", path, value);
  const patchAdvancedLoading = (path, value) => updateAdvanced("loading_config", path, value);
  const patchAdvancedSecurity = (path, value) => updateAdvanced("security", path, value);
  const patchAdvancedGeo = (path, value) => updateAdvanced("geo_config", path, value);
  const patchAdvancedGovernance = (path, value) => updateAdvanced("governance_config", path, value);
  const patchAdvancedUI = (path, value) => updateAdvanced("ui_governance_config", path, value);
  const patchAdvancedIntegration = (path, value) => updateAdvanced("integration_config", path, value);
  const patchAdvancedApp = (path, value) => updateAdvanced("app_config", [appKey, ...path], value);

  const setAdvancedReportSectionOrder = (section, index, direction) => {
    const current = Array.isArray(advancedReport.sections_order)
      ? [...advancedReport.sections_order]
      : [...ADVANCED_REPORT_SECTIONS];
    const target = index + direction;
    if (target < 0 || target >= current.length) return;
    [current[index], current[target]] = [current[target], current[index]];
    patchAdvancedReport(["sections_order"], current);
  };

  const addAdvancedNotificationRule = () => {
    const eventKey = `custom_${Date.now()}`;
    const nextEvents = {
      ...(notificationConfig.events || {}),
      [eventKey]: {
        label: "Novo evento",
        enabled: true,
        mandatory: false,
        allow_user_dismiss: true,
        dedupe: true,
        application_scope: "targeted",
        priority: "medium",
        channels: ["toast"],
        sla_minutes: 0,
      },
    };
    updateAdvanced("notifications_config", ["events"], nextEvents);
    setAdvancedNotificationEvent(eventKey);
  };

  const removeAdvancedNotificationRule = (eventKey) => {
    if (!eventKey) return;
    const nextEvents = { ...(notificationConfig.events || {}) };
    delete nextEvents[eventKey];
    updateAdvanced("notifications_config", ["events"], nextEvents);
    if (advancedNotificationEvent === eventKey) {
      setAdvancedNotificationEvent(Object.keys(nextEvents)[0] || "");
    }
  };

  const ensureReportCategory = (category) => {
    if (reportConfig.categories?.[category]) return;
    const base = {
      label: REPORT_CATEGORY_LABELS[category] || category,
      template: category,
      page: { size: "A4", orientation: category === "sala_eletrica" ? "landscape" : "portrait" },
      branding: { show_logo: true, use_letterhead: true, watermark_enabled: false },
      header: { show_report_title: true, show_period: true, show_company: true, show_unit: true },
      footer: { show_page_number: true, show_generation_time: true },
      photos: { enabled: true, max_per_section: 6, columns: 3 },
      signatures: { enabled: true, count: 3 },
      evidence: { enabled: true, require_caption: true, require_timestamp: true },
      show_qr: true,
      sections_order: [...ADVANCED_REPORT_SECTIONS],
    };
    updateNestedCommand("reports_config", ["categories", category], base);
  };

  const loadPresetIntoSections = (preset) => {
    const presets = {
      operacao_completa: {
        governance_config: { enforce_global: true, audit_changes: true, invalidate_cache: true, application_mode: "immediate" },
        loading_config: { enabled: true, minimum_duration_ms: 450, timeout_ms: 30000, show_progress: true },
        notifications_config: { enabled: true, realtime: true, polling: true, mandatory_system: true },
        reports_config: { enabled: true, output: "pdf", strict_validation: true, fail_on_missing_required_section: true },
        security: { require_mfa_for_admin: true, audit_security_events: true, protect_configuration_routes: true },
        geo_config: { enabled: true, gps_enabled: true, require_accuracy: true, track_history: true },
      },
      seguranca_maxima: {
        governance_config: { enforce_global: true, audit_changes: true, invalidate_cache: true, application_mode: "approval" },
        security: { require_mfa_for_admin: true, force_password_rotation: true, block_after_failed_attempts: true, session_revocation_enabled: true, audit_security_events: true, csrf_protection: true, login_rate_limit: true, require_secure_transport: true },
        notifications_config: { enabled: true, realtime: true, mandatory_system: true },
      },
      operacao_campo: {
        loading_config: { enabled: true, show_progress: true, minimum_duration_ms: 300 },
        notifications_config: { enabled: true, realtime: true, polling: true, toast: true, browser: true, sound: true },
        geo_config: { enabled: true, gps_enabled: true, require_accuracy: true, allow_manual_location: true, track_history: true, store_coordinates: true },
      },
    };
    const selected = presets[preset] || presets.operacao_completa;
    Object.entries(selected).forEach(([section, patch]) => {
      patchCommandSection(section, patch);
    });
    setAdvancedDirty(true);
    toast.success(`Preset '${preset}' carregado na memória. Salve as seções desejadas.`);
  };


  const ac = sec.app_config?.[appKey] || {};
  const setApp = (sub, patch) => setSec("app_config", {
    ...sec.app_config,
    [appKey]: { ...ac, [sub]: { ...(ac[sub] || {}), ...patch } },
  });
  const moveMenu = (i, dir) => {
    const order = [...(ac.interface?.menu_order || [])];
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    setApp("interface", { menu_order: order });
  };

  const selectedAccessRolePolicy = accessMatrix?.roles?.[String(accessMatrixRole || "").toLowerCase()] || { modules: {}, actions: {} };
  const updateRoleMatrix = (patch) => {
    const role = String(accessMatrixRole || "").toLowerCase();
    if (!role || role === "admin") return;
    setAccessMatrix((current) => ({ ...(current || { version: 1, roles: {}, global_config: {} }), roles: { ...(current?.roles || {}), [role]: { ...(current?.roles?.[role] || {}), ...patch } } }));
  };
  const toggleRoleModule = (module, enabled) => updateRoleMatrix({ modules: { ...(selectedAccessRolePolicy.modules || {}), [module]: !!enabled } });
  const toggleRoleAction = (module, action, enabled) => updateRoleMatrix({ actions: { ...(selectedAccessRolePolicy.actions || {}), [module]: { ...(selectedAccessRolePolicy.actions?.[module] || {}), [action]: !!enabled } } });
  const toggleRoleFeature = (feature, enabled) => updateRoleMatrix({ features: { ...(selectedAccessRolePolicy.features || {}), [feature]: !!enabled } });
  const toggleRoleConfig = (group, enabled) => updateRoleMatrix({ config_access: { ...(selectedAccessRolePolicy.config_access || {}), [group]: !!enabled } });
  const selectedRoleDelegation = selectedAccessRolePolicy?.delegation || {};
  const selectedRoleTargetRoles = Array.isArray(selectedRoleDelegation.allowed_target_roles)
    ? Array.from(
        new Set(
          selectedRoleDelegation.allowed_target_roles
            .map(canonicalRoleKey)
            .filter((role) => role && role !== "admin"),
        ),
      )
    : [];
  const canManageUsers = !!selectedRoleDelegation.can_manage_users;
  const canManageRoles = !!selectedRoleDelegation.can_manage_roles;
  const toggleRoleDelegation = (key, enabled) =>
    updateRoleMatrix({
      delegation: {
        ...selectedAccessRolePolicy.delegation,
        [key]: !!enabled,
      },
    });
  const toggleRoleTargetRole = (role, enabled) => {
    const canonical = canonicalRoleKey(role);
    const current = Array.isArray(selectedRoleDelegation.allowed_target_roles)
      ? selectedRoleDelegation.allowed_target_roles.map((item) => String(item).toLowerCase())
      : [];
    const next = current.filter((item) => canonicalRoleKey(item) !== canonical);

    if (enabled) {
      next.push(String(role).toLowerCase());
    }

    updateRoleMatrix({
      delegation: {
        ...(selectedAccessRolePolicy.delegation || {}),
        allowed_target_roles: Array.from(new Set(next)),
      },
    });
  };
  const selectAllRoleModules = () =>
    updateRoleMatrix({
      modules: Object.fromEntries(
        Object.keys(moduleCatalog || {}).map((module) => [module, true]),
      ),
    });
  const clearAllRoleModules = () =>
    updateRoleMatrix({
      modules: Object.fromEntries(
        Object.keys(moduleCatalog || {}).map((module) => [module, false]),
      ),
    });
  const selectAllRoleActions = (module) => {
    const actions = Array.isArray(moduleCatalog?.[module]?.actions)
      ? moduleCatalog[module].actions
      : ADVANCED_MODULE_CAPABILITIES[module] || [];
    updateRoleMatrix({
      modules: {
        ...(selectedAccessRolePolicy.modules || {}),
        [module]: true,
      },
      actions: {
        ...(selectedAccessRolePolicy.actions || {}),
        [module]: Object.fromEntries(
          actions.map((action) => [action, true]),
        ),
      },
    });
  };
  const clearAllRoleActions = (module) => {
    const actions = Array.isArray(moduleCatalog?.[module]?.actions)
      ? moduleCatalog[module].actions
      : ADVANCED_MODULE_CAPABILITIES[module] || [];
    updateRoleMatrix({
      actions: {
        ...(selectedAccessRolePolicy.actions || {}),
        [module]: Object.fromEntries(
          actions.map((action) => [action, false]),
        ),
      },
    });
  };
  const selectAllRoleFeatures = () =>
    updateRoleMatrix({
      features: Object.fromEntries(
        (cfg?.app_features || Object.keys(APP_FEATURE_LABELS)).map(
          (feature) => [feature, true],
        ),
      ),
    });
  const clearAllRoleFeatures = () =>
    updateRoleMatrix({
      features: Object.fromEntries(
        (cfg?.app_features || Object.keys(APP_FEATURE_LABELS)).map(
          (feature) => [feature, false],
        ),
      ),
    });
  const selectAllRoleConfig = () =>
    updateRoleMatrix({
      config_access: Object.fromEntries(
        ["plataforma", "usuarios", "relatorios", "notificacoes", "seguranca", "geoespacial", "aplicativos", "auditoria", "dados"]
          .map((group) => [group, true]),
      ),
    });
  const clearAllRoleConfig = () =>
    updateRoleMatrix({
      config_access: Object.fromEntries(
        ["plataforma", "usuarios", "relatorios", "notificacoes", "seguranca", "geoespacial", "aplicativos", "auditoria", "dados"]
          .map((group) => [group, false]),
      ),
    });
  const getClientScopeCatalog = (level) => {
    const configured = clientScopeCatalog?.[level];
    if (Array.isArray(configured) && configured.length) {
      return configured;
    }

    const candidates = [
      clientCfg?.scope_catalog?.[level],
      clientCfg?.structure?.[level],
      clientCfg?.[`${level}s`],
    ];
    return candidates.find((value) => Array.isArray(value)) || [];
  };
  const toggleClientScope = (level, id, enabled) => {
    const key = `${level}_ids`;
    setClientScopeDraft((current) => {
      const values = Array.isArray(current?.[key])
        ? current[key].map(String)
        : [];
      const normalized = String(id);
      return {
        ...current,
        [key]: enabled
          ? Array.from(new Set([...values, normalized]))
          : values.filter((item) => item !== normalized),
      };
    });
  };
  const selectAllClientScope = (level) => {
    const key = `${level}_ids`;
    setClientScopeDraft((current) => ({
      ...current,
      [key]: getClientScopeCatalog(level).map(
        (item) => String(item?.id ?? item),
      ),
    }));
  };
  const clearAllClientScope = (level) => {
    const key = `${level}_ids`;
    setClientScopeDraft((current) => ({ ...current, [key]: [] }));
  };

  const saveAccessMatrix = async () => {
    if (!accessMatrix || accessMatrixSaving) return;

    setAccessMatrixSaving(true);

    try {
      const { data } = await api.put(
        "/platform/access-matrix",
        accessMatrix,
      );

      setAccessMatrix(
        data?.matrix || accessMatrix,
      );

      // Reconsulta o endpoint oficial para garantir que a interface
      // esteja mostrando a política realmente persistida e normalizada.
      const { data: fresh } = await api.get(
        "/platform/access-matrix",
      );

      setAccessMatrix(
        fresh?.matrix ||
          data?.matrix ||
          accessMatrix,
      );

      setEffectiveRolePreviews(
        fresh?.effective_role_previews &&
          typeof fresh.effective_role_previews === "object"
          ? fresh.effective_role_previews
          : {},
      );

      notifyAuthorizationConsumers();

      toast.success(
        "Matriz global sincronizada. Autorização efetiva revalidada.",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail,
        ) || "Não foi possível sincronizar a matriz global.",
      );
    } finally {
      setAccessMatrixSaving(false);
    }
  };

  const saveClient = async () => {
    if (!clientId || !clientCfg) return;

    setSaving("cliente");

    try {
      await api.put(
        `/users/access/clients/${clientId}/modules`,
        {
          modules: clientCfg.modules || {},
        },
      );

      const { data: moduleData } =
        await api.get(
          `/users/access/clients/${clientId}/modules`,
        );

      setClientCfg((current) => ({
        ...(current || {}),
        modules_catalog:
          moduleCatalog || {},
        modules:
          moduleData?.modules || {},
        overrides: {},
      }));

      notifyAuthorizationConsumers();

      toast.success(
        "Módulos do cliente salvos.",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail,
        ) ||
        "Não foi possível salvar os módulos do cliente.",
      );
    } finally {
      setSaving("");
    }
  };


  const accessEffectiveModules = selectedUserAccess?.effective?.modules || {};
  const accessEffectivePermissions = selectedUserAccess?.effective?.permissions || {};
  const accessClientModules = selectedUserAccess?.client_modules || {};
  const accessUserOverrides = selectedUserAccess?.module_overrides || {};
  const selectedAccessUserRole = String(selectedUserAccess?.user?.role || "").trim().toLowerCase();
  const selectedAccessUserIsAdmin = selectedAccessUserRole === "admin";

  const loadModuleDiagnostic = async () => {
    if (!selectedAccessUserId || !diagnosticModule) return;

    setDiagnosticLoading(true);
    try {
      const params = diagnosticAction
        ? { action: diagnosticAction }
        : undefined;

      const { data } = await api.get(
        `/users/access/${selectedAccessUserId}/modules/${diagnosticModule}/diagnostic`,
        { params },
      );

      setDiagnostic(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setDiagnostic(null);
    } finally {
      setDiagnosticLoading(false);
    }
  };
  const notif = sec.notifications_config || {};
  const scopeRules = notif.scope_rules || { global_roles: ["admin"], unscoped_receives_all: false };
  const setScopeRules = (patch) => setSec("notifications_config", { ...notif, scope_rules: { ...scopeRules, ...patch } });
  const rep = sec.reports_config || {};
  const ai = sec.ai_config || {};
  const secr = sec.security || {};
  const geo = sec.geo_config || {};
  const accessCfg = sec.access_requests || {};

  // Normaliza a configuração recebida do backend para evitar campos ausentes
  // derrubando a renderização da aba Login Page.
  const loginPage = {
    ...loginPageDefault,
    ...(loginPageCfg || {}),
    brand_slide: {
      ...loginPageDefault.brand_slide,
      ...((loginPageCfg || {}).brand_slide || {}),
    },
    branding_slide_count: Math.max(
      1,
      Math.min(
        8,
        Number((loginPageCfg || {}).branding_slide_count || 1),
      ),
    ),
    branding_slides: Array.isArray((loginPageCfg || {}).branding_slides)
      ? (loginPageCfg || {}).branding_slides
      : Array.isArray((loginPageCfg || {}).slides)
        ? (loginPageCfg || {}).slides
        : [],
    slides: Array.isArray((loginPageCfg || {}).branding_slides)
      ? (loginPageCfg || {}).branding_slides
      : Array.isArray((loginPageCfg || {}).slides)
        ? (loginPageCfg || {}).slides
        : [],
  };

  const setLoginPage = (patch) =>
    setLoginPageCfg((current) => {
      const currentBranding =
        Array.isArray((current || {}).branding_slides)
          ? (current || {}).branding_slides
          : Array.isArray((current || {}).slides)
            ? (current || {}).slides
            : [];

      const nextBranding =
        Array.isArray(patch.branding_slides)
          ? patch.branding_slides
          : Array.isArray(patch.slides)
            ? patch.slides
            : currentBranding;

      return {
        ...loginPageDefault,
        ...(current || {}),
        ...patch,
        brand_slide: {
          ...loginPageDefault.brand_slide,
          ...((current || {}).brand_slide || {}),
          ...(patch.brand_slide || {}),
        },
        branding_slide_count: Math.max(
          1,
          Math.min(
            8,
            Number(
              patch.branding_slide_count ??
              (current || {}).branding_slide_count ??
              1,
            ),
          ),
        ),
        branding_slides: nextBranding,
        slides: nextBranding,
      };
    });

  const getBrandingSlides = () =>
    Array.isArray(loginPage.branding_slides)
      ? loginPage.branding_slides
      : Array.isArray(loginPage.slides)
        ? loginPage.slides
        : [];

  const updateLoginSlide = (id, patch) => {
    const brandingSlides = getBrandingSlides();

    setLoginPage({
      branding_slides: brandingSlides.map((slide) =>
        slide.id === id
          ? { ...slide, ...patch }
          : slide
      ),
    });
  };

  const createBrandingSlide = (order) => ({
    id: `branding-${Date.now()}-${order}`,
    order,
    enabled: true,
    type: "branding",
    image_url: "",
    image_name: "",
    eyebrow: "ASTRAL SAÚDE AMBIENTAL",
    title:
      order === 1
        ? "Control Intelligence"
        : `Branding ${order}`,
    text:
      order === 1
        ? "Tecnologia, operação e saúde ambiental conectadas em uma única plataforma."
        : "Conteúdo institucional da ASTRAL Saúde Ambiental.",
    accent: "ASTRAL",
    duration_ms: 4500,
    object_position: "center",
    overlay: 0.35,
  });

  const setBrandingSlideCount = (value) => {
    const count = Math.max(
      1,
      Math.min(8, Number(value || 1)),
    );

    const current = getBrandingSlides();
    let next = current.slice(0, count);

    while (next.length < count) {
      next.push(
        createBrandingSlide(next.length + 1),
      );
    }

    next = next.map((slide, index) => ({
      ...slide,
      order: index + 1,
      type: "branding",
      duration_ms: 4500,
    }));

    setLoginPage({
      branding_slide_count: count,
      branding_slides: next,
    });
  };

  const addLoginSlide = () => {
    const current = getBrandingSlides();

    if (current.length >= 8) {
      toast.error("O limite é de 8 brandings.");
      return;
    }

    setBrandingSlideCount(current.length + 1);
  };

  const removeLoginSlide = (id) => {
    const current = getBrandingSlides();

    if (current.length <= 1) {
      toast.error("Mantenha pelo menos 1 branding.");
      return;
    }

    const next = current
      .filter((slide) => slide.id !== id)
      .map((slide, index) => ({
        ...slide,
        order: index + 1,
        type: "branding",
        duration_ms: 4500,
      }));

    setLoginPage({
      branding_slide_count: next.length,
      branding_slides: next,
    });
  };

  const moveLoginSlide = (index, direction) => {
    const current = getBrandingSlides();
    const target = index + direction;

    if (
      target < 0 ||
      target >= current.length
    ) {
      return;
    }

    const slides = [...current];
    [slides[index], slides[target]] = [
      slides[target],
      slides[index],
    ];

    setLoginPage({
      branding_slides: slides.map(
        (slide, i) => ({
          ...slide,
          order: i + 1,
          type: "branding",
          duration_ms: 4500,
        }),
      ),
    });
  };

  const saveLoginPage = async () => {
    setSaving("login_page_config");
    try {
      const { data } = await api.put("/platform/login-page/config", {
        data: loginPage,
      });
      setLoginPageCfg(data.data || data);
      toast.success("Configuração da Login Page salva.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
        "Não foi possível salvar a Login Page."
      );
    } finally {
      setSaving("");
    }
  };

  const uploadLoginSlideImage = async (slideId, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 10 MB.");
      return;
    }

    setLoginPageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("slide_id", slideId);

      const { data } = await api.post(
        "/platform/login-page/slides/upload",
        formData,
      );

      const uploaded = data?.slide || data?.data || data;

      updateLoginSlide(slideId, {
        image_url:
          uploaded?.image_url ||
          uploaded?.url ||
          "",
        image_name:
          uploaded?.image_name ||
          uploaded?.filename ||
          uploaded?.original_filename ||
          file.name,
        type: "branding",
      });
      toast.success("Imagem enviada com sucesso.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
        "Não foi possível enviar a imagem."
      );
    } finally {
      setLoginPageUploading(false);
    }
  };

  const accessFiltered = accessRequests.filter((item) => {
    const q = accessSearch.trim().toLowerCase();
    if (!q) return true;

    return [
      item.name,
      item.email,
      item.protocol,
      item.company_name,
      item.unit_name,
      item.requested_role,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  const formatAccessDate = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleString("pt-BR");
    } catch {
      return value;
    }
  };

  const formatRemaining = (seconds) => {
    if (seconds == null) return "—";
    if (seconds <= 0) return "Expirado";

    const totalMinutes = Math.floor(seconds / 60);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
  };

  const accessRoleLabel = (role) => ({
    technician: "Técnico",
    tecnico: "Técnico",
    supervisor: "Supervisor",
    coordinator: "Coordenador",
    coordenador: "Coordenador",
    client: "Cliente",
    cliente: "Cliente",
  }[role] || role || "—");

  const accessStatusLabel = (status) => ({
    pending: "Pendente",
    approved: "Pré-cadastro aprovado",
    rejected: "Rejeitada",
    expired: "Expirada",
    cancelled: "Cancelada",
  }[status] || status || "—");

  const accessStatusClass = (status) => ({
    pending: "border-amber-500/40 text-amber-400 bg-amber-500/5",
    approved: "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
    rejected: "border-red-500/40 text-red-400 bg-red-500/5",
    expired: "border-slate-500/40 text-slate-400 bg-slate-500/5",
    cancelled: "border-slate-500/40 text-slate-400 bg-slate-500/5",
  }[status] || "border-border text-muted-foreground");

  const openAccessRequest = async (id) => {
    setAccessAction(`view:${id}`);
    try {
      const { data } = await api.get(`/platform/access-requests/${id}`);
      setSelectedAccessRequest(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAccessAction("");
    }
  };

  const approveAccessRequest = async (item) => {
  setAccessAction(`approve:${item.id}`);

  try {
    const { data } = await api.post(
      `/platform/access-requests/${item.id}/approve`,
    );

    if (data?.email_sent === false) {
      toast.warning(
        "Solicitação aprovada, mas o e-mail de finalização não pôde ser enviado.",
      );
    } else {
      toast.success(
        "Solicitação aprovada. O link para finalizar o cadastro foi enviado.",
      );
    }

    setSelectedAccessRequest(null);

    await loadAccessRequests(
      accessStatus,
    );
  } catch (err) {
    toast.error(
      formatApiErrorDetail(
        err.response?.data?.detail,
      ),
    );

    await loadAccessRequests(
      accessStatus,
    );
  } finally {
    setAccessAction("");
  }
};

  const resendAccessLink = async (item) => {
    if (!item?.id) return;

    if (item.status !== "approved") {
      toast.error("Somente solicitações com pré-cadastro aprovado podem receber um novo link.");
      return;
    }

    if (item.registration_completed_at || item.finalization_completed_at) {
      toast.error("Este cadastro já foi finalizado. Não é possível gerar um novo link.");
      return;
    }

    setResendingAccessLink(item.id);

    try {
      const { data } = await api.post(
        `/platform/access-requests/${item.id}/resend-link`,
      );

      if (data?.email_sent === false) {
        toast.warning("Novo link gerado, mas o e-mail não pôde ser enviado.");
      } else {
        toast.success("Novo link de finalização enviado para o solicitante.");
      }

      if (selectedAccessRequest?.id === item.id) {
        await openAccessRequest(item.id);
      }

      await loadAccessRequests(accessStatus);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail),
      );
    } finally {
      setResendingAccessLink("");
    }
  };

  const rejectAccessRequest = async () => {
    if (!rejectingAccessRequest) return;

    const reason = rejectionReason.trim();

    if (reason.length < 3) {
      toast.error("Informe o motivo da rejeição.");
      return;
    }

    setAccessAction(`reject:${rejectingAccessRequest.id}`);

    try {
      await api.post(
        `/platform/access-requests/${rejectingAccessRequest.id}/reject`,
        { reason },
      );

      toast.success("Solicitação rejeitada e registrada na auditoria.");
      setRejectingAccessRequest(null);
      setRejectionReason("");
      setSelectedAccessRequest(null);
      await loadAccessRequests(accessStatus);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAccessAction("");
    }
  };

  const saveAccessConfig = async () => {
    setSaving("access_requests");

    try {
      const { data } = await api.put("/platform/config", {
        section: "access_requests",
        data: accessCfg,
      });

      setSec("access_requests", data.data);
      toast.success("Configurações de solicitação de acesso salvas.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };


  useEffect(() => {
    if (!advancedModule) {
      setAdvancedModule(moduleRegistryModule || Object.keys(moduleCatalog || {})[0] || "");
    }
  }, [advancedModule, moduleRegistryModule, moduleCatalog]);

  useEffect(() => {
    if (!advancedRole) {
      setAdvancedRole(normalizedRoles.find((role) => canonicalRoleKey(role) !== "admin") || normalizedRoles[0] || "admin");
    }
  }, [advancedRole, normalizedRoles]);

  useEffect(() => {
    if (!advancedNotificationEvent) {
      setAdvancedNotificationEvent(Object.keys(notificationConfig.events || {})[0] || Object.keys(EVENT_LABELS)[0] || "");
    }
  }, [advancedNotificationEvent, notificationConfig.events]);

  const brandingSlides = Array.isArray(loginPage.branding_slides)
    ? loginPage.branding_slides
    : [];

  if (!cfg) {
    return (
      <div
        className="flex min-h-[420px] items-center justify-center"
        data-testid="platform-admin-config-loading"
      >
        <div className="text-center">
          <div className="mx-auto h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <p className="mt-3 text-sm text-muted-foreground">
            Carregando configurações da plataforma...
          </p>
          {configError && (
            <p className="mt-2 max-w-md text-xs text-destructive">
              {configError}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="platform-admin-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2.5">
          <Layers size={26} className="text-primary" /> Administração da Plataforma
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Centro administrativo — ERP, aplicativos, IA, relatórios, notificações e configurações por cliente. Fonte única da verdade: alterações refletem automaticamente nos apps via APIs oficiais.</p>
      </div>

      <Card className="p-4 border-primary/20 bg-primary/5" data-testid="platform-governance-guide">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-primary mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Como a governança do ASTRAL funciona</p>
            <p className="text-xs text-muted-foreground mt-1">O Administrador configura primeiro o cliente, depois o perfil, depois a autoridade de delegação e, por fim, o escopo do usuário. A autorização efetiva é a interseção dessas camadas; o escopo limita os dados, não cria permissões.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-3">
              {[
                ["01", "Cliente", "Define módulos e estrutura disponível."],
                ["02", "Perfil", "Define o que o papel pode fazer."],
                ["03", "Delegação", "Define quem o perfil pode administrar."],
                ["04", "Usuário", "Define onde o usuário pode atuar."],
              ].map(([step, title, desc]) => (
                <div key={step} className="rounded-lg border border-border bg-card/60 px-3 py-2.5">
                  <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{step}</p>
                  <p className="text-xs font-semibold mt-0.5">{title}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="aplicativos">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="aplicativos" data-testid="tab-aplicativos"><Smartphone size={13} className="mr-1.5" /> Aplicativos</TabsTrigger>
          <TabsTrigger value="login-page" data-testid="tab-login-page"><ImageIcon size={13} className="mr-1.5" /> Login Page</TabsTrigger>
          <TabsTrigger value="solicitacoes" data-testid="tab-solicitacoes">
            <UserPlus size={13} className="mr-1.5" /> Solicitações
            {accessSummary.pending > 0 && (
              <Badge className="ml-1.5 h-4 min-w-4 px-1 text-[9px]">
                {accessSummary.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="ia" data-testid="tab-ia"><Sparkles size={13} className="mr-1.5" /> IA</TabsTrigger>
          <TabsTrigger value="notificacoes" data-testid="tab-notificacoes"><Bell size={13} className="mr-1.5" /> Notificações</TabsTrigger>
          <TabsTrigger value="relatorios" data-testid="tab-relatorios"><FileText size={13} className="mr-1.5" /> Relatórios</TabsTrigger>
          <TabsTrigger value="geoespacial" data-testid="tab-geoespacial"><MapIcon size={13} className="mr-1.5" /> Geoespacial</TabsTrigger>
          <TabsTrigger value="seguranca" data-testid="tab-seguranca"><ShieldCheck size={13} className="mr-1.5" /> Segurança</TabsTrigger>
          <TabsTrigger value="clientes" data-testid="tab-clientes"><Building2 size={13} className="mr-1.5" /> Clientes e Escopo</TabsTrigger>
          <TabsTrigger value="comando" data-testid="tab-comando"><Gauge size={13} className="mr-1.5" /> Central de Comando</TabsTrigger>
          <TabsTrigger value="comando-avancado" data-testid="tab-comando-avancado"><SlidersHorizontal size={13} className="mr-1.5" /> Parametrização Profunda</TabsTrigger>
          <TabsTrigger value="erp" data-testid="tab-erp"><Settings size={13} className="mr-1.5" /> ERP</TabsTrigger>
        </TabsList>

        {/* ================= CENTRAL DE COMANDO ULTRA ================= */}
        <TabsContent value="comando" className="space-y-4 mt-4">
          <RobustCommandPanel title="Central de Comando — Configurações do Sistema" testId="ultra-command-center" description="Painel administrativo avançado. Cada alteração é persistida no backend oficial e reaproveitada pelo runtime. O catálogo administrativo permanece completo; o acesso efetivo continua sob o motor central de autorização.">
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
              <div className="xl:col-span-1 space-y-2">
                <Input value={runtimeCommandSearch} onChange={(e) => setRuntimeCommandSearch(e.target.value)} placeholder="Pesquisar..." />
                <div className="rounded-xl border border-border p-2 space-y-1">
                  {[["governanca","Governança",ShieldCheck],["modulos","Módulos",Layers],["loading","Loading",Loader2],["notificacoes","Notificações",BellRing],["relatorios","Relatórios",FileText],["seguranca","Segurança",ShieldCheck],["geoespacial","Geoespacial",MapIcon]].map(([key,label,Icon]) => <button type="button" key={key} onClick={() => setCommandTab(key)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-left ${commandTab === key ? "bg-primary/10 text-primary" : "hover:bg-accent"}`}><Icon size={13}/>{label}</button>)}
                </div>
              </div>
              <div className="xl:col-span-4 space-y-4">
                {commandTab === "governanca" && <>
                  <SettingGroup title="Política de propagação" description="Determina como alterações feitas pelo Administrador devem ser aplicadas ao ERP e aos aplicativos.">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <BoolSetting label="Governança habilitada" description="Ativa a camada central de políticas." checked={governance.enabled !== false} onChange={(v) => patchCommandSection("governance_config", { enabled: v })}/>
                      <BoolSetting label="Aplicação global obrigatória" description="Regras marcadas como globais não dependem de configuração individual." checked={governance.enforce_global !== false} onChange={(v) => patchCommandSection("governance_config", { enforce_global: v })}/>
                      <BoolSetting label="Auditoria obrigatória" description="Registra diffs das alterações administrativas." checked={governance.audit_changes !== false} onChange={(v) => patchCommandSection("governance_config", { audit_changes: v })}/>
                      <BoolSetting label="Invalidar cache" description="Solicita nova leitura das configurações após salvar." checked={governance.invalidate_cache !== false} onChange={(v) => patchCommandSection("governance_config", { invalidate_cache: v })}/>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3"><div><Label className="text-xs">Versão de política</Label><Input type="number" value={governance.policy_version || 1} onChange={(e)=>patchCommandSection("governance_config",{policy_version:Number(e.target.value)})}/></div><div><Label className="text-xs">Delay de propagação (ms)</Label><Input type="number" value={governance.propagation_delay_ms || 0} onChange={(e)=>patchCommandSection("governance_config",{propagation_delay_ms:Number(e.target.value)})}/></div><div><Label className="text-xs">Modo de aplicação</Label><Select value={governance.application_mode || "immediate"} onValueChange={(v)=>patchCommandSection("governance_config",{application_mode:v})}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{[["immediate","Imediato"],["scheduled","Programado"],["approval","Com aprovação"]].map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div><div><Label className="text-xs">Evento de propagação</Label><Input value={integrationCfg.event_name || "astral:platform-config-changed"} onChange={(e)=>patchCommandSection("integration_config",{event_name:e.target.value})}/></div></div>
                  </SettingGroup>
                  <SettingGroup title="Integrações" description="Controla a propagação das configurações para as diferentes camadas do sistema."><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[["erp_sync_enabled","ERP"],["apps_sync_enabled","Aplicativos"],["notification_sync_enabled","Notificações"],["report_sync_enabled","Relatórios"],["event_propagation_enabled","Eventos em tempo real"],["enabled","Integrações globais"]].map(([key,label])=><BoolSetting key={key} label={`Sincronização — ${label}`} checked={integrationCfg[key] !== false} onChange={(v)=>patchCommandSection("integration_config",{[key]:v})}/>)}</div><div className="flex justify-end mt-3"><Button onClick={()=>{saveCommandSection("governance_config","Governança salva.");saveCommandSection("integration_config","Integrações salvas.")}} disabled={!!commandSaving}>Salvar governança</Button></div></SettingGroup>
                  <SettingGroup title="Governança da interface" description="Parâmetros globais para evitar telas inconsistentes e reduzir estados não determinísticos."><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[["enabled","Governança UI"],["compact_mode","Modo compacto"],["remember_last_tab","Lembrar última aba"],["confirm_destructive_actions","Confirmar ações destrutivas"],["autosave_enabled","Autosave"],["show_advanced_controls","Mostrar controles avançados"],["show_diagnostics","Mostrar diagnósticos"]].map(([key,label])=><BoolSetting key={key} label={label} checked={uiGovernance[key] !== false} onChange={(v)=>patchCommandSection("ui_governance_config",{[key]:v})}/>)}</div><div className="flex justify-end mt-3"><Button onClick={()=>saveCommandSection("ui_governance_config","Governança de interface salva.")}>Salvar UI</Button></div></SettingGroup>
                </>}

                {commandTab === "modulos" && <>
                  <SettingGroup title="Catálogo de módulos — configuração profunda" description="Todos os módulos oficiais aparecem aqui. O Admin pode preparar comportamento, rota, navegação, auditoria e exposição sem remover o catálogo."
                  ><div className="grid grid-cols-1 md:grid-cols-3 gap-3"><div><Label className="text-xs">Módulo</Label><Select value={moduleRegistryModule} onValueChange={setModuleRegistryModule}><SelectTrigger><SelectValue placeholder="Selecione..."/></SelectTrigger><SelectContent>{commandModuleEntries.map(([m,meta])=><SelectItem key={m} value={m}>{meta?.name || m}</SelectItem>)}</SelectContent></Select></div><div className="md:col-span-2 rounded-lg border border-border p-3 bg-muted/20"><p className="text-sm font-semibold">{moduleMeta?.name || moduleRegistryModule || "—"}</p><p className="text-[10px] text-muted-foreground">{moduleRegistryModule} · feature: {moduleMeta?.feature || "—"} · ações: {(moduleMeta?.actions || []).length}</p></div></div>{moduleRegistryModule && <div className="space-y-3 mt-3"><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[["enabled","Disponível globalmente"],["menu_visible","Visível no menu"],["dashboard_visible","Visível no Dashboard"],["app_visible","Visível nos aplicativos"],["audit_enabled","Auditoria detalhada"],["maintenance_mode","Modo manutenção"],["show_breadcrumb","Mostrar breadcrumb"],["allow_deep_link","Permitir acesso por rota direta"]].map(([key,label])=><BoolSetting key={key} label={label} checked={commandModule[key] !== false} onChange={(v)=>patchModuleRegistry(moduleRegistryModule,{[key]:v})}/>)}</div><div className="grid grid-cols-1 md:grid-cols-4 gap-3"><div><Label className="text-xs">Rota</Label><Input value={commandModule.route || `/${moduleRegistryModule}`} onChange={(e)=>patchModuleRegistry(moduleRegistryModule,{route:e.target.value})}/></div><div><Label className="text-xs">Grupo de menu</Label><Input value={commandModule.menu_group || "operacao"} onChange={(e)=>patchModuleRegistry(moduleRegistryModule,{menu_group:e.target.value})}/></div><div><Label className="text-xs">Ordem</Label><Input type="number" value={commandModule.menu_order || 0} onChange={(e)=>patchModuleRegistry(moduleRegistryModule,{menu_order:Number(e.target.value)})}/></div><div><Label className="text-xs">Ícone</Label><Input value={commandModule.icon || "Layers"} onChange={(e)=>patchModuleRegistry(moduleRegistryModule,{icon:e.target.value})}/></div></div><div className="grid grid-cols-1 md:grid-cols-3 gap-3"><div><Label className="text-xs">Descrição</Label><Input value={commandModule.description || ""} onChange={(e)=>patchModuleRegistry(moduleRegistryModule,{description:e.target.value})}/></div><div><Label className="text-xs">Tags</Label><Input value={(commandModule.tags || []).join(", ")} onChange={(e)=>patchModuleRegistry(moduleRegistryModule,{tags:e.target.value.split(",").map(x=>x.trim()).filter(Boolean)})}/></div><div><Label className="text-xs">Dependências</Label><Input value={(commandModule.dependencies || []).join(", ")} onChange={(e)=>patchModuleRegistry(moduleRegistryModule,{dependencies:e.target.value.split(",").map(x=>x.trim()).filter(Boolean)})}/></div></div><div className="flex justify-end"><Button onClick={()=>saveCommandSection("module_registry","Catálogo do módulo salvo.")}>Salvar módulo</Button></div></div>}</SettingGroup>
                  <SettingGroup title="Inventário operacional" description="Visão rápida de tudo que pode ser controlado pela Central de Comando."><div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[620px] overflow-auto">{commandModuleEntries.map(([m,meta])=><button type="button" key={m} onClick={()=>setModuleRegistryModule(m)} className="text-left rounded-lg border border-border p-3 hover:border-primary/40"><div className="flex justify-between gap-2"><p className="text-sm font-semibold truncate">{meta?.name || m}</p><Badge variant="outline">{(meta?.actions || []).length} ações</Badge></div><p className="text-[10px] text-muted-foreground mt-1">{m} · {moduleRegistry[m]?.menu_group || "operacao"}</p></button>)}</div></SettingGroup>
                </>}

                
                {commandTab === "loading" && <SettingGroup title="Loading / Inicialização — todos os textos" description="Controle integral da experiência de inicialização."
                ><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[["enabled","Loading habilitado"],["show_progress","Mostrar progresso"],["allow_cancel","Permitir cancelamento"],["safe_mode_on_timeout","Modo seguro após timeout"]].map(([key,label])=><BoolSetting key={key} label={label} checked={loadingCfg[key] !== false} onChange={(v)=>patchCommandSection("loading_config",{[key]:v})}/>)}</div><div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3"><div><Label className="text-xs">Título</Label><Input value={loadingCfg.title || "Carregando ASTRAL"} onChange={(e)=>patchCommandSection("loading_config",{title:e.target.value})}/></div><div><Label className="text-xs">Subtítulo</Label><Input value={loadingCfg.subtitle || "Preparando ambiente de trabalho..."} onChange={(e)=>patchCommandSection("loading_config",{subtitle:e.target.value})}/></div><div><Label className="text-xs">Mensagem inicial</Label><Input value={loadingCfg.initializing_message || "Iniciando módulos, permissões e dados..."} onChange={(e)=>patchCommandSection("loading_config",{initializing_message:e.target.value})}/></div><div><Label className="text-xs">Conclusão</Label><Input value={loadingCfg.completed_message || "Tudo pronto."} onChange={(e)=>patchCommandSection("loading_config",{completed_message:e.target.value})}/></div><div><Label className="text-xs">Erro</Label><Input value={loadingCfg.error_message || "Não foi possível concluir a inicialização."} onChange={(e)=>patchCommandSection("loading_config",{error_message:e.target.value})}/></div><div><Label className="text-xs">Recuperação</Label><Input value={loadingCfg.recovery_button_label || "Tentar novamente"} onChange={(e)=>patchCommandSection("loading_config",{recovery_button_label:e.target.value})}/></div></div><div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3"><div><Label className="text-xs">Mínimo (ms)</Label><Input type="number" value={loadingCfg.min_duration_ms || 350} onChange={(e)=>patchCommandSection("loading_config",{min_duration_ms:Number(e.target.value)})}/></div><div><Label className="text-xs">Timeout (ms)</Label><Input type="number" value={loadingCfg.timeout_ms || 30000} onChange={(e)=>patchCommandSection("loading_config",{timeout_ms:Number(e.target.value)})}/></div><div><Label className="text-xs">Variante</Label><Select value={loadingCfg.variant || "branded"} onValueChange={(v)=>patchCommandSection("loading_config",{variant:v})}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{[["branded","Institucional"],["minimal","Minimalista"],["technical","Técnico"],["progressive","Progressivo"]].map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div><div><Label className="text-xs">Transição</Label><Select value={loadingCfg.transition || "fade"} onValueChange={(v)=>patchCommandSection("loading_config",{transition:v})}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{[["fade","Fade"],["slide","Slide"],["none","Nenhuma"]].map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div></div><div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3"><div><Label className="text-xs">Etapas (uma por linha)</Label><Textarea rows={7} value={(loadingCfg.steps || []).join("\n")} onChange={(e)=>patchCommandSection("loading_config",{steps:e.target.value.split("\n").map(v=>v.trim()).filter(Boolean)})}/></div><div><Label className="text-xs">Mensagens por etapa (chave=texto)</Label><Textarea rows={7} value={Object.entries(loadingCfg.step_messages || {}).map(([k,v])=>`${k}=${v}`).join("\n")} onChange={(e)=>patchCommandSection("loading_config",{step_messages:Object.fromEntries(e.target.value.split("\n").map(line=>line.split("=")).filter(p=>p.length>1).map(([k,...v])=>[k.trim(),v.join("=").trim()]))})}/></div></div><div className="flex gap-2 justify-end mt-3"><Button variant="outline" onClick={previewLoading}>{loadingPreview?"Simulando...":"Pré-visualizar"}</Button><Button onClick={()=>saveCommandSection("loading_config","Loading salvo.")}>Salvar Loading</Button></div>{loadingPreview&&<div className="rounded-lg border border-primary/20 bg-primary/5 p-4"><p className="text-sm font-semibold">{loadingCfg.title}</p><p className="text-xs text-muted-foreground mt-1">{loadingCfg.initializing_message}</p></div>}</SettingGroup>}

                {commandTab === "notificacoes" && <><SettingGroup title="Matriz de distribuição — global x individual" description="Cada evento pode ser sistêmico, pessoal ou direcionado. Eventos obrigatórios podem ser aplicados globalmente sem depender de preferências pessoais."><div className="grid grid-cols-1 md:grid-cols-2 gap-3"><div><Label className="text-xs">Evento</Label><Select value={notificationEvent} onValueChange={setNotificationEvent}><SelectTrigger><SelectValue placeholder="Selecione um evento"/></SelectTrigger><SelectContent>{Object.keys(EVENT_LABELS).map(event=><SelectItem key={event} value={event}>{EVENT_LABELS[event]}</SelectItem>)}</SelectContent></Select></div><div><Label className="text-xs">Aplicação</Label><Select value={notificationConfig.events?.[notificationEvent]?.application_scope || "targeted"} onValueChange={(v)=>updateNestedCommand("notifications_config",["events",notificationEvent,"application_scope"],v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{[["global","Global — todos"],["self","Somente destinatário"],["targeted","Direcionada"],["admin_only","Somente Admin"]].map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div></div><div className="grid grid-cols-1 md:grid-cols-3 gap-3"><BoolSetting label="Obrigatória" checked={!!notificationConfig.events?.[notificationEvent]?.mandatory} onChange={(v)=>updateNestedCommand("notifications_config",["events",notificationEvent,"mandatory"],v)}/><BoolSetting label="Pode dispensar" checked={notificationConfig.events?.[notificationEvent]?.allow_user_dismiss !== false} onChange={(v)=>updateNestedCommand("notifications_config",["events",notificationEvent,"allow_user_dismiss"],v)}/><BoolSetting label="Agrupar duplicadas" checked={notificationConfig.events?.[notificationEvent]?.dedupe !== false} onChange={(v)=>updateNestedCommand("notifications_config",["events",notificationEvent,"dedupe"],v)}/></div><div className="grid grid-cols-1 md:grid-cols-4 gap-3"><div><Label className="text-xs">Perfis CSV</Label><Input value={(notificationConfig.routing?.[notificationEvent]?.roles || []).join(", ")} onChange={(e)=>updateNestedCommand("notifications_config",["routing",notificationEvent,"roles"],e.target.value.split(",").map(v=>v.trim().toLowerCase()).filter(Boolean))}/></div><div><Label className="text-xs">Usuários CSV</Label><Input value={(notificationConfig.routing?.[notificationEvent]?.user_ids || []).join(", ")} onChange={(e)=>updateNestedCommand("notifications_config",["routing",notificationEvent,"user_ids"],e.target.value.split(",").map(v=>v.trim()).filter(Boolean))}/></div><div><Label className="text-xs">Canais CSV</Label><Input value={(notificationConfig.events?.[notificationEvent]?.channels || []).join(", ")} onChange={(e)=>updateNestedCommand("notifications_config",["events",notificationEvent,"channels"],e.target.value.split(",").map(v=>v.trim()).filter(Boolean))}/></div><div><Label className="text-xs">SLA (min)</Label><Input type="number" value={notificationConfig.events?.[notificationEvent]?.sla_minutes || 0} onChange={(e)=>updateNestedCommand("notifications_config",["events",notificationEvent,"sla_minutes"],Number(e.target.value))}/></div></div><div className="flex justify-end gap-2"><Button variant="outline" onClick={previewNotification} disabled={!notificationEvent || !!saving}>{saving==="notification_preview"?"Consultando...":"Pré-visualizar regra"}</Button><Button onClick={()=>saveCommandSection("notifications_config","Matriz de notificações salva.")}>Salvar notificações</Button></div>{notificationPreview&&<JsonSummary value={notificationPreview} maxHeight={300}/>}</SettingGroup></>}

                {commandTab === "relatorios" && <SettingGroup title="Relatórios por categoria" description="O motor central fornece uma configuração resolvida por categoria. Cada tipo pode sobrescrever página, identidade, evidências, fotos, assinaturas, QR, seções e layout."><div className="grid grid-cols-1 md:grid-cols-3 gap-3"><div><Label className="text-xs">Categoria</Label><Select value={reportCategory} onValueChange={setReportCategory}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{Object.entries(reportConfig?.categories || {}).map(([key,val])=><SelectItem key={key} value={key}>{val?.label || key}</SelectItem>)}</SelectContent></Select></div><div><Label className="text-xs">Template</Label><Input value={reportConfig?.categories?.[reportCategory]?.template || reportCategory} onChange={(e)=>updateNestedCommand("reports_config",["categories",reportCategory,"template"],e.target.value)}/></div><div><Label className="text-xs">Rótulo</Label><Input value={reportConfig?.categories?.[reportCategory]?.label || ""} onChange={(e)=>updateNestedCommand("reports_config",["categories",reportCategory,"label"],e.target.value)}/></div></div><div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3"><SettingGroup title="Página"><div className="grid grid-cols-2 gap-2"><div><Label className="text-[10px]">Papel</Label><Select value={reportConfig?.categories?.[reportCategory]?.page?.size || "A4"} onValueChange={(v)=>updateNestedCommand("reports_config",["categories",reportCategory,"page","size"],v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{["A4","A3","A5","Letter","Legal"].map(v=><SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div><div><Label className="text-[10px]">Orientação</Label><Select value={reportConfig?.categories?.[reportCategory]?.page?.orientation || "portrait"} onValueChange={(v)=>updateNestedCommand("reports_config",["categories",reportCategory,"page","orientation"],v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="portrait">Retrato</SelectItem><SelectItem value="landscape">Paisagem</SelectItem></SelectContent></Select></div></div></SettingGroup><SettingGroup title="Elementos"><div className="grid grid-cols-1 gap-2">{[["branding","show_logo","Logo"],["branding","use_letterhead","Papel timbrado"],["branding","watermark_enabled","Marca d'água"],["header","show_report_title","Título"],["header","show_period","Período"],["footer","show_page_number","Paginação"],["photos","enabled","Fotos"],["signatures","enabled","Assinaturas"],["show_qr",null,"QR Code"]].map(([group,key,label])=><BoolSetting key={`${group}-${key}`} label={label} checked={key===null?reportConfig?.categories?.[reportCategory]?.show_qr !== false:reportConfig?.categories?.[reportCategory]?.[group]?.[key] !== false} onChange={(v)=>key===null?updateNestedCommand("reports_config",["categories",reportCategory,"show_qr"],v):updateNestedCommand("reports_config",["categories",reportCategory,group,key],v)}/>)}</div></SettingGroup></div><div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3"><div><Label className="text-xs">Fotos/seção</Label><Input type="number" value={reportConfig?.categories?.[reportCategory]?.photos?.max_per_section || 6} onChange={(e)=>updateNestedCommand("reports_config",["categories",reportCategory,"photos","max_per_section"],Number(e.target.value))}/></div><div><Label className="text-xs">Colunas fotos</Label><Input type="number" value={reportConfig?.categories?.[reportCategory]?.photos?.columns || 3} onChange={(e)=>updateNestedCommand("reports_config",["categories",reportCategory,"photos","columns"],Number(e.target.value))}/></div><div><Label className="text-xs">Assinaturas</Label><Input type="number" value={reportConfig?.categories?.[reportCategory]?.signatures?.count || 3} onChange={(e)=>updateNestedCommand("reports_config",["categories",reportCategory,"signatures","count"],Number(e.target.value))}/></div><div><Label className="text-xs">Seções CSV</Label><Input value={(reportConfig?.categories?.[reportCategory]?.sections_order || []).join(", ")} onChange={(e)=>updateNestedCommand("reports_config",["categories",reportCategory,"sections_order"],e.target.value.split(",").map(v=>v.trim()).filter(Boolean))}/></div></div><div className="flex justify-end mt-3"><Button onClick={()=>saveCommandSection("reports_config",`Relatório ${REPORT_CATEGORY_LABELS[reportCategory] || reportCategory} salvo.`)}>Salvar categoria</Button></div></SettingGroup>}

                {commandTab === "seguranca" && <SettingGroup title="Segurança de plataforma" description="Controles administrativos de sessão, senha, auditoria, tentativas e proteção das rotas."><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[["force_password_rotation","Rotação obrigatória de senha"],["require_mfa_for_admin","MFA para Admin"],["block_after_failed_attempts","Bloqueio após tentativas"],["session_revocation_enabled","Revogação de sessões"],["audit_security_events","Auditoria de segurança"],["protect_configuration_routes","Proteção de rotas críticas"],["csrf_protection","CSRF"],["login_rate_limit","Rate limit"]].map(([key,label])=><BoolSetting key={key} label={label} checked={securityConfig[key] !== false} onChange={(v)=>updateNestedCommand("security",[key],v)}/>)}</div><div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3"><div><Label className="text-xs">Tentativas</Label><Input type="number" value={securityConfig.max_login_attempts || 5} onChange={(e)=>updateNestedCommand("security",["max_login_attempts"],Number(e.target.value))}/></div><div><Label className="text-xs">Sessão (min)</Label><Input type="number" value={securityConfig.session_timeout_minutes || 480} onChange={(e)=>updateNestedCommand("security",["session_timeout_minutes"],Number(e.target.value))}/></div><div><Label className="text-xs">Expiração senha (dias)</Label><Input type="number" value={securityConfig.password_expiration_days || 90} onChange={(e)=>updateNestedCommand("security",["password_expiration_days"],Number(e.target.value))}/></div><div><Label className="text-xs">Rate limit / janela</Label><Input type="number" value={securityConfig.rate_limit_window_seconds || 60} onChange={(e)=>updateNestedCommand("security",["rate_limit_window_seconds"],Number(e.target.value))}/></div></div><div className="flex justify-end mt-3"><Button onClick={()=>saveCommandSection("security","Segurança salva.")}>Salvar segurança</Button></div></SettingGroup>}

                {commandTab === "geoespacial" && <SettingGroup title="Geoespacial — configuração profunda" description="GPS, precisão, histórico, persistência, mapa, provedor e apresentação."><div className="grid grid-cols-1 md:grid-cols-2 gap-3">{[["enabled","Geoespacial"],["gps_enabled","GPS"],["require_accuracy","Precisão obrigatória"],["allow_manual_location","Localização manual"],["track_history","Histórico"],["store_coordinates","Persistir coordenadas"],["show_map_on_dashboard","Mapa no Dashboard"],["show_map_on_reports","Mapa nos relatórios"]].map(([key,label])=><BoolSetting key={key} label={label} checked={geoConfig[key] !== false} onChange={(v)=>updateNestedCommand("geo_config",[key],v)}/>)}</div><div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3"><div><Label className="text-xs">Precisão mínima (m)</Label><Input type="number" value={geoConfig.min_accuracy_m || 50} onChange={(e)=>updateNestedCommand("geo_config",["min_accuracy_m"],Number(e.target.value))}/></div><div><Label className="text-xs">Intervalo GPS (s)</Label><Input type="number" value={geoConfig.gps_interval_seconds || 30} onChange={(e)=>updateNestedCommand("geo_config",["gps_interval_seconds"],Number(e.target.value))}/></div><div><Label className="text-xs">Zoom</Label><Input type="number" value={geoConfig.default_zoom || 15} onChange={(e)=>updateNestedCommand("geo_config",["default_zoom"],Number(e.target.value))}/></div><div><Label className="text-xs">Provedor</Label><Select value={geoConfig.provider || "osm"} onValueChange={(v)=>updateNestedCommand("geo_config",["provider"],v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{[["osm","OpenStreetMap"],["google","Google Maps"],["mapbox","Mapbox"],["custom","Customizado"]].map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div></div><div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3"><div><Label className="text-xs">URL tiles</Label><Input value={geoConfig.tile_url || ""} onChange={(e)=>updateNestedCommand("geo_config",["tile_url"],e.target.value)}/></div><div><Label className="text-xs">Attribution</Label><Input value={geoConfig.attribution || ""} onChange={(e)=>updateNestedCommand("geo_config",["attribution"],e.target.value)}/></div></div><div className="flex justify-end mt-3"><Button onClick={()=>saveCommandSection("geo_config","Geoespacial salvo.")}>Salvar geoespacial</Button></div></SettingGroup>}
              </div>
            </div>
          </RobustCommandPanel>
        </TabsContent>

        {/* ================= APLICATIVOS ================= */}
        <TabsContent value="aplicativos" className="space-y-4 mt-4">
          <div className="flex items-center gap-2">
            {["tecnico", "cliente"].map((k) => (
              <button key={k} onClick={() => setAppKey(k)} data-testid={`app-select-${k}`}
                className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${appKey === k ? "bg-primary/15 text-primary border-primary/40" : "border-border text-muted-foreground hover:bg-accent"}`}>
                <Smartphone size={13} className="inline mr-1.5 -mt-0.5" /> App do {k === "tecnico" ? "Técnico" : "Cliente"}
              </button>
            ))}
            <Badge variant="outline" className="ml-auto text-[10px]">Atualização dinâmica: o app busca estas configurações a cada inicialização via GET /api/app-config</Badge>
          </div>

          <SectionCard icon={Palette} title="Identidade Visual do Aplicativo" testId="app-identity-card"
            desc="Nome, logotipo, splash, banner e cores — alterados remotamente sem publicar nova versão do app."
            onSave={() => saveCommandSection("app_config")} saving={saving === "command:app_config"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Nome do aplicativo</Label>
                <Input value={ac.identity?.app_name || ""} onChange={(e) => setApp("identity", { app_name: e.target.value })} data-testid="app-name-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Tema padrão</Label>
                <Select value={ac.identity?.theme_default || "dark"} onValueChange={(v) => setApp("identity", { theme_default: v })}>
                  <SelectTrigger data-testid="app-theme-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="dark">Escuro</SelectItem><SelectItem value="light">Claro</SelectItem><SelectItem value="system">Automático (sistema)</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <ImageField label="Logotipo" value={ac.identity?.logo} onChange={(v) => setApp("identity", { logo: v })} testId="app-logo-upload" />
              <ImageField label="Ícone" value={ac.identity?.icon} onChange={(v) => setApp("identity", { icon: v })} testId="app-icon-upload" />
              <ImageField label="Splash Screen" value={ac.identity?.splash} onChange={(v) => setApp("identity", { splash: v })} testId="app-splash-upload" />
              <ImageField label="Banner principal" value={ac.identity?.banner} onChange={(v) => setApp("identity", { banner: v })} testId="app-banner-upload" />
              <ImageField label="Imagem de fundo" value={ac.identity?.background} onChange={(v) => setApp("identity", { background: v })} testId="app-bg-upload" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[["color_primary", "Cor primária"], ["color_bg_dark", "Fundo (tema escuro)"], ["color_bg_light", "Fundo (tema claro)"]].map(([k, l]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{l}</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={ac.identity?.[k] || "#f97316"} onChange={(e) => setApp("identity", { [k]: e.target.value })}
                      className="h-9 w-12 rounded-md border border-border bg-transparent cursor-pointer" data-testid={`app-${k}`} />
                    <Input value={ac.identity?.[k] || ""} onChange={(e) => setApp("identity", { [k]: e.target.value })} className="h-9 font-mono text-xs" />
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={SlidersHorizontal} title="Interface e Menus" testId="app-interface-card"
            desc="Ordem dos menus, tela inicial e comportamento — refletidos no app na próxima sincronização."
            onSave={() => saveCommandSection("app_config")} saving={saving === "command:app_config"}>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs">Ordem dos menus</Label>
                <div className="space-y-1">
                  {(ac.interface?.menu_order || []).map((m, i) => (
                    <div key={m} className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-card text-sm" data-testid={`menu-item-${m}`}>
                      <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                      <span className="flex-1">{MENU_LABELS[m] || m}</span>
                      <button onClick={() => moveMenu(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-primary disabled:opacity-30" data-testid={`menu-up-${m}`}><ArrowUp size={13} /></button>
                      <button onClick={() => moveMenu(i, 1)} disabled={i === (ac.interface?.menu_order || []).length - 1} className="text-muted-foreground hover:text-primary disabled:opacity-30" data-testid={`menu-down-${m}`}><ArrowDown size={13} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label className="text-xs">Tela inicial</Label>
                  <Select value={ac.interface?.home_screen || "dashboard"} onValueChange={(v) => setApp("interface", { home_screen: v })}>
                    <SelectTrigger data-testid="app-home-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(MENU_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                  <div><p className="text-sm font-medium flex items-center gap-2"><Gauge size={14} /> Dashboard inicial visível</p>
                    <p className="text-xs text-muted-foreground">Exibe o dashboard resumido na tela inicial do app</p></div>
                  <Switch checked={ac.interface?.show_dashboard !== false} onCheckedChange={(v) => setApp("interface", { show_dashboard: v })} data-testid="app-show-dashboard" />
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={SlidersHorizontal} title="Funcionalidades" testId="app-features-card"
            desc="Habilite ou desabilite recursos do aplicativo sem necessidade de atualização na loja."
            onSave={() => saveCommandSection("app_config")} saving={saving === "command:app_config"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(cfg.app_features || []).map((f) => (
                <div key={f} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                  <p className="text-sm">{APP_FEATURE_LABELS[f] || f}</p>
                  <Switch checked={!!ac.features?.[f]} onCheckedChange={(v) => setApp("features", { [f]: v })} data-testid={`app-feature-${f}`} />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard icon={FileText} title="Conteúdo e Mensagens" testId="app-content-card"
            desc="Texto de boas-vindas, avisos, banners e campanhas exibidos no aplicativo."
            onSave={() => saveCommandSection("app_config")} saving={saving === "command:app_config"}>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Texto de boas-vindas</Label>
                <Textarea rows={2} value={ac.content?.welcome || ""} onChange={(e) => setApp("content", { welcome: e.target.value })} className="resize-none text-sm" data-testid="app-welcome-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Aviso / informativo ativo</Label>
                <Textarea rows={2} value={ac.content?.notice || ""} onChange={(e) => setApp("content", { notice: e.target.value })} className="resize-none text-sm" placeholder="Ex.: Manutenção programada dia 15/07" data-testid="app-notice-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Texto do banner</Label>
                <Input value={ac.content?.banner_text || ""} onChange={(e) => setApp("content", { banner_text: e.target.value })} data-testid="app-banner-text" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Campanha ativa</Label>
                <Input value={ac.content?.campaign || ""} onChange={(e) => setApp("content", { campaign: e.target.value })} placeholder="Ex.: Campanha de prevenção — verão 2026" data-testid="app-campaign-input" /></div>
            </div>
           </SectionCard>
         </TabsContent>
           <TabsContent value="login-page" className="space-y-4 mt-4">
          <SectionCard
            icon={ImageIcon}
            title="Configuração da Login Page"
            testId="login-page-config-card"
            desc="Configure o carrossel da tela de login. O fundo institucional continua sendo controlado pela Identidade Visual do sistema; esta configuração não adiciona logotipo sobre o fundo."
            onSave={saveLoginPage}
            saving={saving === "command:login_page_config"}
          >
            <SectionCard icon={Clock3} title="Tela de Carregamento / Loading" testId="loading-page-config-card" desc="Parametrize todos os textos e comportamentos exibidos durante o carregamento da plataforma." onSave={saveLoginPage} saving={saving === "command:login_page_config"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5"><div><p className="text-sm">Loading habilitado</p><p className="text-xs text-muted-foreground">Controla a exibição da tela de carregamento.</p></div><Switch checked={loginPage.loading?.enabled !== false} onCheckedChange={(v) => setLoginPage({ loading: { ...(loginPage.loading || {}), enabled: v } })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Título</Label><Input value={loginPage.loading?.title || "Carregando ASTRAL"} onChange={(e) => setLoginPage({ loading: { ...(loginPage.loading || {}), title: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Subtítulo</Label><Input value={loginPage.loading?.subtitle || "Preparando seu ambiente de trabalho..."} onChange={(e) => setLoginPage({ loading: { ...(loginPage.loading || {}), subtitle: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Mensagem de progresso</Label><Input value={loginPage.loading?.progress_text || "Inicializando módulos, permissões e dados..."} onChange={(e) => setLoginPage({ loading: { ...(loginPage.loading || {}), progress_text: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Texto de espera</Label><Input value={loginPage.loading?.waiting_text || "Quase pronto..."} onChange={(e) => setLoginPage({ loading: { ...(loginPage.loading || {}), waiting_text: e.target.value } })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Duração mínima (ms)</Label><Input type="number" min="0" max="30000" value={loginPage.loading?.min_duration_ms ?? 0} onChange={(e) => setLoginPage({ loading: { ...(loginPage.loading || {}), min_duration_ms: Number(e.target.value) } })} /></div>
              </div>
            </SectionCard>
            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-sm font-semibold text-primary">
                Fundo da Login Page
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                O fundo central da tela de login permanece exclusivamente vinculado
                à configuração visual existente do sistema. Os slides abaixo são
                conteúdo do carrossel e não substituem, duplicam ou inserem a logo
                institucional como imagem de fundo.
              </p>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-4">
              <div>
                <h4 className="font-heading font-semibold text-sm">
                  Comportamento do carrossel
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Controle global da apresentação dos slides da Login Page.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium">Carrossel habilitado</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Exibe os slides configurados.
                    </p>
                  </div>
                  <Switch
                    checked={loginPage.enabled !== false}
                    onCheckedChange={(enabled) => setLoginPage({ enabled })}
                    data-testid="login-page-enabled"
                  />
                </div>

                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium">Reprodução automática</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Avança os slides automaticamente.
                    </p>
                  </div>
                  <Switch
                    checked={loginPage.autoplay !== false}
                    onCheckedChange={(autoplay) => setLoginPage({ autoplay })}
                    data-testid="login-page-autoplay"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Duração padrão (ms)</Label>
                  <Input
                    type="number"
                    min={1500}
                    max={60000}
                    step={500}
                    value={loginPage.default_duration_ms ?? 4500}
                    onChange={(e) =>
                      setLoginPage({
                        default_duration_ms: Math.max(
                          1500,
                          Math.min(60000, Number(e.target.value || 4500)),
                        ),
                      })
                    }
                    data-testid="login-page-default-duration"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Transição</Label>
                  <Select
                    value={loginPage.transition || "fade"}
                    onValueChange={(transition) => setLoginPage({ transition })}
                  >
                    <SelectTrigger data-testid="login-page-transition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fade">Fade</SelectItem>
                      <SelectItem value="slide">Deslizar</SelectItem>
                      <SelectItem value="none">Sem transição</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-4">
              <div>
                <h4 className="font-heading font-semibold text-sm">
                  Quantidade de brandings
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Defina quantos brandings participarão do carrossel. O limite é de 1 a 8.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs">Total de brandings</Label>
                  <Select
                    value={String(loginPage.branding_slide_count || 1)}
                    onValueChange={(value) => setBrandingSlideCount(Number(value))}
                  >
                    <SelectTrigger data-testid="login-page-branding-count">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 8 }, (_, index) => index + 1).map((count) => (
                        <SelectItem key={count} value={String(count)}>
                          {count} {count === 1 ? "branding" : "brandings"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5">
                  <p className="text-xs font-medium text-primary">
                    Todos os brandings usam 4,5 segundos
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    A duração é padronizada para manter o fluxo visual uniforme.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h4 className="font-heading font-semibold text-sm">
                    Brandings da Login Page
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Envie imagens originais para evitar imagens borradas ou
                    comprimidas artificialmente. A imagem é armazenada pelo backend
                    e vinculada somente ao slide.
                  </p>
                </div>

                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addLoginSlide}
                  data-testid="login-page-add-slide"
                >
                  <ImageIcon size={14} className="mr-1.5" />
                  Adicionar branding
                </Button>
              </div>

              {brandingSlides.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border py-12 text-center">
                  <ImageIcon size={28} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">Nenhum branding cadastrado</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Cada branding é um slot do carrossel. Você pode ter de 1 a 8 brandings.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={addLoginSlide}
                    data-testid="login-page-add-first-slide"
                  >
                    <ImageIcon size={14} className="mr-1.5" />
                    Criar primeiro branding
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {brandingSlides.map((slide, index) => (
                    <div
                      key={slide.id}
                      className="rounded-lg border border-border p-4 space-y-4"
                      data-testid={`login-page-slide-${slide.id}`}
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">Branding {index + 1}</Badge>
                          <span className="text-xs text-muted-foreground">
                            ordem {slide.order || index + 1}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8"
                            onClick={() => moveLoginSlide(index, -1)}
                            disabled={index === 0}
                            title="Mover para cima"
                            data-testid={`login-page-slide-up-${slide.id}`}
                          >
                            <ArrowUp size={14} />
                          </Button>

                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8"
                            onClick={() => moveLoginSlide(index, 1)}
                            disabled={index === brandingSlides.length - 1}
                            title="Mover para baixo"
                            data-testid={`login-page-slide-down-${slide.id}`}
                          >
                            <ArrowDown size={14} />
                          </Button>

                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 text-red-400 hover:text-red-400"
                            onClick={() => removeLoginSlide(slide.id)}
                            title="Remover slide"
                            data-testid={`login-page-slide-remove-${slide.id}`}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5">
                        <div className="space-y-3">
                          <div className="aspect-video rounded-lg overflow-hidden border border-border bg-muted/20 flex items-center justify-center">
                            {slide.image_url ? (
                              <img
                                src={slide.image_url}
                                alt={slide.title || `Slide ${index + 1}`}
                                className="w-full h-full object-cover"
                                style={{
                                  objectPosition: slide.object_position || "center",
                                }}
                              />
                            ) : (
                              <div className="text-center px-4">
                                <ImageIcon size={28} className="mx-auto text-muted-foreground mb-2" />
                                <p className="text-xs text-muted-foreground">
                                  Nenhuma imagem enviada
                                </p>
                              </div>
                            )}
                          </div>

                          <label className="flex items-center justify-center gap-2 w-full h-9 rounded-md border border-border text-xs font-medium cursor-pointer hover:bg-accent transition-colors">
                            {loginPageUploading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Upload size={14} />
                            )}
                            {loginPageUploading ? "Enviando..." : "Enviar imagem"}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/avif"
                              className="hidden"
                              disabled={loginPageUploading}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) uploadLoginSlideImage(slide.id, file);
                                e.target.value = "";
                              }}
                              data-testid={`login-page-slide-upload-${slide.id}`}
                            />
                          </label>

                          {slide.image_name && (
                            <p className="text-[10px] text-muted-foreground break-all">
                              {slide.image_name}
                            </p>
                          )}
                        </div>

                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <Label className="text-xs">Eyebrow</Label>
                              <Input
                                value={slide.eyebrow || ""}
                                onChange={(e) =>
                                  updateLoginSlide(slide.id, { eyebrow: e.target.value })
                                }
                                data-testid={`login-page-slide-eyebrow-${slide.id}`}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs">Título</Label>
                              <Input
                                value={slide.title || ""}
                                onChange={(e) =>
                                  updateLoginSlide(slide.id, { title: e.target.value })
                                }
                                data-testid={`login-page-slide-title-${slide.id}`}
                              />
                            </div>

                            <div className="space-y-1.5 md:col-span-2">
                              <Label className="text-xs">Texto</Label>
                              <Textarea
                                rows={3}
                                value={slide.text || ""}
                                onChange={(e) =>
                                  updateLoginSlide(slide.id, { text: e.target.value })
                                }
                                className="resize-none"
                                data-testid={`login-page-slide-text-${slide.id}`}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs">Texto de destaque</Label>
                              <Input
                                value={slide.accent || ""}
                                onChange={(e) =>
                                  updateLoginSlide(slide.id, { accent: e.target.value })
                                }
                                placeholder="Ex.: ASTRAL"
                                data-testid={`login-page-slide-accent-${slide.id}`}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs">Duração (ms)</Label>
                              <Input
                                type="number"
                                min={1500}
                                max={60000}
                                step={500}
                                value={slide.duration_ms ?? loginPage.default_duration_ms ?? 4500}
                                onChange={(e) =>
                                  updateLoginSlide(slide.id, {
                                    duration_ms: Math.max(
                                      1500,
                                      Math.min(60000, Number(e.target.value || 4500)),
                                    ),
                                  })
                                }
                                data-testid={`login-page-slide-duration-${slide.id}`}
                              />
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs">Posição da imagem</Label>
                              <Select
                                value={slide.object_position || "center"}
                                onValueChange={(object_position) =>
                                  updateLoginSlide(slide.id, { object_position })
                                }
                              >
                                <SelectTrigger data-testid={`login-page-slide-position-${slide.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="center">Centro</SelectItem>
                                  <SelectItem value="top">Superior</SelectItem>
                                  <SelectItem value="bottom">Inferior</SelectItem>
                                  <SelectItem value="left">Esquerda</SelectItem>
                                  <SelectItem value="right">Direita</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs">
                                Opacidade do overlay
                              </Label>
                              <Input
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={slide.overlay ?? 0.35}
                                onChange={(e) =>
                                  updateLoginSlide(slide.id, {
                                    overlay: Math.max(
                                      0,
                                      Math.min(1, Number(e.target.value || 0)),
                                    ),
                                  })
                                }
                                data-testid={`login-page-slide-overlay-${slide.id}`}
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                            <div>
                              <p className="text-sm font-medium">Slide ativo</p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                Slides desativados não são exibidos na Login Page.
                              </p>
                            </div>
                            <Switch
                              checked={slide.enabled !== false}
                              onCheckedChange={(enabled) =>
                                updateLoginSlide(slide.id, { enabled })
                              }
                              data-testid={`login-page-slide-enabled-${slide.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-xs font-medium text-primary">
                Regra visual importante
              </p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                Esta configuração não cria nem injeta uma segunda logo no fundo da
                Login Page. A marca institucional continua sendo responsabilidade da
                configuração de identidade visual existente. Os slides são apenas
                conteúdo do carrossel e suas imagens ficam limitadas ao próprio slide.
              </p>
            </div>
          </SectionCard>
        </TabsContent>

        {/* ================= SOLICITAÇÕES DE ACESSO ================= */}
        <TabsContent value="solicitacoes" className="space-y-4 mt-4">
          <SectionCard
            icon={UserPlus}
            title="Central de Solicitações de Acesso"
            testId="access-requests-card"
            desc="Analise solicitações de acesso, acompanhe o prazo, aprove ou rejeite com motivo e, após a aprovação, encaminhe o solicitante para finalizar o próprio cadastro."
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                ["pending", "Pendentes", accessSummary.pending, Clock3, "border-amber-500/30"],
                ["approved", "Aprovadas", accessSummary.approved, CheckCircle2, "border-emerald-500/30"],
                ["rejected", "Rejeitadas", accessSummary.rejected, XCircle, "border-red-500/30"],
                ["expired", "Expiradas", accessSummary.expired, AlertCircle, "border-slate-500/30"],
              ].map(([status, label, value, Icon, border]) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setAccessStatus(status)}
                  className={`rounded-lg border ${border} p-4 text-left hover:bg-accent/40 transition-colors ${
                    accessStatus === status ? "ring-1 ring-primary/50 bg-primary/5" : ""
                  }`}
                  data-testid={`access-summary-${status}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <Icon size={16} className="text-primary" />
                  </div>
                  <p className="text-2xl font-bold mt-2">{value}</p>
                </button>
              ))}
            </div>

            <div className="rounded-lg border border-border p-4 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h4 className="font-heading font-semibold text-sm">Configuração do fluxo</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Estas opções controlam o pré-cadastro público e as comunicações do fluxo.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  onClick={saveAccessConfig}
                  disabled={saving === "access_requests"}
                  data-testid="access-request-config-save"
                >
                  <Save size={13} className="mr-1.5" />
                  {saving === "access_requests" ? "Salvando..." : "Salvar configuração"}
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  ["enabled", "Permitir novos pré-cadastros", true],
                  ["send_request_confirmation_email", "E-mail de recebimento ao solicitante", true],
                  ["send_admin_email", "E-mail de nova solicitação ao administrador", true],
                  ["send_approval_email", "E-mail de aprovação", true],
                  ["send_rejection_email", "E-mail de rejeição", true],
                  ["send_internal_notification", "Notificação interna", true],
                ].map(([key, label, defaultValue]) => (
                  <div key={key} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                    <p className="text-sm">{label}</p>
                    <Switch
                      checked={accessCfg[key] !== false && (accessCfg[key] ?? defaultValue)}
                      onCheckedChange={(v) => setSec("access_requests", { ...accessCfg, [key]: v })}
                      data-testid={`access-config-${key}`}
                    />
                  </div>
                ))}

                <div className="space-y-1.5">
                  <Label className="text-xs">Prazo para análise (horas)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={720}
                    value={accessCfg.review_deadline_hours ?? 24}
                    onChange={(e) => setSec("access_requests", {
                      ...accessCfg,
                      review_deadline_hours: Math.max(1, Math.min(720, Number(e.target.value || 24))),
                    })}
                    data-testid="access-review-deadline"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">E-mail administrativo</Label>
                  <Input
                    value={accessCfg.admin_email || ""}
                    onChange={(e) => setSec("access_requests", {
                      ...accessCfg,
                      admin_email: e.target.value,
                    })}
                    placeholder="administracao@empresa.com"
                    data-testid="access-admin-email"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={accessSearch}
                  onChange={(e) => setAccessSearch(e.target.value)}
                  placeholder="Pesquisar nome, e-mail, protocolo, empresa ou unidade..."
                  className="pl-9"
                  data-testid="access-request-search"
                />
              </div>

              <Select value={accessStatus} onValueChange={setAccessStatus}>
                <SelectTrigger className="w-[170px]" data-testid="access-request-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="approved">Aprovadas</SelectItem>
                  <SelectItem value="rejected">Rejeitadas</SelectItem>
                  <SelectItem value="expired">Expiradas</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={() => loadAccessRequests(accessStatus)}
                disabled={accessLoading}
                data-testid="access-request-refresh"
              >
                <RefreshCw size={14} className={`mr-1.5 ${accessLoading ? "animate-spin" : ""}`} />
                Atualizar
              </Button>
            </div>

            {accessLoading ? (
              <div className="py-12 flex items-center justify-center text-sm text-muted-foreground">
                <Loader2 size={18} className="mr-2 animate-spin" />
                Carregando solicitações...
              </div>
            ) : accessFiltered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border py-12 text-center">
                <UserPlus size={28} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">Nenhuma solicitação encontrada</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Não há registros para o filtro selecionado.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="hidden lg:grid grid-cols-[1.2fr_1.1fr_1fr_.55fr_.7fr_.7fr_1fr] gap-3 px-4 py-3 bg-muted/20 border-b border-border text-[11px] font-semibold text-muted-foreground">
                  <span>Solicitante</span>
                  <span>Empresa / Unidade</span>
                  <span>Perfil</span>
                  <span>Origem</span>
                  <span>Status</span>
                  <span>Prazo</span>
                  <span className="text-right">Ações</span>
                </div>

                <div className="divide-y divide-border/50">
                  {accessFiltered.map((item) => {
                    const pending = item.status === "pending";
                    const approved = item.status === "approved";
                    const expired = pending && (item.expired || item.remaining_seconds === 0);
                    const approveBusy = accessAction === `approve:${item.id}`;
                    const resendBusy = resendingAccessLink === item.id;
                    const registrationCompleted = !!(
                      item.registration_completed_at ||
                      item.finalization_completed_at
                    );

                    return (
                      <div key={item.id} className="px-4 py-4">
                        <div className="grid lg:grid-cols-[1.2fr_1.1fr_1fr_.55fr_.7fr_.7fr_1fr] gap-3 items-center">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{item.name || "—"}</p>
                            <p className="text-xs text-muted-foreground truncate">{item.email || "—"}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{item.protocol || item.id}</p>
                          </div>

                          <div className="min-w-0">
                            <p className="text-sm truncate">{item.company_name || "—"}</p>
                            <p className="text-xs text-muted-foreground truncate">{item.unit_name || "Todas / não informada"}</p>
                          </div>

                          <div>
                            <Badge variant="outline">{accessRoleLabel(item.requested_role)}</Badge>
                          </div>

                          <div>
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold ${item.source === "ERP" ? "border-sky-500/30 bg-sky-500/10 text-sky-400" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-600"}`}>
                              {item.source === "ERP"
                                ? <Monitor size={10} />
                                : <Smartphone size={10} />}
                              {item.source || "APP"}
                            </span>
                          </div>

                          <div>
                            <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[10px] font-medium ${accessStatusClass(item.status)}`}>
                              {accessStatusLabel(item.status)}
                            </span>
                          </div>

                          <div>
                            {pending ? (
                              <div className={expired ? "text-red-400" : "text-amber-400"}>
                                <p className="text-sm font-semibold">
                                  {expired ? "Expirado" : formatRemaining(item.remaining_seconds)}
                                </p>
                                <p className="text-[10px] text-muted-foreground">até {formatAccessDate(item.expires_at)}</p>
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">{formatAccessDate(item.reviewed_at)}</p>
                            )}
                          </div>

                          <div className="flex lg:justify-end items-center gap-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8"
                              onClick={() => openAccessRequest(item.id)}
                              disabled={accessAction === `view:${item.id}`}
                              data-testid={`access-view-${item.id}`}
                            >
                              {accessAction === `view:${item.id}` ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                            </Button>

                            {pending && !expired && (
                              <>
                                <Button
                                  size="sm"
                                  className="h-8"
                                  onClick={() => approveAccessRequest(item)}
                                  disabled={!!accessAction || !!resendingAccessLink}
                                  data-testid={`access-approve-${item.id}`}
                                >
                                  {approveBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
                                  Aprovar
                                </Button>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-red-400 hover:text-red-400"
                                  onClick={() => {
                                    setRejectingAccessRequest(item);
                                    setRejectionReason("");
                                  }}
                                  disabled={!!accessAction || !!resendingAccessLink}
                                  data-testid={`access-reject-${item.id}`}
                                >
                                  <XCircle size={14} />
                                </Button>
                              </>
                            )}

                            {approved && !registrationCompleted && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={() => resendAccessLink(item)}
                                disabled={!!accessAction || !!resendingAccessLink}
                                title="Gerar novo link e reenviar por e-mail"
                                data-testid={`access-resend-link-${item.id}`}
                              >
                                {resendBusy ? (
                                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                                ) : (
                                  <RefreshCw size={14} className="mr-1.5" />
                                )}
                                {resendBusy ? "Enviando..." : "Reenviar link"}
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="lg:hidden mt-3 pt-3 border-t border-border/50 grid grid-cols-2 gap-2 text-xs">
                          <div><span className="text-muted-foreground">Solicitado em:</span> {formatAccessDate(item.created_at)}</div>
                          <div><span className="text-muted-foreground">Mensagem:</span> {item.message || "—"}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-xs font-medium text-primary">Fluxo administrativo</p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                Aprovar altera a solicitação para <b>pré-cadastro aprovado</b> e inicia o fluxo oficial de
                finalização. O backend deve gerar um token único, enviar ao solicitante o link para finalizar
                o próprio cadastro e manter a solicitação vinculada até a conclusão. A criação definitiva do
                usuário ocorre somente após a finalização do cadastro. Rejeitar exige motivo e registra o
                responsável na auditoria.
              </p>
            </div>
          </SectionCard>

          {selectedAccessRequest && (
            <Card className="p-5 border-border space-y-4" data-testid="access-request-details">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
                    <Eye size={15} className="text-primary" /> Detalhes da solicitação
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">{selectedAccessRequest.protocol || selectedAccessRequest.id}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedAccessRequest(null)} data-testid="access-request-details-close">
                  <X size={15} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  ["Nome", selectedAccessRequest.name],
                  ["E-mail", selectedAccessRequest.email],
                  ["Telefone", selectedAccessRequest.phone],
                  ["Perfil solicitado", accessRoleLabel(selectedAccessRequest.requested_role || selectedAccessRequest.role)],
                  ["Empresa", selectedAccessRequest.company_name],
                  ["Unidade", selectedAccessRequest.unit_name || "Não informada"],
                  ["Solicitado em", formatAccessDate(selectedAccessRequest.created_at)],
                  ["Prazo", formatAccessDate(selectedAccessRequest.expires_at)],
                  ["Revisado em", formatAccessDate(selectedAccessRequest.reviewed_at)],
                  ["Revisado por", selectedAccessRequest.reviewed_by_name || selectedAccessRequest.reviewed_by],
                  ["Finalização do cadastro", selectedAccessRequest.finalization_completed_at || selectedAccessRequest.registration_completed_at ? formatAccessDate(selectedAccessRequest.finalization_completed_at || selectedAccessRequest.registration_completed_at) : "Aguardando solicitante"],
                  ["Cadastro concluído", selectedAccessRequest.finalization_completed_at || selectedAccessRequest.registration_completed_at ? "Sim" : "Não"],
                  ["Link gerado em", selectedAccessRequest.registration_token_created_at ? formatAccessDate(selectedAccessRequest.registration_token_created_at) : "—"],
                  ["Link válido até", selectedAccessRequest.registration_token_expires_at ? formatAccessDate(selectedAccessRequest.registration_token_expires_at) : "—"],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border border-border px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
                    <p className="text-sm mt-1 break-words">{value || "—"}</p>
                  </div>
                ))}
              </div>

              {selectedAccessRequest.status === "approved" &&
                !selectedAccessRequest.registration_completed_at &&
                !selectedAccessRequest.finalization_completed_at && (
                  <div className="flex items-center justify-end rounded-md border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resendAccessLink(selectedAccessRequest)}
                      disabled={
                        !!accessAction ||
                        resendingAccessLink === selectedAccessRequest.id
                      }
                      data-testid="access-request-resend-link"
                    >
                      {resendingAccessLink === selectedAccessRequest.id ? (
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                      ) : (
                        <RefreshCw size={14} className="mr-1.5" />
                      )}
                      {resendingAccessLink === selectedAccessRequest.id
                        ? "Gerando novo link..."
                        : "Gerar novo link e reenviar"}
                    </Button>
                  </div>
                )}

              {selectedAccessRequest.message && (
                <div className="rounded-md border border-border p-3">
                  <p className="text-xs font-semibold">Mensagem do solicitante</p>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{selectedAccessRequest.message}</p>
                </div>
              )}

              {selectedAccessRequest.rejection_reason && (
                <div className="rounded-md border border-red-500/20 bg-red-500/5 p-3">
                  <p className="text-xs font-semibold text-red-400">Motivo da rejeição</p>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{selectedAccessRequest.rejection_reason}</p>
                </div>
              )}
            </Card>
          )}

          {rejectingAccessRequest && (
            <Card className="p-5 border-red-500/30 space-y-4" data-testid="access-request-rejection-dialog">
              <div>
                <h3 className="font-heading font-semibold text-sm flex items-center gap-2 text-red-400">
                  <XCircle size={16} /> Rejeitar solicitação
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Informe o motivo que será registrado na solicitação e utilizado na comunicação ao solicitante.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Motivo da rejeição</Label>
                <Textarea
                  rows={4}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Ex.: vínculo com a empresa/unidade não pôde ser validado..."
                  className="resize-none"
                  data-testid="access-rejection-reason"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRejectingAccessRequest(null);
                    setRejectionReason("");
                  }}
                  disabled={accessAction.startsWith("reject:")}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={rejectAccessRequest}
                  disabled={accessAction.startsWith("reject:")}
                  data-testid="access-rejection-confirm"
                >
                  {accessAction.startsWith("reject:") && <Loader2 size={14} className="mr-2 animate-spin" />}
                  Confirmar rejeição
                </Button>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* ================= IA ================= */}
        <TabsContent value="ia" className="space-y-4 mt-4">
          <SectionCard icon={Sparkles} title="Configurações da Inteligência Artificial" testId="ai-config-card"
            desc="Controle a IA por módulo, retenção de histórico e comportamento. IA por cliente/usuário: aba Por Cliente e Matriz de Permissões."
            onSave={() => saveCommandSection("ai_config")} saving={saving === "command:ai_config"}>
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div><p className="text-sm font-medium">IA habilitada na plataforma</p>
                <p className="text-xs text-muted-foreground">Desligar desabilita todos os recursos de IA no ERP e nos aplicativos</p></div>
              <Switch checked={ai.enabled !== false} onCheckedChange={(v) => setSec("ai_config", { ...ai, enabled: v })} data-testid="ai-enabled-switch" />
            </div>
            <div>
              <Label className="text-xs">IA por módulo</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                {Object.entries(AI_MODULE_LABELS).map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                    <p className="text-sm">{l}</p>
                    <Switch checked={(ai.per_module || {})[k] !== false} onCheckedChange={(v) => setSec("ai_config", { ...ai, per_module: { ...(ai.per_module || {}), [k]: v } })} data-testid={`ai-module-${k}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Retenção do histórico (dias)</Label>
                <Input type="number" min={30} value={ai.retention_days ?? 365} onChange={(e) => setSec("ai_config", { ...ai, retention_days: parseInt(e.target.value || "365") })} data-testid="ai-retention-input" /></div>
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <p className="text-sm">Resumo automático das conversas</p>
                <Switch checked={!!ai.auto_summary} onCheckedChange={(v) => setSec("ai_config", { ...ai, auto_summary: v })} data-testid="ai-summary-switch" />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <p className="text-sm">Organizar conversas por tópicos</p>
                <Switch checked={ai.topics !== false} onCheckedChange={(v) => setSec("ai_config", { ...ai, topics: v })} data-testid="ai-topics-switch" />
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        {/* ================= NOTIFICAÇÕES ================= */}
        <TabsContent value="notificacoes" className="space-y-4 mt-4">
          <SectionCard
            icon={BellRing}
            title="Central de Notificações em Tempo Real"
            testId="notif-config-card"
            desc="Fonte única de configuração das notificações do ASTRAL. Controla tempo real, fallback por polling, aviso no portal, som, voz, notificação do sistema e eventos."
            onSave={() => saveCommandSection("notifications_config")}
            saving={saving === "command:notifications_config"}
          >
            <div className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-start gap-3">
                <BellRing size={20} className="text-primary mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-sm">Sistema de notificações</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Controle global. Quando desligado, o backend não cria novas notificações.
                  </p>
                </div>
                <Switch
                  checked={notif.enabled !== false}
                  onCheckedChange={(enabled) => setSec("notifications_config", { ...notif, enabled })}
                  data-testid="platform-notifications-enabled"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Radio size={16} className="text-primary" />
                  <p className="font-medium text-sm">Tempo real</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">SSE</p>
                      <p className="text-xs text-muted-foreground">Canal principal, praticamente imediato.</p>
                    </div>
                    <Switch
                      checked={notif.realtime !== false}
                      disabled={notif.enabled === false}
                      onCheckedChange={(realtime) => setSec("notifications_config", { ...notif, realtime })}
                      data-testid="notif-realtime-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">Polling de segurança</p>
                      <p className="text-xs text-muted-foreground">Fallback quando o SSE falhar.</p>
                    </div>
                    <Switch
                      checked={notif.polling !== false}
                      disabled={notif.enabled === false}
                      onCheckedChange={(polling) => setSec("notifications_config", { ...notif, polling })}
                      data-testid="notif-polling-switch"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Intervalo do polling</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={3}
                        max={60}
                        value={Math.round(Number(notif.polling_interval_ms ?? 5000) / 1000)}
                        disabled={notif.enabled === false || notif.polling === false}
                        onChange={(e) => setSec("notifications_config", {
                          ...notif,
                          polling_interval_ms: Math.max(3000, Number(e.target.value || 5) * 1000),
                        })}
                        className="w-24"
                        data-testid="notif-polling-interval"
                      />
                      <span className="text-xs text-muted-foreground">segundos</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Monitor size={16} className="text-primary" />
                  <p className="font-medium text-sm">Alertas visuais e do computador</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">Popup dentro do portal</p>
                      <p className="text-xs text-muted-foreground">Aviso visual no ASTRAL.</p>
                    </div>
                    <Switch
                      checked={notif.toast !== false}
                      disabled={notif.enabled === false}
                      onCheckedChange={(toastEnabled) => setSec("notifications_config", { ...notif, toast: toastEnabled })}
                      data-testid="notif-toast-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">Notificação do computador</p>
                      <p className="text-xs text-muted-foreground">Notificação nativa do navegador/sistema.</p>
                    </div>
                    <Switch
                      checked={notif.browser !== false}
                      disabled={notif.enabled === false}
                      onCheckedChange={(browser) => setSec("notifications_config", { ...notif, browser })}
                      data-testid="notif-browser-switch"
                    />
                  </div>

                  <div className="rounded-md border border-border bg-muted/20 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">Permissão do navegador</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Autorize neste computador para receber alertas mesmo fora da aba ativa.
                        </p>
                      </div>
                      <span
                        className={`text-[11px] px-2 py-1 rounded-full border ${
                          typeof window === "undefined" || !("Notification" in window)
                            ? "border-border text-muted-foreground"
                            : Notification.permission === "granted"
                              ? "border-emerald-500/40 text-emerald-400"
                              : Notification.permission === "denied"
                                ? "border-red-500/40 text-red-400"
                                : "border-amber-500/40 text-amber-400"
                        }`}
                        data-testid="notif-browser-permission"
                      >
                        {typeof window === "undefined" || !("Notification" in window)
                          ? "Não suportado"
                          : Notification.permission === "granted"
                            ? "Autorizada"
                            : Notification.permission === "denied"
                              ? "Bloqueada"
                              : "Não autorizada"}
                      </span>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      disabled={
                        typeof window === "undefined" ||
                        !("Notification" in window) ||
                        Notification.permission === "granted"
                      }
                      onClick={async () => {
                        if (typeof window === "undefined" || !("Notification" in window)) {
                          toast.error("Este navegador não suporta notificações do sistema.");
                          return;
                        }
                        try {
                          const result = await Notification.requestPermission();
                          if (result === "granted") {
                            toast.success("Notificações do computador autorizadas.");
                            new Notification("ASTRAL", {
                              body: "Notificações do sistema autorizadas com sucesso.",
                            });
                          } else if (result === "denied") {
                            toast.error("O navegador bloqueou as notificações. Libere a permissão nas configurações do navegador.");
                          } else {
                            toast.warning("A permissão não foi concedida.");
                          }
                        } catch {
                          toast.error("Não foi possível solicitar a permissão do navegador.");
                        }
                      }}
                      data-testid="notif-browser-request"
                    >
                      <Monitor size={14} className="mr-2" />
                      Autorizar notificações do sistema
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-3">
                  {notif.sound !== false ? <Volume2 size={16} className="text-primary" /> : <VolumeX size={16} className="text-muted-foreground" />}
                  <p className="font-medium text-sm">Som</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm">Som de alerta</p>
                    <Switch
                      checked={notif.sound !== false}
                      disabled={notif.enabled === false}
                      onCheckedChange={(sound) => setSec("notifications_config", { ...notif, sound })}
                      data-testid="notif-sound-switch"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Duração do aviso visual (ms)</Label>
                    <Input
                      type="number"
                      min={1000}
                      max={30000}
                      step={500}
                      value={notif.toast_duration ?? 7000}
                      disabled={notif.enabled === false || notif.toast === false}
                      onChange={(e) => setSec("notifications_config", {
                        ...notif,
                        toast_duration: Math.max(1000, Math.min(30000, Number(e.target.value || 7000))),
                      })}
                      data-testid="notif-toast-duration"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Volume</Label>
                    <Input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={notif.sound_volume ?? 0.55}
                      disabled={notif.enabled === false || notif.sound === false}
                      onChange={(e) => setSec("notifications_config", { ...notif, sound_volume: Number(e.target.value) })}
                      data-testid="notif-sound-volume"
                    />
                    <p className="text-xs text-muted-foreground">{Math.round(Number(notif.sound_volume ?? 0.55) * 100)}%</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const AudioContextClass = window.AudioContext || window?.webkitAudioContext;
                      if (!AudioContextClass) {
                        toast.error("Áudio não suportado neste navegador.");
                        return;
                      }
                      try {
                        const audio = new AudioContextClass();
                        const oscillator = audio.createOscillator();
                        const gain = audio.createGain();
                        const volume = Math.min(1, Math.max(0, Number(notif.sound_volume ?? 0.55)));
                        oscillator.type = "sine";
                        oscillator.frequency.setValueAtTime(880, audio.currentTime);
                        oscillator.frequency.exponentialRampToValueAtTime(660, audio.currentTime + 0.12);
                        gain.gain.setValueAtTime(0, audio.currentTime);
                        gain.gain.linearRampToValueAtTime(volume, audio.currentTime + 0.01);
                        gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.2);
                        oscillator.connect(gain);
                        gain.connect(audio.destination);
                        oscillator.start();
                        oscillator.stop(audio.currentTime + 0.2);
                        window.setTimeout(() => audio.close().catch(() => {}), 400);
                      } catch {
                        toast.error("Não foi possível testar o som.");
                      }
                    }}
                    data-testid="notif-test-sound"
                  >
                    <Volume2 size={14} className="mr-2" /> Testar som
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mic2 size={16} className="text-primary" />
                  <p className="font-medium text-sm">Voz</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm">Leitura por voz</p>
                    <Switch
                      checked={notif.voice !== false && notif.voice_enabled !== false}
                      disabled={notif.enabled === false}
                      onCheckedChange={(voice) => setSec("notifications_config", { ...notif, voice, voice_enabled: voice })}
                      data-testid="notif-voice-switch"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Idioma da voz</Label>
                    <Input
                      value={notif.voice_config?.lang ?? "pt-BR"}
                      disabled={notif.enabled === false || notif.voice === false}
                      onChange={(e) => setSec("notifications_config", {
                        ...notif,
                        voice_config: { ...(notif.voice_config || {}), lang: e.target.value || "pt-BR" },
                      })}
                      data-testid="notif-voice-lang"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Velocidade</Label>
                      <Input
                        type="number"
                        min={0.5}
                        max={2}
                        step={0.1}
                        value={notif.voice_config?.rate ?? 1}
                        disabled={notif.enabled === false || notif.voice === false}
                        onChange={(e) => setSec("notifications_config", {
                          ...notif,
                          voice_config: { ...(notif.voice_config || {}), rate: Number(e.target.value || 1) },
                        })}
                        data-testid="notif-voice-rate"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Tom</Label>
                      <Input
                        type="number"
                        min={0.5}
                        max={2}
                        step={0.1}
                        value={notif.voice_config?.pitch ?? 1}
                        disabled={notif.enabled === false || notif.voice === false}
                        onChange={(e) => setSec("notifications_config", {
                          ...notif,
                          voice_config: { ...(notif.voice_config || {}), pitch: Number(e.target.value || 1) },
                        })}
                        data-testid="notif-voice-pitch"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Volume da voz</Label>
                    <Input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={notif.voice_config?.volume ?? 1}
                      disabled={notif.enabled === false || notif.voice === false}
                      onChange={(e) => setSec("notifications_config", {
                        ...notif,
                        voice_config: { ...(notif.voice_config || {}), volume: Number(e.target.value) },
                      })}
                      data-testid="notif-voice-volume"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!("speechSynthesis" in window)) {
                        toast.error("Este navegador não possui suporte a voz.");
                        return;
                      }
                      window.speechSynthesis.cancel();
                      const utterance = new SpeechSynthesisUtterance(
                        "Atenção. Nova ocorrência HIG. Prioridade crítica. Lançada por João."
                      );
                      utterance.lang = notif.voice_config?.lang || "pt-BR";
                      utterance.rate = Number(notif.voice_config?.rate ?? 1);
                      utterance.pitch = Number(notif.voice_config?.pitch ?? 1);
                      utterance.volume = Number(notif.voice_config?.volume ?? 1);
                      window.speechSynthesis.speak(utterance);
                    }}
                    data-testid="notif-test-voice"
                  >
                    <Mic2 size={14} className="mr-2" /> Testar voz
                  </Button>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-3" data-testid="notif-scope-rules">
              <div>
                <p className="text-sm font-semibold">Escopo das notificações</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  O backend continua responsável pelos destinatários e pelo isolamento por cliente, unidade, área e subárea.
                </p>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                <div>
                  <p className="text-sm">Usuário sem vínculo recebe de todos os clientes</p>
                  <p className="text-xs text-muted-foreground">Desligado por padrão para evitar vazamento entre clientes.</p>
                </div>
                <Switch
                  checked={!!scopeRules.unscoped_receives_all}
                  onCheckedChange={(v) => setScopeRules({ unscoped_receives_all: v })}
                  data-testid="notif-unscoped-switch"
                />
              </div>
              <div>
                <Label className="text-xs">Perfis com visão global</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {buildRoleOptions(cfg.roles || []).map((item) => (
                    <label key={item.value} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <Checkbox
                        className="h-3.5 w-3.5"
                        checked={(scopeRules.global_roles || []).some((role) => canonicalRoleKey(role) === item.canonical)}
                        onCheckedChange={(v) => setScopeRules({
                          global_roles: v
                            ? Array.from(new Set([...(scopeRules.global_roles || []), item.value]))
                            : (scopeRules.global_roles || []).filter((x) => canonicalRoleKey(x) !== item.canonical),
                        })}
                        data-testid={`notif-global-role-${item.value}`}
                      />
                      {ROLE_LABELS?.[item.value] || ROLE_LABELS?.[item.canonical] || item.canonical}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-4" data-testid="notif-priority-config">
              <div>
                <p className="text-sm font-semibold">Configuração por prioridade</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Defina, de forma centralizada, o comportamento visual, sonoro e de voz para cada nível.
                  O backend deve usar esta configuração ao resolver e publicar as notificações.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ["critical", "Crítica", "#dc2626", true],
                  ["high", "Alta", "#ea580c", true],
                  ["medium", "Média", "#ca8a04", true],
                  ["low", "Baixa", "#2563eb", false],
                ].map(([key, label, fallback, repeatDefault]) => {
                  const priority = notif.priorities?.[key] || {};
                  const setPriority = (patch) =>
                    setSec("notifications_config", {
                      ...notif,
                      priorities: {
                        ...(notif.priorities || {}),
                        [key]: { ...priority, ...patch },
                      },
                    });

                  return (
                    <div key={key} className="rounded-lg border border-border p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="h-3 w-3 rounded-full border border-white/20"
                            style={{ backgroundColor: priority.color || fallback }}
                          />
                          <p className="text-sm font-semibold">{label}</p>
                        </div>
                        <Switch
                          checked={priority.enabled !== false}
                          disabled={notif.enabled === false}
                          onCheckedChange={(enabled) => setPriority({ enabled })}
                          data-testid={`notif-priority-${key}-enabled`}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Cor</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={priority.color || fallback}
                              disabled={notif.enabled === false || priority.enabled === false}
                              onChange={(e) => setPriority({ color: e.target.value })}
                              className="h-9 w-12 rounded-md border border-border bg-transparent cursor-pointer"
                              data-testid={`notif-priority-${key}-color`}
                            />
                            <Input
                              value={priority.color || fallback}
                              disabled={notif.enabled === false || priority.enabled === false}
                              onChange={(e) => setPriority({ color: e.target.value })}
                              className="h-9 font-mono text-xs"
                              data-testid={`notif-priority-${key}-color-input`}
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs">Repetição</Label>
                          <Input
                            type="number"
                            min={1}
                            max={20}
                            value={priority.repeat ?? (repeatDefault ? 2 : 1)}
                            disabled={notif.enabled === false || priority.enabled === false}
                            onChange={(e) =>
                              setPriority({
                                repeat: Math.max(1, Math.min(20, Number(e.target.value || 1))),
                              })
                            }
                            data-testid={`notif-priority-${key}-repeat`}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs">Duração do popup (ms)</Label>
                          <Input
                            type="number"
                            min={1000}
                            max={60000}
                            step={500}
                            value={priority.duration_ms ?? notif.toast_duration ?? 7000}
                            disabled={notif.enabled === false || priority.enabled === false}
                            onChange={(e) =>
                              setPriority({
                                duration_ms: Math.max(
                                  1000,
                                  Math.min(60000, Number(e.target.value || 7000)),
                                ),
                              })
                            }
                            data-testid={`notif-priority-${key}-duration`}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs">Volume do som</Label>
                          <Input
                            type="range"
                            min={0}
                            max={1}
                            step={0.05}
                            value={priority.sound_volume ?? notif.sound_volume ?? 0.55}
                            disabled={
                              notif.enabled === false ||
                              priority.enabled === false ||
                              notif.sound === false
                            }
                            onChange={(e) =>
                              setPriority({ sound_volume: Number(e.target.value) })
                            }
                            data-testid={`notif-priority-${key}-sound-volume`}
                          />
                          <p className="text-[11px] text-muted-foreground">
                            {Math.round(Number(priority.sound_volume ?? notif.sound_volume ?? 0.55) * 100)}%
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                          <span className="text-xs">Som</span>
                          <Switch
                            checked={priority.sound !== false}
                            disabled={
                              notif.enabled === false ||
                              priority.enabled === false ||
                              notif.sound === false
                            }
                            onCheckedChange={(sound) => setPriority({ sound })}
                            data-testid={`notif-priority-${key}-sound`}
                          />
                        </div>

                        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                          <span className="text-xs">Voz</span>
                          <Switch
                            checked={priority.voice !== false}
                            disabled={
                              notif.enabled === false ||
                              priority.enabled === false ||
                              notif.voice === false ||
                              notif.voice_enabled === false
                            }
                            onCheckedChange={(voice) => setPriority({ voice })}
                            data-testid={`notif-priority-${key}-voice`}
                          />
                        </div>

                        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                          <span className="text-xs">Popup</span>
                          <Switch
                            checked={priority.toast !== false}
                            disabled={
                              notif.enabled === false ||
                              priority.enabled === false ||
                              notif.toast === false
                            }
                            onCheckedChange={(toastEnabled) => setPriority({ toast: toastEnabled })}
                            data-testid={`notif-priority-${key}-toast`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2.5">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  <b className="text-primary">Importante:</b> esta tela grava os parâmetros em
                  <code className="mx-1">notifications_config.priorities</code>. A aplicação cliente
                  e o resolvedor de notificações precisam consumir esses valores; a simples existência
                  dos controles não altera o comportamento do runtime.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-muted/20 flex items-center gap-2">
                <RefreshCw size={15} className="text-primary" />
                <div>
                  <p className="text-sm font-medium">Eventos notificáveis</p>
                  <p className="text-[11px] text-muted-foreground">
                    Estes controles alimentam diretamente <code>notifications_config.events</code>.
                  </p>
                </div>
              </div>
              <div className="divide-y divide-border/50">
                {[
                  ["new_occurrence", "Nova ocorrência", "Nova ocorrência registrada."],
                  ["occurrence_updated", "Ocorrência atualizada", "Alteração relevante na ocorrência."],
                  ["occurrence_assigned", "Ocorrência atribuída", "Ocorrência atribuída a responsável."],
                  ["sla_escalated", "SLA vencido / escalonado", "Prazo vencido ou ocorrência escalonada."],
                  ["sla_escalation_level2", "Escalonamento SLA — nível 2", "Escalonamento para nível 2."],
                  ["sla_escalation_level3", "Escalonamento SLA — nível 3", "Escalonamento para nível 3."],
                  ["completion_requested", "Conclusão solicitada", "Conclusão enviada para aprovação."],
                  ["completion_approved", "Conclusão aprovada", "Conclusão aprovada."],
                  ["completion_rejected", "Conclusão rejeitada", "Conclusão rejeitada e requer nova ação."],
                  ["action_plan", "Plano de ação", "Plano de ação gerado ou alterado."],
                  ["recurrence_detected", "Reincidência detectada", "Reincidência identificada."],
                ].map(([event, label, desc]) => {
                  const current = notif.events?.[event];
                  const enabled = typeof current === "boolean" ? current : current?.enabled !== false;
                  return (
                    <div key={event} className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`notif-event-${event}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{label}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{desc}</p>
                      </div>
                      <Switch
                        checked={enabled}
                        disabled={notif.enabled === false}
                        onCheckedChange={(value) => setSec("notifications_config", {
                          ...notif,
                          events: { ...(notif.events || {}), [event]: value },
                        })}
                        data-testid={`notif-${event}-switch`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
              <p className="text-xs font-medium text-primary">Fonte única da verdade</p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                A Central de Notificações saiu do Administração do Sistema e agora fica integralmente na Administração da Plataforma.
                O backend continua responsável por escopo e destinatários.
              </p>
            </div>
                      <SectionCard icon={Radio} title="Matriz de Distribuição e Escalonamento" testId="notification-routing-matrix" desc="O Admin define o destino central de cada evento. A configuração é executada pelo backend: individual, global, por perfil, por usuário, por cliente ou por unidade.">
              <div className="space-y-3">
                {Object.entries(notif.routing || {}).map(([event, rule]) => (
                  <div key={event} className="rounded-lg border border-border p-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                      <div className="md:col-span-2"><p className="text-sm font-medium">{EVENT_LABELS[event] || event}</p><p className="text-[10px] text-muted-foreground mt-1">{event}</p></div>
                      <div className="space-y-1.5"><Label className="text-xs">Destino</Label><Select value={rule?.mode || "direct"} onValueChange={(mode) => setSec("notifications_config", { ...notif, routing: { ...(notif.routing || {}), [event]: { ...(rule || {}), mode } } })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{[["direct","Destinatário original"],["global","Todos os usuários"],["role","Por perfil"],["users","Usuários selecionados"],["client","Por cliente"],["unit","Por unidade"]].map(([value,label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1.5"><Label className="text-xs">Perfis</Label><Input value={(rule?.roles || []).join(", ")} placeholder="admin, supervisor, coordenador, rt" onChange={(e) => setSec("notifications_config", { ...notif, routing: { ...(notif.routing || {}), [event]: { ...(rule || {}), roles: e.target.value.split(",").map((v) => v.trim().toLowerCase()).filter(Boolean) } } })} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
</SectionCard>
        </TabsContent>
{/* ================= RELATÓRIOS ================= */}
<TabsContent value="relatorios" className="space-y-4 mt-4">

  <SectionCard
    icon={FileText}
    title="Configuração dos Relatórios PDF"
    testId="reports-config-card"
    desc="Controle completamente o layout, papel timbrado, margens, tipografia, cores, cabeçalho, rodapé, fotos, assinaturas e demais elementos dos relatórios PDF."
    onSave={() => saveCommandSection("reports_config")}
    saving={saving === "command:reports_config"}
  >
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="report-category-matrix"><div><p className="text-sm font-semibold">Categorias de PDF</p><p className="text-xs text-muted-foreground mt-1">Cada categoria herda a identidade global e pode sobrescrever layout, seções, página, cabeçalho, rodapé, tipografia, cores, fotos, assinaturas e QR.</p></div><div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">{Object.entries(rep.categories || {}).map(([category, value]) => <div key={category} className="rounded-md border border-border bg-card p-3"><div className="flex items-center justify-between gap-2"><div><p className="text-sm font-medium">{value?.label || category}</p><p className="text-[10px] text-muted-foreground">Template: {value?.template || category}</p></div><Switch checked={value?.enabled !== false} onCheckedChange={(v) => setSec("reports_config", { ...rep, categories: { ...(rep.categories || {}), [category]: { ...(value || {}), enabled: v } } })} /></div></div>)}</div></div>

    <div className="rounded-xl border border-border p-4 space-y-4" data-testid="report-category-editor">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="space-y-1.5"><Label className="text-xs">Categoria em edição</Label><Select value={reportCategory} onValueChange={setReportCategory}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(rep.categories || {}).map(([key, value]) => <SelectItem key={key} value={key}>{value?.label || key}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Modelo / template</Label><Input value={rep.categories?.[reportCategory]?.template || reportCategory} onChange={(e) => setSec("reports_config", { ...rep, categories: { ...(rep.categories || {}), [reportCategory]: { ...(rep.categories?.[reportCategory] || {}), template: e.target.value } } })} /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Papel</Label><Select value={rep.categories?.[reportCategory]?.page?.size || rep.page?.size || "A4"} onValueChange={(v) => setSec("reports_config", { ...rep, categories: { ...(rep.categories || {}), [reportCategory]: { ...(rep.categories?.[reportCategory] || {}), page: { ...(rep.categories?.[reportCategory]?.page || {}), size: v } } } })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["A4","A3","A5","Carta","Ofício"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Orientação</Label><Select value={rep.categories?.[reportCategory]?.page?.orientation || rep.page?.orientation || "portrait"} onValueChange={(v) => setSec("reports_config", { ...rep, categories: { ...(rep.categories || {}), [reportCategory]: { ...(rep.categories?.[reportCategory] || {}), page: { ...(rep.categories?.[reportCategory]?.page || {}), orientation: v } } } })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="portrait">Retrato</SelectItem><SelectItem value="landscape">Paisagem</SelectItem></SelectContent></Select></div>
        <div className="space-y-1.5"><Label className="text-xs">Fotos por seção</Label><Input type="number" min="1" max="50" value={rep.categories?.[reportCategory]?.photos?.max_per_section ?? rep.photos?.max_per_section ?? 6} onChange={(e) => setSec("reports_config", { ...rep, categories: { ...(rep.categories || {}), [reportCategory]: { ...(rep.categories?.[reportCategory] || {}), photos: { ...(rep.categories?.[reportCategory]?.photos || {}), max_per_section: Number(e.target.value) } } } })} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Assinaturas</Label><Input type="number" min="1" max="8" value={rep.categories?.[reportCategory]?.signatures?.count ?? rep.signatures?.count ?? 3} onChange={(e) => setSec("reports_config", { ...rep, categories: { ...(rep.categories || {}), [reportCategory]: { ...(rep.categories?.[reportCategory] || {}), signatures: { ...(rep.categories?.[reportCategory]?.signatures || {}), count: Number(e.target.value) } } } })} /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[['show_period','Período'],['show_company','Empresa'],['show_unit','Unidade']].map(([key,label]) => <div key={key} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5"><span className="text-xs">{label}</span><Switch checked={rep.categories?.[reportCategory]?.header?.[key] !== false} onCheckedChange={(v) => setSec("reports_config", { ...rep, categories: { ...(rep.categories || {}), [reportCategory]: { ...(rep.categories?.[reportCategory] || {}), header: { ...(rep.categories?.[reportCategory]?.header || {}), [key]: v } } } })} /></div>)}
      </div>
    </div>
    {/* ========================================================= */}
    {/* PÁGINA E LAYOUT */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Página e Layout
        </h4>

        <p className="text-xs text-muted-foreground mt-1">
          Defina tamanho da página, orientação e margens utilizadas em todos
          os relatórios PDF.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        <div className="space-y-1.5">
          <Label className="text-xs">Tamanho da página</Label>

          <Select
            value={rep.page?.size || "A4"}
            onValueChange={(v) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  size: v,
                },
              })
            }
          >
            <SelectTrigger data-testid="report-page-size">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="A4">A4</SelectItem>
              <SelectItem value="LETTER">Carta / Letter</SelectItem>
              <SelectItem value="A3">A3</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Orientação</Label>

          <Select
            value={rep.page?.orientation || "portrait"}
            onValueChange={(v) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  orientation: v,
                },
              })
            }
          >
            <SelectTrigger data-testid="report-page-orientation">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="portrait">
                Retrato
              </SelectItem>

              <SelectItem value="landscape">
                Paisagem
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Margem esquerda (mm)
          </Label>

          <Input
            type="number"
            min="0"
            max="50"
            value={rep.page?.margin_left_mm ?? 15}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  margin_left_mm: Number(e.target.value),
                },
              })
            }
            data-testid="report-margin-left"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Margem direita (mm)
          </Label>

          <Input
            type="number"
            min="0"
            max="50"
            value={rep.page?.margin_right_mm ?? 15}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  margin_right_mm: Number(e.target.value),
                },
              })
            }
            data-testid="report-margin-right"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Margem superior (mm)
          </Label>

          <Input
            type="number"
            min="0"
            max="80"
            value={rep.page?.margin_top_mm ?? 12}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  margin_top_mm: Number(e.target.value),
                },
              })
            }
            data-testid="report-margin-top"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Margem inferior (mm)
          </Label>

          <Input
            type="number"
            min="0"
            max="50"
            value={rep.page?.margin_bottom_mm ?? 18}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  margin_bottom_mm: Number(e.target.value),
                },
              })
            }
            data-testid="report-margin-bottom"
          />
        </div>

      </div>
    </div>


    {/* ========================================================= */}
    {/* PAPEL TIMBRADO */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Papel Timbrado
        </h4>

        <p className="text-xs text-muted-foreground mt-1">
          Configure o fundo institucional sem permitir que o conteúdo do
          relatório fique sobreposto ao logotipo ou às informações do timbrado.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            Usar papel timbrado
          </p>

          <Switch
            checked={rep.branding?.use_letterhead !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                branding: {
                  ...(rep.branding || {}),
                  use_letterhead: v,
                },
              })
            }
            data-testid="report-use-letterhead"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Altura reservada ao timbrado (mm)
          </Label>

          <Input
            type="number"
            min="0"
            max="120"
            value={rep.page?.letterhead_reserved_mm ?? 42}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  letterhead_reserved_mm: Number(e.target.value),
                },
              })
            }
            data-testid="report-letterhead-reserved"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Espaço após o timbrado (mm)
          </Label>

          <Input
            type="number"
            min="0"
            max="30"
            value={rep.page?.letterhead_gap_mm ?? 5}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                page: {
                  ...(rep.page || {}),
                  letterhead_gap_mm: Number(e.target.value),
                },
              })
            }
            data-testid="report-letterhead-gap"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Ajuste do fundo
          </Label>

          <Select
            value={rep.branding?.letterhead_fit || "contain"}
            onValueChange={(v) =>
              setSec("reports_config", {
                ...rep,
                branding: {
                  ...(rep.branding || {}),
                  letterhead_fit: v,
                },
              })
            }
          >
            <SelectTrigger data-testid="report-letterhead-fit">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="contain">
                Manter proporção
              </SelectItem>

              <SelectItem value="cover">
                Preencher área
              </SelectItem>

              <SelectItem value="stretch">
                Ajustar à área
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

      </div>

      <div className="rounded-md border border-dashed border-border p-4">
        <ImageField
          label="Papel timbrado / fundo do PDF"
          value={rep.branding?.letterhead_image || ""}
          onChange={(v) =>
            setSec("reports_config", {
              ...rep,
              branding: {
                ...(rep.branding || {}),
                letterhead_image: v,
              },
            })
          }
          testId="report-letterhead-upload"
        />

        <p className="text-xs text-muted-foreground mt-2">
          Recomenda-se utilizar uma imagem preparada no tamanho proporcional
          à página. O sistema deverá reservar automaticamente a área definida
          acima para impedir sobreposição do conteúdo.
        </p>
      </div>

    </div>


    {/* ========================================================= */}
    {/* IDENTIDADE VISUAL */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Identidade Visual
        </h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        {[
          ["logo", "Exibir logotipo"],
          ["company_name", "Exibir nome da empresa"],
          ["system_name", "Exibir nome do sistema"],
        ].map(([key, label]) => (
          <div
            key={key}
            className="flex items-center justify-between rounded-md border border-border px-3 py-2.5"
          >
            <p className="text-sm">
              {label}
            </p>

            <Switch
              checked={rep.branding?.[key] !== false}
              onCheckedChange={(v) =>
                setSec("reports_config", {
                  ...rep,
                  branding: {
                    ...(rep.branding || {}),
                    [key]: v,
                  },
                })
              }
              data-testid={`report-branding-${key}`}
            />
          </div>
        ))}

        <div className="space-y-1.5">
          <Label className="text-xs">
            Posição do logotipo
          </Label>

          <Select
            value={rep.branding?.logo_position || "left"}
            onValueChange={(v) =>
              setSec("reports_config", {
                ...rep,
                branding: {
                  ...(rep.branding || {}),
                  logo_position: v,
                },
              })
            }
          >
            <SelectTrigger data-testid="report-logo-position">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="left">
                Esquerda
              </SelectItem>

              <SelectItem value="center">
                Centro
              </SelectItem>

              <SelectItem value="right">
                Direita
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

      </div>

    </div>


    {/* ========================================================= */}
    {/* CABEÇALHO */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Cabeçalho
        </h4>

        <p className="text-xs text-muted-foreground mt-1">
          Controle os elementos institucionais exibidos antes do conteúdo.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        {[
          ["enabled", "Exibir cabeçalho"],
          ["show_logo", "Exibir logotipo"],
          ["show_company_name", "Exibir empresa"],
          ["show_system_name", "Exibir sistema"],
          ["show_report_title", "Exibir título"],
          ["show_date", "Exibir data"],
          ["show_protocol", "Exibir protocolo"],
          ["line", "Exibir linha"],
        ].map(([key, label]) => (
          <div
            key={key}
            className="flex items-center justify-between rounded-md border border-border px-3 py-2.5"
          >
            <p className="text-sm">
              {label}
            </p>

            <Switch
              checked={rep.header?.[key] !== false}
              onCheckedChange={(v) =>
                setSec("reports_config", {
                  ...rep,
                  header: {
                    ...(rep.header || {}),
                    [key]: v,
                  },
                })
              }
              data-testid={`report-header-${key}`}
            />
          </div>
        ))}

      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          Texto adicional do cabeçalho
        </Label>

        <Input
          value={rep.header_text || ""}
          onChange={(e) =>
            setSec("reports_config", {
              ...rep,
              header_text: e.target.value,
            })
          }
          placeholder="Texto exibido abaixo do cabeçalho"
          data-testid="report-header-input"
        />
      </div>

    </div>


    {/* ========================================================= */}
    {/* TIPOGRAFIA */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Tipografia
        </h4>

        <p className="text-xs text-muted-foreground mt-1">
          Ajuste o tamanho dos textos sem precisar alterar o código do PDF.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        <div className="space-y-1.5">
          <Label className="text-xs">
            Escala geral
          </Label>

          <Input
            type="number"
            min="0.7"
            max="1.4"
            step="0.05"
            value={rep.typography?.scale ?? 0.95}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  scale: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-scale"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Título (pt)
          </Label>

          <Input
            type="number"
            min="8"
            max="30"
            value={rep.typography?.title_pt ?? 14}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  title_pt: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-title"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Subtítulo (pt)
          </Label>

          <Input
            type="number"
            min="6"
            max="18"
            value={rep.typography?.subtitle_pt ?? 8}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  subtitle_pt: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-subtitle"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Texto normal (pt)
          </Label>

          <Input
            type="number"
            min="6"
            max="16"
            value={rep.typography?.body_pt ?? 8.5}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  body_pt: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-body"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Texto pequeno (pt)
          </Label>

          <Input
            type="number"
            min="5"
            max="14"
            value={rep.typography?.small_pt ?? 7.5}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  small_pt: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-small"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Título de seção (pt)
          </Label>

          <Input
            type="number"
            min="6"
            max="18"
            value={rep.typography?.section_pt ?? 9.5}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  section_pt: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-section"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Texto de tabela (pt)
          </Label>

          <Input
            type="number"
            min="5"
            max="14"
            value={rep.typography?.table_pt ?? 7.5}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  table_pt: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-table"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Número KPI (pt)
          </Label>

          <Input
            type="number"
            min="8"
            max="24"
            value={rep.typography?.kpi_pt ?? 13}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                typography: {
                  ...(rep.typography || {}),
                  kpi_pt: Number(e.target.value),
                },
              })
            }
            data-testid="report-font-kpi"
          />
        </div>

      </div>

    </div>


    {/* ========================================================= */}
    {/* CORES */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Cores do Relatório
        </h4>

        <p className="text-xs text-muted-foreground mt-1">
          Todas as principais cores utilizadas nos PDFs podem ser alteradas
          diretamente aqui.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        {[
          ["primary", "Cor primária", "#1d4ed8"],
          ["secondary", "Cor secundária", "#0f172a"],
          ["text", "Texto principal", "#0f172a"],
          ["muted", "Texto secundário", "#64748b"],
          ["section", "Fundo das seções", "#1d4ed8"],
          ["section_text", "Texto das seções", "#ffffff"],
          ["table_header", "Cabeçalho das tabelas", "#0f172a"],
          ["background", "Fundo do relatório", "#ffffff"],
          ["light_background", "Fundo alternativo", "#f1f5f9"],
          ["border", "Bordas", "#e2e8f0"],
          ["accent", "Destaque", "#1d4ed8"],
          ["footer", "Texto do rodapé", "#64748b"],
          ["chart", "Gráficos", "#1d4ed8"],
          ["kpi", "KPIs", "#1d4ed8"],
        ].map(([key, label, fallback]) => (
          <div
            key={key}
            className="space-y-1.5"
          >
            <Label className="text-xs">
              {label}
            </Label>

            <div className="flex items-center gap-2">

              <input
                type="color"
                value={
                  rep.colors?.[key]
                  || fallback
                }
                onChange={(e) =>
                  setSec("reports_config", {
                    ...rep,
                    colors: {
                      ...(rep.colors || {}),
                      [key]: e.target.value,
                    },
                  })
                }
                className="h-9 w-12 rounded-md border border-border bg-transparent cursor-pointer"
                data-testid={`report-color-${key}`}
              />

              <Input
                value={
                  rep.colors?.[key]
                  || fallback
                }
                onChange={(e) =>
                  setSec("reports_config", {
                    ...rep,
                    colors: {
                      ...(rep.colors || {}),
                      [key]: e.target.value,
                    },
                  })
                }
                className="h-9 font-mono text-xs"
                data-testid={`report-color-input-${key}`}
              />

            </div>
          </div>
        ))}

      </div>

    </div>


    {/* ========================================================= */}
    {/* RODAPÉ */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Rodapé
        </h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        {[
          ["enabled", "Exibir rodapé"],
          ["show_generated_by", "Mostrar gerado por"],
          ["show_generated_at", "Mostrar data/hora"],
          ["show_page_number", "Mostrar número da página"],
          ["show_company", "Mostrar empresa"],
        ].map(([key, label]) => (
          <div
            key={key}
            className="flex items-center justify-between rounded-md border border-border px-3 py-2.5"
          >
            <p className="text-sm">
              {label}
            </p>

            <Switch
              checked={rep.footer?.[key] !== false}
              onCheckedChange={(v) =>
                setSec("reports_config", {
                  ...rep,
                  footer: {
                    ...(rep.footer || {}),
                    [key]: v,
                  },
                })
              }
              data-testid={`report-footer-${key}`}
            />
          </div>
        ))}

      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          Texto personalizado do rodapé
        </Label>

        <Input
          value={rep.footer?.custom_text || ""}
          onChange={(e) =>
            setSec("reports_config", {
              ...rep,
              footer: {
                ...(rep.footer || {}),
                custom_text: e.target.value,
              },
            })
          }
          placeholder="Opcional"
          data-testid="report-footer-input"
        />
      </div>

    </div>


    {/* ========================================================= */}
    {/* FOTOS */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Fotos e Evidências
        </h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            Exibir fotos/evidências
          </p>

          <Switch
            checked={rep.photos?.enabled !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                photos: {
                  ...(rep.photos || {}),
                  enabled: v,
                },
              })
            }
            data-testid="report-show-photos"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Máximo de fotos por seção
          </Label>

          <Input
            type="number"
            min="1"
            max="30"
            value={rep.photos?.max_per_section ?? 6}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                photos: {
                  ...(rep.photos || {}),
                  max_per_section: Number(e.target.value),
                },
              })
            }
            data-testid="report-photo-max"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Colunas de fotos
          </Label>

          <Input
            type="number"
            min="1"
            max="5"
            value={rep.photos?.columns ?? 3}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                photos: {
                  ...(rep.photos || {}),
                  columns: Number(e.target.value),
                },
              })
            }
            data-testid="report-photo-columns"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Altura máxima da foto (mm)
          </Label>

          <Input
            type="number"
            min="10"
            max="100"
            value={rep.photos?.max_height_mm ?? 42}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                photos: {
                  ...(rep.photos || {}),
                  max_height_mm: Number(e.target.value),
                },
              })
            }
            data-testid="report-photo-height"
          />
        </div>

      </div>

    </div>


    {/* ========================================================= */}
    {/* QR + ASSINATURAS */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          QR Code e Assinaturas
        </h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            QR Code de verificação
          </p>

          <Switch
            checked={rep.show_qr !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                show_qr: v,
              })
            }
            data-testid="report-show-qr"
          />
        </div>

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            Bloco de assinaturas
          </p>

          <Switch
            checked={rep.signatures?.enabled !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                signatures: {
                  ...(rep.signatures || {}),
                  enabled: v,
                },
              })
            }
            data-testid="report-show-signatures"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">
            Quantidade de assinaturas
          </Label>

          <Input
            type="number"
            min="1"
            max="6"
            value={rep.signatures?.count ?? 3}
            onChange={(e) =>
              setSec("reports_config", {
                ...rep,
                signatures: {
                  ...(rep.signatures || {}),
                  count: Number(e.target.value),
                },
              })
            }
            data-testid="report-signature-count"
          />
        </div>

      </div>

    </div>


    {/* ========================================================= */}
    {/* ELEMENTOS / QR / CONFIGURAÇÃO GERAL */}
    {/* ========================================================= */}
    <div className="rounded-lg border border-border p-4 space-y-4">

      <div>
        <h4 className="font-heading font-semibold text-sm">
          Elementos dos Relatórios
        </h4>

        <p className="text-xs text-muted-foreground mt-1">
          Ative ou desative elementos gerais utilizados pelos relatórios.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            Relatórios habilitados
          </p>

          <Switch
            checked={rep.enabled !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                enabled: v,
              })
            }
            data-testid="report-enabled"
          />
        </div>

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            QR Code
          </p>

          <Switch
            checked={rep.show_qr !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                show_qr: v,
              })
            }
            data-testid="report-global-qr"
          />
        </div>

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            Fotos
          </p>

          <Switch
            checked={rep.photos?.enabled !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                photos: {
                  ...(rep.photos || {}),
                  enabled: v,
                },
              })
            }
            data-testid="report-global-photos"
          />
        </div>

        <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
          <p className="text-sm">
            Assinaturas
          </p>

          <Switch
            checked={rep.signatures?.enabled !== false}
            onCheckedChange={(v) =>
              setSec("reports_config", {
                ...rep,
                signatures: {
                  ...(rep.signatures || {}),
                  enabled: v,
                },
              })
            }
            data-testid="report-global-signatures"
          />
        </div>

      </div>

    </div>

  </SectionCard>

</TabsContent>
        {/* ================= GEOESPACIAL ================= */}
        <TabsContent value="geoespacial" className="space-y-4 mt-4">
          <SectionCard icon={MapIcon} title="Configurações Geoespaciais" testId="geo-config-card"
            desc="Comportamento padrão da Central Geoespacial no ERP e nos aplicativos."
            onSave={() => saveCommandSection("geo_config")} saving={saving === "command:geo_config"}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Período padrão (dias, 0 = tudo)</Label>
                <Input type="number" min={0} value={geo.default_days ?? 0} onChange={(e) => setSec("geo_config", { ...geo, default_days: parseInt(e.target.value || "0") })} data-testid="geo-days-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Zoom inicial do mapa</Label>
                <Input type="number" min={3} max={18} value={geo.default_zoom ?? 5} onChange={(e) => setSec("geo_config", { ...geo, default_zoom: parseInt(e.target.value || "5") })} data-testid="geo-zoom-input" /></div>
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <p className="text-sm">Heatmap ativado por padrão</p>
                <Switch checked={!!geo.default_heatmap} onCheckedChange={(v) => setSec("geo_config", { ...geo, default_heatmap: v })} data-testid="geo-heatmap-switch" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Tolerância de GPS do Double Check é configurada em Administração → Double Check.</p>
          </SectionCard>
        </TabsContent>

        {/* ================= SEGURANÇA ================= */}
        <TabsContent value="seguranca" className="space-y-4 mt-4">
          <SectionCard icon={ShieldCheck} title="Configurações de Segurança" testId="security-config-card"
            desc="Políticas de senha, bloqueio por tentativas e validade do link de recuperação de senha."
            onSave={() => saveCommandSection("security")} saving={saving === "command:security"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Tamanho mínimo da senha</Label>
                <Input type="number" min={6} max={32} value={secr.password_min_length ?? 6} onChange={(e) => setSec("security", { ...secr, password_min_length: parseInt(e.target.value || "6") })} data-testid="security-pwd-min" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Validade do link de redefinição (min)</Label>
                <Input type="number" min={10} max={1440} value={secr.reset_token_ttl_min ?? 60} onChange={(e) => setSec("security", { ...secr, reset_token_ttl_min: parseInt(e.target.value || "60") })} data-testid="security-reset-ttl" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Tentativas até bloqueio temporário</Label>
                <Input type="number" min={3} max={20} value={secr.lockout_attempts ?? 5} onChange={(e) => setSec("security", { ...secr, lockout_attempts: parseInt(e.target.value || "5") })} data-testid="security-lockout-attempts" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Duração do bloqueio (min)</Label>
                <Input type="number" min={5} max={240} value={secr.lockout_minutes ?? 15} onChange={(e) => setSec("security", { ...secr, lockout_minutes: parseInt(e.target.value || "15") })} data-testid="security-lockout-minutes" /></div>
            </div>
            <p className="text-xs text-muted-foreground">Recuperação de senha: fluxo "Esqueci minha senha" na tela de login envia e-mail via SMTP Hostinger com link de uso único e auditoria completa.</p>
          </SectionCard>
        </TabsContent>

        {/* ================= POR CLIENTE ================= */}
        <TabsContent value="clientes" className="space-y-4 mt-4">
          <SectionCard icon={Building2} title="Configuração do Cliente — Módulos Disponíveis" testId="client-config-card"
            desc="Esta central controla somente os módulos disponíveis para o cliente. Permissões, ações, perfis e escopo de dados são tratados separadamente."
            onSave={clientCfg ? saveClient : null} saving={saving === "command:cliente"}>
            <div className="max-w-sm space-y-1.5">
              <Label className="text-xs">Cliente</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="client-settings-select"><SelectValue placeholder="Selecione o cliente..." /></SelectTrigger>
                <SelectContent>
                  {clients.filter((c) => c?.id !== null && c?.id !== undefined && String(c.id).trim() !== "").map((c) => <SelectItem key={String(c.id)} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {clientCfg && (
              <>
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid="client-config-context">
                  <p className="text-xs font-semibold text-primary">
                    Você está configurando o cliente: {clients.find((c) => String(c?.id) === String(clientId))?.name || clientId}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Os switches abaixo representam o ENTITLEMENT persistido deste cliente — não o catálogo, não o perfil, não permissões de usuário.
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Módulos disponíveis para este cliente</Label>
                  <BulkSelectBar
                    label="Módulos do cliente"
                    count={Object.values(clientCfg.modules || {}).filter(Boolean).length}
                    total={Object.keys(moduleCatalog || {}).length}
                    onSelectAll={() =>
                      setClientCfg((current) => ({
                        ...current,
                        modules: Object.fromEntries(
                          Object.keys(moduleCatalog || {}).map((key) => [key, true]),
                        ),
                      }))
                    }
                    onClearAll={() =>
                      setClientCfg((current) => ({
                        ...current,
                        modules: Object.fromEntries(
                          Object.keys(moduleCatalog || {}).map((key) => [key, false]),
                        ),
                      }))
                    }
                    disabled={!Object.keys(moduleCatalog || {}).length}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                    {Object.entries(moduleCatalog || {}).map(([k, meta]) => (
                      <div key={k} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5" data-testid={`client-module-${k}`}>
                        <div className="min-w-0 pr-3">
                          <p className="text-sm font-medium">{meta?.name || meta?.label || k}</p>
                          <p className="text-[10px] text-muted-foreground">{k}</p>
                        </div>
                        <Switch checked={!!clientCfg.modules?.[k]} onCheckedChange={(v) => setClientCfg((c) => ({ ...c, modules: { ...c.modules, [k]: v } }))} data-testid={`client-module-switch-${k}`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 p-3">
                  <p className="text-xs font-semibold">Regra de parametrização (ausência = OFF)</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Um módulo só fica ON quando o ADMIN o habilitar explicitamente aqui para este cliente
                    (persistido em client_settings.modules). Cliente novo nasce com TODOS os módulos OFF.
                    Ausência de registro = OFF. Perfil, permissões (grants), membership e contexto ativo
                    NÃO habilitam módulo: o acesso efetivo do usuário só existe quando o módulo está ON
                    neste cliente e a autorização vigente do usuário permitir. Empresa, cliente e unidade
                    continuam reservadas à filtragem dos dados operacionais.
                  </p>
                </div>

                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4" data-testid="client-scope-configuration">
                  <div>
                    <p className="text-sm font-semibold text-primary">Escopo estrutural do cliente</p>
                    <p className="text-xs text-muted-foreground mt-1">Este bloco define o universo estrutural do cliente. Ele não substitui o escopo do usuário: o usuário será sempre restringido dentro do teto definido aqui.</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{clientHierarchyLoading ? "Carregando estrutura oficial do cadastro..." : "Estrutura carregada pela árvore cadastral oficial."}</p>
                    <p className="text-[10px] text-amber-500 mt-1">O teto estrutural do cliente é carregado e parametrizado aqui, mas sua persistência deve ser feita pelo endpoint de escopo do cliente no backend; esta tela não envia esse estado para uma rota de módulos.</p>
                  </div>
                  {[
                    ["company", "Empresas", "company_ids"],
                    ["client", "Clientes", "client_ids"],
                    ["unit", "Unidades", "unit_ids"],
                    ["area", "Áreas", "area_ids"],
                    ["subarea", "Subáreas", "subarea_ids"],
                  ].map(([level, label, key]) => {
                    const catalog = getClientScopeCatalog(level);
                    const selected = Array.isArray(clientScopeDraft?.[key]) ? clientScopeDraft[key].map(String) : [];
                    if (!catalog.length && level === "client") {
                      const fallback = [{ id: String(clientId), name: clients.find((c) => String(c.id) === String(clientId))?.name || String(clientId) }];
                      const items = fallback;
                      const allSelected = selected.length > 0;
                      return (
                        <div key={level} className="rounded-lg border border-border p-3">
                          <div className="flex items-center justify-between gap-2"><p className="text-xs font-semibold">{label}</p><Badge variant="outline">{allSelected ? 1 : 0}/1</Badge></div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">{items.map((item) => <label key={item.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs"><Checkbox checked={selected.includes(String(item.id))} onCheckedChange={(v) => toggleClientScope(level, item.id, v === true)} /><span>{item.name || item.id}</span></label>)}</div>
                        </div>
                      );
                    }
                    return (
                      <div key={level} className="rounded-lg border border-border p-3">
                        <div className="flex items-center justify-between gap-2"><div><p className="text-xs font-semibold">{label}</p><p className="text-[10px] text-muted-foreground">{catalog.length ? `${selected.length}/${catalog.length} selecionadas` : "Catálogo ainda não fornecido pela API"}</p></div><div className="flex items-center gap-1.5"><Button type="button" size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => selectAllClientScope(level)} disabled={!catalog.length || clientHierarchyLoading}>Marcar todos</Button><Button type="button" size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => clearAllClientScope(level)} disabled={!catalog.length || clientHierarchyLoading}>Desmarcar todos</Button></div></div>
                        {catalog.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">{catalog.map((item) => { const id = String(item?.id ?? item); return <label key={id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs"><Checkbox checked={selected.includes(id)} onCheckedChange={(v) => toggleClientScope(level, id, v === true)} /><span className="truncate">{item?.name || item?.label || id}</span></label>; })}</div> : <div className="rounded-md border border-dashed border-border p-3 text-[10px] text-muted-foreground">A estrutura deste nível ainda não foi entregue pelo backend. A interface está preparada para receber o catálogo sem alterar o escopo dos usuários.</div>}
                      </div>
                    );
                  })}
                  <div className="rounded-lg border border-border bg-card p-3"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Regra de teste</p><p className="text-xs mt-1">Ex.: Cliente INPASA → marque Balsas. Depois, no usuário Supervisor, atribua o escopo Balsas. O Dashboard, Ocorrências e Usuários deverão operar somente nesse universo quando o backend estiver conectado a esta matriz.</p></div>
                </div>
              </>
            )}
          </SectionCard>
        </TabsContent>

        {/* ================= GESTÃO DE ACESSOS ================= */}
        {/* REMOVIDO: a parametrização de acessos foi migrada para a Central de Acessos (/central-de-acessos) */}



        {/* ================= CENTRAL DE COMANDO — GOVERNANÇA PROFUNDA ================= */}
        <TabsContent value="comando-avancado" className="space-y-4 mt-4">
          <RobustCommandPanel
            title="Central de Comando — Parametrização Profunda"
            testId="advanced-command-center"
            description="Camada administrativa de alta granularidade. O Platform Admin organiza módulos, perfis, ações, notificações, relatórios, loading, segurança, geoespacial, aplicativos, governança, integração e critérios de qualidade. O catálogo nunca desaparece: o acesso efetivo é calculado pelo backend.">
            <div className="grid grid-cols-1 xl:grid-cols-6 gap-4">
              <div className="xl:col-span-1 space-y-2">
                <Input
                  value={advancedSearch}
                  onChange={(e) => setAdvancedSearch(e.target.value)}
                  placeholder="Pesquisar parametrização..."
                  data-testid="advanced-command-search"
                />
                <div className="rounded-xl border border-border p-2 space-y-1 max-h-[720px] overflow-auto">
                  {[
                    ["governanca", "Governança global"],
                    ["modulos", "Catálogo de módulos"],
                    
                    ["notificacoes", "Matriz de notificações"],
                    ["relatorios", "Relatórios / PDF"],
                    ["loading", "Loading Page"],
                    ["seguranca", "Segurança avançada"],
                    ["geoespacial", "Geoespacial avançado"],
                    ["aplicativos", "Aplicativos"],
                    ["interface", "Governança de UI"],
                    ["integracoes", "Integrações e propagação"],
                    ["observabilidade", "Observabilidade"],
                    ["qualidade", "Qualidade e contratos"],
                    ["dados", "Governança de dados"],
                    ["presets", "Presets operacionais"],
                    ["diagnostico", "Diagnóstico central"],
                  ].map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => setAdvancedTab(key)}
                      className={`w-full text-left rounded-md px-3 py-2 text-xs transition-colors ${advancedTab === key ? "bg-primary/10 text-primary" : "hover:bg-accent"}`}
                      data-testid={`advanced-command-tab-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="rounded-xl border border-border p-3 space-y-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</p>
                  <CommandBadge label="Perfil" value={selectedAdvancedRole} />
                  <CommandBadge label="Módulos no catálogo" value={Object.keys(moduleCatalog || {}).length} />
                  <CommandBadge label="Alterações pendentes" value={advancedDirty ? "SIM" : "NÃO"} tone={advancedDirty ? "destructive" : "default"} />
                </div>
              </div>

              <div className="xl:col-span-5 space-y-4">
                {advancedTab === "governanca" && (
                  <>
                    <SettingGroup
                      title="Roteiro de configuração e teste"
                      description="Use esta ordem para configurar a governança sem depender de tentativa e erro."
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                          ["01", "Cliente", "Marque os módulos e o escopo estrutural do cliente. Isso define o teto."],
                          ["02", "Perfil", "Marque módulos, ações, features e os perfis que o papel pode administrar."],
                          ["03", "Usuário", "Depois vincule cliente, unidade e demais níveis do escopo individual."],
                        ].map(([step, title, description]) => (
                          <div key={step} className="rounded-lg border border-border p-3">
                            <Badge variant="outline">{step}</Badge>
                            <p className="text-sm font-semibold mt-2">{title}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{description}</p>
                          </div>
                        ))}
                      </div>
                    </SettingGroup>

                    <SettingGroup
                      title="Governança global de configuração"
                      description="Define como alterações administrativas são versionadas, propagadas, auditadas e aplicadas."
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Governança habilitada" description="Ativa o modelo central de políticas." checked={governance.enabled !== false} onChange={(v) => patchAdvancedGovernance(["enabled"], v)} />
                        <BoolSetting label="Aplicação global" description="Aplicações globais não dependem da preferência individual." checked={governance.enforce_global !== false} onChange={(v) => patchAdvancedGovernance(["enforce_global"], v)} />
                        <BoolSetting label="Auditoria obrigatória" description="Toda alteração crítica deve registrar antes/depois." checked={governance.audit_changes !== false} onChange={(v) => patchAdvancedGovernance(["audit_changes"], v)} />
                        <BoolSetting label="Rollback habilitado" description="Permite recuperar uma versão administrativa anterior." checked={governance.rollback_enabled !== false} onChange={(v) => patchAdvancedGovernance(["rollback_enabled"], v)} />
                        <BoolSetting label="Validação antes de publicar" description="Impede publicar configuração estrutural inválida." checked={governance.validate_before_publish !== false} onChange={(v) => patchAdvancedGovernance(["validate_before_publish"], v)} />
                        <BoolSetting label="Propagação em tempo real" description="Dispara atualização dos consumidores quando possível." checked={governance.real_time_propagation !== false} onChange={(v) => patchAdvancedGovernance(["real_time_propagation"], v)} />
                        <BoolSetting label="Aplicação atômica" description="Uma configuração inválida não deve ser parcialmente aplicada." checked={governance.atomic_apply !== false} onChange={(v) => patchAdvancedGovernance(["atomic_apply"], v)} />
                        <BoolSetting label="Bloquear publicação fora da janela" description="Restringe alterações globais a janelas operacionais." checked={governance.enforce_change_window === true} onChange={(v) => patchAdvancedGovernance(["enforce_change_window"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Versão da política" value={governance.policy_version || 1} type="number" min={1} onChange={(v) => patchAdvancedGovernance(["policy_version"], v)} />
                        <CommandField label="Delay de propagação (ms)" value={governance.propagation_delay_ms || 0} type="number" min={0} max={60000} onChange={(v) => patchAdvancedGovernance(["propagation_delay_ms"], v)} />
                        <CommandField label="Janela mínima de publicação (s)" value={governance.minimum_publish_interval_seconds || 0} type="number" min={0} onChange={(v) => patchAdvancedGovernance(["minimum_publish_interval_seconds"], v)} />
                        <CommandSelect label="Modo de aplicação" value={governance.application_mode || "immediate"} onChange={(v) => patchAdvancedGovernance(["application_mode"], v)} options={[["immediate", "Imediato"], ["scheduled", "Programado"], ["approval", "Com aprovação"], ["maintenance", "Janela de manutenção"]]} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandArrayField label="Responsáveis por aprovação" value={governance.approver_roles || ["admin"]} onChange={(v) => patchAdvancedGovernance(["approver_roles"], v)} description="Perfis que podem aprovar mudanças que exigem aprovação." />
                        <CommandArrayField label="Seções críticas" value={governance.critical_sections || ["security", "notifications_config", "reports_config", "module_registry"]} onChange={(v) => patchAdvancedGovernance(["critical_sections"], v)} description="Seções que devem exigir tratamento reforçado." />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("governance_config", "Governança global salva.")} disabled={saving === "command:governance_config"}>
                          {saving === "command:governance_config" ? "Salvando..." : "Salvar governança"}
                        </Button>
                      </div>
                    </SettingGroup>

                    <SettingGroup title="Nota administrativa / justificativa" description="A justificativa fica associada à sessão administrativa e ajuda na auditoria humana da alteração.">
                      <Textarea
                        value={advancedAuditNote}
                        onChange={(e) => setAdvancedAuditNote(e.target.value)}
                        rows={4}
                        placeholder="Ex.: Ajuste global para adequar a nova safra, exigência de cliente, mudança de processo..."
                      />
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "modulos" && (
                  <>
                    <SettingGroup title="Registro profundo de módulos" description="Catálogo técnico e estrutural dos módulos. A disponibilidade para cada cliente é configurada exclusivamente na Central de Clientes.">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Módulo</Label>
                          <Select value={advancedModule || moduleRegistryModule || ""} onValueChange={setAdvancedModule}>
                            <SelectTrigger><SelectValue placeholder="Selecione um módulo" /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(moduleCatalog || {}).map(([module, meta]) => (
                                <SelectItem key={module} value={module}>{meta?.name || module}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2 rounded-lg border border-border p-3 bg-muted/20">
                          <p className="text-sm font-semibold">{moduleCatalog?.[advancedModule || moduleRegistryModule]?.name || advancedModule || moduleRegistryModule || "Nenhum módulo selecionado"}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">Feature: {moduleCatalog?.[advancedModule || moduleRegistryModule]?.feature || "—"}</p>
                          <p className="text-[10px] text-muted-foreground">Ações oficiais: {(moduleCatalog?.[advancedModule || moduleRegistryModule]?.actions || ADVANCED_MODULE_CAPABILITIES[advancedModule || moduleRegistryModule] || []).length}</p>
                        </div>
                      </div>

                      {advancedModule && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <BoolSetting label="Módulo globalmente disponível" description="Disponibilidade estrutural do módulo." checked={commandModule?.enabled !== false} onChange={(v) => patchModuleAdvanced(["enabled"], v)} />
                            <BoolSetting label="Visível no menu" description="Controla a presença do módulo na navegação." checked={commandModule?.menu_visible !== false} onChange={(v) => patchModuleAdvanced(["menu_visible"], v)} />
                            <BoolSetting label="Visível no dashboard" description="Permite exibição no dashboard conforme o perfil." checked={commandModule?.dashboard_visible !== false} onChange={(v) => patchModuleAdvanced(["dashboard_visible"], v)} />
                            <BoolSetting label="Visível no aplicativo" description="Disponibiliza o módulo para os apps compatíveis." checked={commandModule?.app_visible !== false} onChange={(v) => patchModuleAdvanced(["app_visible"], v)} />
                            <BoolSetting label="Acesso por rota direta" description="Permite deep link quando a autorização efetiva permitir." checked={commandModule?.allow_deep_link !== false} onChange={(v) => patchModuleAdvanced(["allow_deep_link"], v)} />
                            <BoolSetting label="Breadcrumb ativo" description="Exibe trilha de navegação do módulo." checked={commandModule?.show_breadcrumb !== false} onChange={(v) => patchModuleAdvanced(["show_breadcrumb"], v)} />
                            <BoolSetting label="Auditoria detalhada" description="Registra ações críticas do módulo." checked={commandModule?.audit_enabled !== false} onChange={(v) => patchModuleAdvanced(["audit_enabled"], v)} />
                            <BoolSetting label="Modo manutenção" description="Esconde operação e informa manutenção quando necessário." checked={commandModule?.maintenance_mode === true} onChange={(v) => patchModuleAdvanced(["maintenance_mode"], v)} />
                            <BoolSetting label="Permitir exportação" description="Ativa exportação quando a ação também estiver autorizada." checked={commandModule?.allow_export !== false} onChange={(v) => patchModuleAdvanced(["allow_export"], v)} />
                            <BoolSetting label="Permitir compartilhamento" description="Permite compartilhamento de resultados quando disponível." checked={commandModule?.allow_share === true} onChange={(v) => patchModuleAdvanced(["allow_share"], v)} />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <CommandField label="Nome administrativo" value={commandModule?.name || moduleCatalog?.[advancedModule]?.name || advancedModule} onChange={(v) => patchModuleAdvanced(["name"], v)} />
                            <CommandField label="Rota principal" value={commandModule?.route || ""} onChange={(v) => patchModuleAdvanced(["route"], v)} placeholder="/modulo" />
                            <CommandField label="Ícone" value={commandModule?.icon || "Layers"} onChange={(v) => patchModuleAdvanced(["icon"], v)} />
                            <CommandField label="Grupo do menu" value={commandModule?.menu_group || "operacao"} onChange={(v) => patchModuleAdvanced(["menu_group"], v)} />
                            <CommandField label="Ordem do menu" value={commandModule?.menu_order ?? 100} type="number" min={0} onChange={(v) => patchModuleAdvanced(["menu_order"], v)} />
                            <CommandSelect label="Escopo padrão" value={commandModule?.default_scope || "client"} onChange={(v) => patchModuleAdvanced(["default_scope"], v)} options={COMMAND_SCOPE_OPTIONS} />
                            <CommandField label="Descrição curta" value={commandModule?.description || ""} onChange={(v) => patchModuleAdvanced(["description"], v)} />
                            <CommandField label="Descrição longa" value={commandModule?.long_description || ""} onChange={(v) => patchModuleAdvanced(["long_description"], v)} />
                            <CommandArrayField label="Tags" value={commandModule?.tags || []} onChange={(v) => patchModuleAdvanced(["tags"], v)} />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <CommandArrayField label="Dependências de módulos" value={commandModule?.dependencies || []} onChange={(v) => patchModuleAdvanced(["dependencies"], v)} description="Módulos que precisam estar disponíveis antes deste módulo." />
                            <CommandArrayField label="Features exigidas" value={commandModule?.required_features || [moduleCatalog?.[advancedModule]?.feature].filter(Boolean)} onChange={(v) => patchModuleAdvanced(["required_features"], v)} description="Features que devem ser verdadeiras para permitir o módulo." />
                          </div>
                          <div className="rounded-lg border border-border p-3">
                            <p className="text-xs font-semibold mb-2">Ações operacionais adicionais</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                              {(moduleCatalog?.[advancedModule]?.actions || ADVANCED_MODULE_CAPABILITIES[advancedModule] || []).map((action) => (
                                <BoolSetting
                                  key={action}
                                  label={getActionLabel(action)}
                                  description="Disponibilidade administrativa da ação."
                                  checked={commandModule?.action_overrides?.[action] !== false}
                                  onChange={(v) => patchModuleAdvanced(["action_overrides", action], v)}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button onClick={() => saveCommandSection("module_registry", `Módulo ${advancedModule} salvo.`)} disabled={saving === "command:module_registry"}>
                              {saving === "command:module_registry" ? "Salvando..." : "Salvar módulo"}
                            </Button>
                          </div>
                        </>
                      )}
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "perfis" && (
                  <SettingGroup title="Perfil — referência única" description="A edição de módulos, ações, features, grupos de configuração e delegação fica centralizada em Governança de Acesso. Esta área não duplica os controles.">
                    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                      <p className="text-sm font-semibold text-primary">Editor oficial: Governança de Acesso</p>
                      <p className="text-xs text-muted-foreground mt-1">Use a aba Governança de Acesso para configurar o perfil. A Parametrização Profunda mantém apenas configurações técnicas do sistema.</p>
                    </div>
                  </SettingGroup>
                )}

                {advancedTab === "notificacoes" && (
                  <>
                    <SettingGroup title="Matriz de notificações — motor de distribuição" description="Configure evento, prioridade, obrigatoriedade, canais, destinatários, agrupamento, SLA e aplicação global. Preferências pessoais não devem desligar notificações sistêmicas obrigatórias.">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Evento</Label>
                          <Select value={advancedEvent} onValueChange={setAdvancedNotificationEvent}>
                            <SelectTrigger><SelectValue placeholder="Evento" /></SelectTrigger>
                            <SelectContent>
                              {Object.keys(notificationConfig.events || EVENT_LABELS).filter(Boolean).map((event) => (
                                <SelectItem key={event} value={event}>{notificationConfig.events?.[event]?.label || EVENT_LABELS[event] || event}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <CommandSelect label="Aplicação" value={advancedEventRule.application_scope || "targeted"} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "application_scope"], v)} options={[["global", "Global — todos"], ["self", "Somente destinatário"], ["targeted", "Direcionada"], ["admin_only", "Somente Admin"], ["role", "Por perfil"], ["client", "Por cliente"], ["unit", "Por unidade"]]} />
                        <CommandSelect label="Prioridade" value={advancedEventRule.priority || "medium"} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "priority"], v)} options={COMMAND_PRIORITY_OPTIONS} />
                        <CommandField label="SLA (minutos)" value={advancedEventRule.sla_minutes || 0} type="number" min={0} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "sla_minutes"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Evento habilitado" checked={advancedEventRule.enabled !== false} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "enabled"], v)} />
                        <BoolSetting label="Obrigatória" description="Quando ativa, a regra deve prevalecer sobre preferências pessoais." checked={advancedEventRule.mandatory === true} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "mandatory"], v)} />
                        <BoolSetting label="Usuário pode dispensar" checked={advancedEventRule.allow_user_dismiss !== false} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "allow_user_dismiss"], v)} />
                        <BoolSetting label="Agrupar duplicadas" checked={advancedEventRule.dedupe !== false} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "dedupe"], v)} />
                        <BoolSetting label="Exigir confirmação de leitura" checked={advancedEventRule.require_ack === true} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "require_ack"], v)} />
                        <BoolSetting label="Escalar após SLA" checked={advancedEventRule.escalate_after_sla === true} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "escalate_after_sla"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandArrayField label="Canais" value={advancedEventRule.channels || ["toast"]} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "channels"], v)} description="Ex.: toast, browser, sound, voice, email, push, in_app, sse." />
                        <CommandArrayField label="Perfis" value={advancedRouteRule.roles || []} onChange={(v) => patchAdvancedNotification(["routing", advancedEvent, "roles"], v)} description="Aplicar especificamente aos perfis listados." />
                        <CommandArrayField label="Usuários" value={advancedRouteRule.user_ids || []} onChange={(v) => patchAdvancedNotification(["routing", advancedEvent, "user_ids"], v)} description="IDs dos usuários que devem receber a regra direcionada." />
                        <CommandArrayField label="Clientes" value={advancedRouteRule.client_ids || []} onChange={(v) => patchAdvancedNotification(["routing", advancedEvent, "client_ids"], v)} description="IDs de clientes específicos." />
                        <CommandArrayField label="Unidades" value={advancedRouteRule.unit_ids || []} onChange={(v) => patchAdvancedNotification(["routing", advancedEvent, "unit_ids"], v)} description="IDs de unidades específicas." />
                        <CommandArrayField label="Tags de escalonamento" value={advancedEventRule.escalation_tags || []} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "escalation_tags"], v)} description="Marcadores usados em escalonamentos posteriores." />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandField label="Título de fallback" value={advancedEventRule.title || EVENT_LABELS[advancedEvent] || advancedEvent} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "title"], v)} />
                        <CommandField label="Mensagem de fallback" value={advancedEventRule.message || ""} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "message"], v)} />
                        <CommandField label="Ícone" value={advancedEventRule.icon || "Bell"} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "icon"], v)} />
                        <CommandField label="URL de ação" value={advancedEventRule.action_url || ""} onChange={(v) => patchAdvancedNotification(["events", advancedEvent, "action_url"], v)} placeholder="/rota" />
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button variant="outline" onClick={addAdvancedNotificationRule}>Adicionar evento</Button>
                        <Button variant="outline" onClick={() => removeAdvancedNotificationRule(advancedEvent)} disabled={!advancedEvent}>Excluir evento atual</Button>
                        <Button variant="outline" onClick={previewNotification} disabled={!advancedEvent || !!saving}>Pré-visualizar evento</Button>
                        <Button onClick={() => saveCommandSection("notifications_config", "Matriz de notificações salva.")}>Salvar matriz</Button>
                      </div>
                      {notificationPreview && <JsonSummary value={notificationPreview} title="Prévia da distribuição" maxHeight={460} />}
                    </SettingGroup>

                    <SettingGroup title="Política global de notificações" description="Parâmetros que governam a camada sistêmica antes da resolução específica por evento.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Notificações habilitadas" checked={notificationConfig.enabled !== false} onChange={(v) => patchAdvancedNotification(["enabled"], v)} />
                        <BoolSetting label="Tempo real" checked={notificationConfig.realtime !== false} onChange={(v) => patchAdvancedNotification(["realtime"], v)} />
                        <BoolSetting label="Polling de segurança" checked={notificationConfig.polling !== false} onChange={(v) => patchAdvancedNotification(["polling"], v)} />
                        <BoolSetting label="Sistêmicas obrigatórias" checked={notificationConfig.mandatory_system !== false} onChange={(v) => patchAdvancedNotification(["mandatory_system"], v)} />
                        <BoolSetting label="Permitir preferências pessoais" checked={notificationConfig.allow_personal_preferences !== false} onChange={(v) => patchAdvancedNotification(["allow_personal_preferences"], v)} />
                        <BoolSetting label="Permitir silenciar globais" description="Normalmente mantenha desligado para regras de segurança e SLA." checked={notificationConfig.allow_global_mute === true} onChange={(v) => patchAdvancedNotification(["allow_global_mute"], v)} />
                        <BoolSetting label="Retentar entrega" checked={notificationConfig.retry_enabled !== false} onChange={(v) => patchAdvancedNotification(["retry_enabled"], v)} />
                        <BoolSetting label="Persistir histórico" checked={notificationConfig.persist_history !== false} onChange={(v) => patchAdvancedNotification(["persist_history"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Retentativas" value={notificationConfig.retry_count || 3} type="number" min={0} max={20} onChange={(v) => patchAdvancedNotification(["retry_count"], v)} />
                        <CommandField label="Intervalo retry (s)" value={notificationConfig.retry_delay_seconds || 5} type="number" min={0} onChange={(v) => patchAdvancedNotification(["retry_delay_seconds"], v)} />
                        <CommandField label="Retenção (dias)" value={notificationConfig.retention_days || 90} type="number" min={1} onChange={(v) => patchAdvancedNotification(["retention_days"], v)} />
                        <CommandField label="Máximo por lote" value={notificationConfig.batch_size || 100} type="number" min={1} onChange={(v) => patchAdvancedNotification(["batch_size"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("notifications_config", "Política global de notificações salva.")}>Salvar política global</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "relatorios" && (
                  <>
                    <SettingGroup title="Motor de relatórios — configuração por categoria" description="Cada PDF recebe um contrato de apresentação próprio. A identidade global pode ser herdada e sobrescrita somente quando necessário.">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Categoria</Label>
                          <Select
                            value={advancedReportCategory}
                            onValueChange={(value) => {
                              setAdvancedReportCategory(value);
                              ensureReportCategory(value);
                            }}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(REPORT_CATEGORY_LABELS).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <CommandField label="Template" value={advancedReport.template || advancedReportCategory} onChange={(v) => patchAdvancedReport(["template"], v)} />
                        <CommandField label="Nome exibido" value={advancedReport.label || REPORT_CATEGORY_LABELS[advancedReportCategory] || advancedReportCategory} onChange={(v) => patchAdvancedReport(["label"], v)} />
                        <CommandSelect label="Herança" value={advancedReport.inherit_global === false ? "none" : "global"} onChange={(v) => patchAdvancedReport(["inherit_global"], v !== "none")} options={[["global", "Herdar global"], ["none", "Sem herança"]]} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <SettingGroup title="Página e acabamento">
                          <div className="grid grid-cols-2 gap-2">
                            <CommandSelect label="Papel" value={advancedReport.page?.size || "A4"} onChange={(v) => patchAdvancedReport(["page", "size"], v)} options={[["A4", "A4"], ["A3", "A3"], ["A5", "A5"], ["Letter", "Carta"], ["Legal", "Legal"]]} />
                            <CommandSelect label="Orientação" value={advancedReport.page?.orientation || "portrait"} onChange={(v) => patchAdvancedReport(["page", "orientation"], v)} options={[["portrait", "Retrato"], ["landscape", "Paisagem"]]} />
                            <CommandField label="Margem superior (mm)" value={advancedReport.page?.margin_top ?? 15} type="number" min={0} onChange={(v) => patchAdvancedReport(["page", "margin_top"], v)} />
                            <CommandField label="Margem inferior (mm)" value={advancedReport.page?.margin_bottom ?? 15} type="number" min={0} onChange={(v) => patchAdvancedReport(["page", "margin_bottom"], v)} />
                            <CommandField label="Margem esquerda (mm)" value={advancedReport.page?.margin_left ?? 15} type="number" min={0} onChange={(v) => patchAdvancedReport(["page", "margin_left"], v)} />
                            <CommandField label="Margem direita (mm)" value={advancedReport.page?.margin_right ?? 15} type="number" min={0} onChange={(v) => patchAdvancedReport(["page", "margin_right"], v)} />
                          </div>
                        </SettingGroup>
                        <SettingGroup title="Identidade visual do PDF">
                          <div className="space-y-2">
                            <BoolSetting label="Logo" checked={advancedReport.branding?.show_logo !== false} onChange={(v) => patchAdvancedReport(["branding", "show_logo"], v)} />
                            <BoolSetting label="Papel timbrado" checked={advancedReport.branding?.use_letterhead !== false} onChange={(v) => patchAdvancedReport(["branding", "use_letterhead"], v)} />
                            <BoolSetting label="Marca d'água" checked={advancedReport.branding?.watermark_enabled === true} onChange={(v) => patchAdvancedReport(["branding", "watermark_enabled"], v)} />
                            <BoolSetting label="Título do relatório" checked={advancedReport.header?.show_report_title !== false} onChange={(v) => patchAdvancedReport(["header", "show_report_title"], v)} />
                            <BoolSetting label="Período" checked={advancedReport.header?.show_period !== false} onChange={(v) => patchAdvancedReport(["header", "show_period"], v)} />
                            <BoolSetting label="Empresa" checked={advancedReport.header?.show_company !== false} onChange={(v) => patchAdvancedReport(["header", "show_company"], v)} />
                            <BoolSetting label="Unidade" checked={advancedReport.header?.show_unit !== false} onChange={(v) => patchAdvancedReport(["header", "show_unit"], v)} />
                            <BoolSetting label="Paginação" checked={advancedReport.footer?.show_page_number !== false} onChange={(v) => patchAdvancedReport(["footer", "show_page_number"], v)} />
                            <BoolSetting label="Data/hora de geração" checked={advancedReport.footer?.show_generation_time !== false} onChange={(v) => patchAdvancedReport(["footer", "show_generation_time"], v)} />
                          </div>
                        </SettingGroup>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <SettingGroup title="Evidências">
                          <BoolSetting label="Evidências habilitadas" checked={advancedReport.evidence?.enabled !== false} onChange={(v) => patchAdvancedReport(["evidence", "enabled"], v)} />
                          <BoolSetting label="Exigir legenda" checked={advancedReport.evidence?.require_caption !== false} onChange={(v) => patchAdvancedReport(["evidence", "require_caption"], v)} />
                          <BoolSetting label="Exigir timestamp" checked={advancedReport.evidence?.require_timestamp !== false} onChange={(v) => patchAdvancedReport(["evidence", "require_timestamp"], v)} />
                          <BoolSetting label="Exigir localização" checked={advancedReport.evidence?.require_location === true} onChange={(v) => patchAdvancedReport(["evidence", "require_location"], v)} />
                        </SettingGroup>
                        <SettingGroup title="Fotos">
                          <BoolSetting label="Fotos habilitadas" checked={advancedReport.photos?.enabled !== false} onChange={(v) => patchAdvancedReport(["photos", "enabled"], v)} />
                          <CommandField label="Máximo por seção" value={advancedReport.photos?.max_per_section ?? 6} type="number" min={0} max={100} onChange={(v) => patchAdvancedReport(["photos", "max_per_section"], v)} />
                          <CommandField label="Colunas" value={advancedReport.photos?.columns ?? 3} type="number" min={1} max={6} onChange={(v) => patchAdvancedReport(["photos", "columns"], v)} />
                          <CommandSelect label="Ajuste da imagem" value={advancedReport.photos?.fit || "contain"} onChange={(v) => patchAdvancedReport(["photos", "fit"], v)} options={[["contain", "Conter"], ["cover", "Preencher"], ["original", "Original"]]} />
                        </SettingGroup>
                        <SettingGroup title="Assinaturas e autenticação">
                          <BoolSetting label="Assinaturas habilitadas" checked={advancedReport.signatures?.enabled !== false} onChange={(v) => patchAdvancedReport(["signatures", "enabled"], v)} />
                          <CommandField label="Quantidade" value={advancedReport.signatures?.count ?? 3} type="number" min={0} max={10} onChange={(v) => patchAdvancedReport(["signatures", "count"], v)} />
                          <BoolSetting label="QR Code" checked={advancedReport.show_qr !== false} onChange={(v) => patchAdvancedReport(["show_qr"], v)} />
                          <BoolSetting label="Hash de integridade" checked={advancedReport.integrity_hash === true} onChange={(v) => patchAdvancedReport(["integrity_hash"], v)} />
                        </SettingGroup>
                      </div>
                      <SettingGroup title="Ordem das seções" description="Defina a ordem exacta do documento por categoria. Essa ordem deve ser respeitada pelo gerador do PDF.">
                        <div className="space-y-1">
                          {(advancedReport.sections_order || ADVANCED_REPORT_SECTIONS).map((section, index) => (
                            <div key={`${section}-${index}`} className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                              <span className="text-[10px] text-muted-foreground w-6">{index + 1}</span>
                              <span className="flex-1 text-xs">{section.replaceAll("_", " ")}</span>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setAdvancedReportSectionOrder(section, index, -1)} disabled={index === 0}><ArrowUp size={13} /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setAdvancedReportSectionOrder(section, index, 1)} disabled={index === (advancedReport.sections_order || ADVANCED_REPORT_SECTIONS).length - 1}><ArrowDown size={13} /></Button>
                              <Switch checked={advancedReport.section_visibility?.[section] !== false} onCheckedChange={(v) => patchAdvancedReport(["section_visibility", section], v)} />
                            </div>
                          ))}
                        </div>
                      </SettingGroup>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Fonte principal" value={advancedReport.typography?.font_family || "Inter"} onChange={(v) => patchAdvancedReport(["typography", "font_family"], v)} />
                        <CommandField label="Tamanho base" value={advancedReport.typography?.base_size ?? 9} type="number" min={5} max={20} onChange={(v) => patchAdvancedReport(["typography", "base_size"], v)} />
                        <CommandField label="Cor primária" value={advancedReport.colors?.primary || "#14532d"} onChange={(v) => patchAdvancedReport(["colors", "primary"], v)} />
                        <CommandField label="Cor de destaque" value={advancedReport.colors?.accent || "#166534"} onChange={(v) => patchAdvancedReport(["colors", "accent"], v)} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => ensureReportCategory(advancedReportCategory)}>Inicializar categoria</Button>
                        <Button onClick={() => saveCommandSection("reports_config", `Configuração ${REPORT_CATEGORY_LABELS[advancedReportCategory] || advancedReportCategory} salva.`)}>Salvar categoria</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "loading" && (
                  <>
                    <SettingGroup title="Loading Page — controle integral" description="A Loading Page passa a ter textos, tempos, mensagens de erro, etapas e comportamento configuráveis pelo Administrador.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Loading habilitado" checked={loadingCfg.enabled !== false} onChange={(v) => patchAdvancedLoading(["enabled"], v)} />
                        <BoolSetting label="Autoplay de etapas" checked={loadingCfg.autoplay !== false} onChange={(v) => patchAdvancedLoading(["autoplay"], v)} />
                        <BoolSetting label="Mostrar progresso" checked={loadingCfg.show_progress !== false} onChange={(v) => patchAdvancedLoading(["show_progress"], v)} />
                        <BoolSetting label="Permitir cancelar inicialização" checked={loadingCfg.allow_cancel === true} onChange={(v) => patchAdvancedLoading(["allow_cancel"], v)} />
                        <BoolSetting label="Mostrar logo" checked={loadingCfg.show_logo !== false} onChange={(v) => patchAdvancedLoading(["show_logo"], v)} />
                        <BoolSetting label="Mostrar percentuais" checked={loadingCfg.show_percentage !== false} onChange={(v) => patchAdvancedLoading(["show_percentage"], v)} />
                        <BoolSetting label="Modo silencioso" checked={loadingCfg.silent_mode === true} onChange={(v) => patchAdvancedLoading(["silent_mode"], v)} />
                        <BoolSetting label="Redirecionar em timeout" checked={loadingCfg.redirect_on_timeout !== false} onChange={(v) => patchAdvancedLoading(["redirect_on_timeout"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Duração mínima (ms)" value={loadingCfg.minimum_duration_ms ?? 450} type="number" min={0} max={120000} onChange={(v) => patchAdvancedLoading(["minimum_duration_ms"], v)} />
                        <CommandField label="Timeout (ms)" value={loadingCfg.timeout_ms ?? 30000} type="number" min={1000} max={300000} onChange={(v) => patchAdvancedLoading(["timeout_ms"], v)} />
                        <CommandSelect label="Transição" value={loadingCfg.transition || "fade"} onChange={(v) => patchAdvancedLoading(["transition"], v)} options={[["fade", "Fade"], ["slide", "Slide"], ["zoom", "Zoom"], ["none", "Nenhuma"]]} />
                        <CommandSelect label="Variante" value={loadingCfg.variant || "standard"} onChange={(v) => patchAdvancedLoading(["variant"], v)} options={[["standard", "Padrão"], ["minimal", "Minimalista"], ["branded", "Institucional"], ["technical", "Técnica"]]} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandField label="Título" value={loadingCfg.title || "Preparando a plataforma"} onChange={(v) => patchAdvancedLoading(["title"], v)} />
                        <CommandField label="Subtítulo" value={loadingCfg.subtitle || "Sincronizando módulos, permissões e dados."} onChange={(v) => patchAdvancedLoading(["subtitle"], v)} />
                        <CommandField label="Mensagem inicial" value={loadingCfg.initial_message || "Inicializando o ambiente..."} onChange={(v) => patchAdvancedLoading(["initial_message"], v)} />
                        <CommandField label="Mensagem de autenticação" value={loadingCfg.auth_message || "Validando sua sessão..."} onChange={(v) => patchAdvancedLoading(["auth_message"], v)} />
                        <CommandField label="Mensagem de autorização" value={loadingCfg.authorization_message || "Aplicando seus acessos..."} onChange={(v) => patchAdvancedLoading(["authorization_message"], v)} />
                        <CommandField label="Mensagem de sincronização" value={loadingCfg.sync_message || "Sincronizando dados..."} onChange={(v) => patchAdvancedLoading(["sync_message"], v)} />
                        <CommandField label="Mensagem final" value={loadingCfg.success_message || "Tudo pronto."} onChange={(v) => patchAdvancedLoading(["success_message"], v)} />
                        <CommandField label="Mensagem de erro" value={loadingCfg.error_message || "Não foi possível concluir a inicialização."} onChange={(v) => patchAdvancedLoading(["error_message"], v)} />
                        <CommandField label="Texto do botão de retry" value={loadingCfg.retry_label || "Tentar novamente"} onChange={(v) => patchAdvancedLoading(["retry_label"], v)} />
                        <CommandField label="Texto do botão cancelar" value={loadingCfg.cancel_label || "Cancelar"} onChange={(v) => patchAdvancedLoading(["cancel_label"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <CommandArrayField label="Etapas" value={loadingCfg.steps || ["sessao", "autorizacao", "configuracoes", "dados", "finalizacao"]} onChange={(v) => patchAdvancedLoading(["steps"], v)} />
                        <CommandArrayField label="Mensagens por etapa" value={loadingCfg.step_messages || ["Validando sessão", "Calculando permissões", "Aplicando configurações", "Carregando dados", "Finalizando"]} onChange={(v) => patchAdvancedLoading(["step_messages"], v)} />
                        <CommandArrayField label="Classes visuais" value={loadingCfg.visual_tokens || ["brand", "primary", "success", "warning", "error"]} onChange={(v) => patchAdvancedLoading(["visual_tokens"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("loading_config", "Loading Page configurada e salva.")}>Salvar Loading Page</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "seguranca" && (
                  <>
                    <SettingGroup title="Segurança — política máxima administrativa" description="Controles preventivos de autenticação, sessão, auditoria e proteção de configuração.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="MFA obrigatório para Admin" checked={securityConfig.require_mfa_for_admin !== false} onChange={(v) => patchAdvancedSecurity(["require_mfa_for_admin"], v)} />
                        <BoolSetting label="Rotação obrigatória de senha" checked={securityConfig.force_password_rotation === true} onChange={(v) => patchAdvancedSecurity(["force_password_rotation"], v)} />
                        <BoolSetting label="Bloqueio por tentativas" checked={securityConfig.block_after_failed_attempts !== false} onChange={(v) => patchAdvancedSecurity(["block_after_failed_attempts"], v)} />
                        <BoolSetting label="Revogação de sessões" checked={securityConfig.session_revocation_enabled !== false} onChange={(v) => patchAdvancedSecurity(["session_revocation_enabled"], v)} />
                        <BoolSetting label="Auditoria de segurança" checked={securityConfig.audit_security_events !== false} onChange={(v) => patchAdvancedSecurity(["audit_security_events"], v)} />
                        <BoolSetting label="Proteger rotas críticas" checked={securityConfig.protect_configuration_routes !== false} onChange={(v) => patchAdvancedSecurity(["protect_configuration_routes"], v)} />
                        <BoolSetting label="CSRF" checked={securityConfig.csrf_protection !== false} onChange={(v) => patchAdvancedSecurity(["csrf_protection"], v)} />
                        <BoolSetting label="Rate limit" checked={securityConfig.login_rate_limit !== false} onChange={(v) => patchAdvancedSecurity(["login_rate_limit"], v)} />
                        <BoolSetting label="Transporte seguro obrigatório" checked={securityConfig.require_secure_transport !== false} onChange={(v) => patchAdvancedSecurity(["require_secure_transport"], v)} />
                        <BoolSetting label="Impedir configuração fora do escopo" checked={securityConfig.enforce_scope_on_configuration !== false} onChange={(v) => patchAdvancedSecurity(["enforce_scope_on_configuration"], v)} />
                        <BoolSetting label="Proteção contra reenvio" checked={securityConfig.replay_protection !== false} onChange={(v) => patchAdvancedSecurity(["replay_protection"], v)} />
                        <BoolSetting label="Alertar alteração crítica" checked={securityConfig.notify_critical_changes !== false} onChange={(v) => patchAdvancedSecurity(["notify_critical_changes"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Tentativas máximas" value={securityConfig.max_login_attempts ?? 5} type="number" min={1} max={50} onChange={(v) => patchAdvancedSecurity(["max_login_attempts"], v)} />
                        <CommandField label="Janela de tentativa (s)" value={securityConfig.failed_attempt_window_seconds ?? 900} type="number" min={30} onChange={(v) => patchAdvancedSecurity(["failed_attempt_window_seconds"], v)} />
                        <CommandField label="Timeout de sessão (min)" value={securityConfig.session_timeout_minutes ?? 480} type="number" min={5} onChange={(v) => patchAdvancedSecurity(["session_timeout_minutes"], v)} />
                        <CommandField label="Expiração de senha (dias)" value={securityConfig.password_expiration_days ?? 90} type="number" min={1} onChange={(v) => patchAdvancedSecurity(["password_expiration_days"], v)} />
                        <CommandField label="Rate limit / janela" value={securityConfig.rate_limit_window_seconds ?? 60} type="number" min={1} onChange={(v) => patchAdvancedSecurity(["rate_limit_window_seconds"], v)} />
                        <CommandField label="Rate limit máx." value={securityConfig.rate_limit_max_requests ?? 60} type="number" min={1} onChange={(v) => patchAdvancedSecurity(["rate_limit_max_requests"], v)} />
                        <CommandField label="Retenção de auditoria (dias)" value={securityConfig.audit_retention_days ?? 365} type="number" min={1} onChange={(v) => patchAdvancedSecurity(["audit_retention_days"], v)} />
                        <CommandField label="Máximo de sessões simultâneas" value={securityConfig.max_concurrent_sessions ?? 5} type="number" min={1} onChange={(v) => patchAdvancedSecurity(["max_concurrent_sessions"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandArrayField label="IPs permitidos para Admin" value={securityConfig.admin_ip_allowlist || []} onChange={(v) => patchAdvancedSecurity(["admin_ip_allowlist"], v)} description="Opcional. Uma regra vazia significa ausência de restrição por IP nesta camada." />
                        <CommandArrayField label="Ações sempre auditadas" value={securityConfig.always_audit_actions || ["delegate", "reset_password", "change_role", "change_permissions", "change_security"]} onChange={(v) => patchAdvancedSecurity(["always_audit_actions"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("security", "Política de segurança salva.")}>Salvar segurança</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "geoespacial" && (
                  <>
                    <SettingGroup title="Geoespacial — controle operacional completo" description="Controla GPS, precisão, histórico, mapas, persistência e exigências de localização.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Geoespacial habilitado" checked={geoConfig.enabled !== false} onChange={(v) => patchAdvancedGeo(["enabled"], v)} />
                        <BoolSetting label="GPS habilitado" checked={geoConfig.gps_enabled !== false} onChange={(v) => patchAdvancedGeo(["gps_enabled"], v)} />
                        <BoolSetting label="Exigir precisão mínima" checked={geoConfig.require_accuracy !== false} onChange={(v) => patchAdvancedGeo(["require_accuracy"], v)} />
                        <BoolSetting label="Permitir localização manual" checked={geoConfig.allow_manual_location === true} onChange={(v) => patchAdvancedGeo(["allow_manual_location"], v)} />
                        <BoolSetting label="Guardar histórico" checked={geoConfig.track_history !== false} onChange={(v) => patchAdvancedGeo(["track_history"], v)} />
                        <BoolSetting label="Persistir coordenadas" checked={geoConfig.store_coordinates !== false} onChange={(v) => patchAdvancedGeo(["store_coordinates"], v)} />
                        <BoolSetting label="Mapa no Dashboard" checked={geoConfig.show_map_on_dashboard !== false} onChange={(v) => patchAdvancedGeo(["show_map_on_dashboard"], v)} />
                        <BoolSetting label="Mapa nos relatórios" checked={geoConfig.show_map_on_reports !== false} onChange={(v) => patchAdvancedGeo(["show_map_on_reports"], v)} />
                        <BoolSetting label="Exigir localização em evidências" checked={geoConfig.require_location_for_evidence === true} onChange={(v) => patchAdvancedGeo(["require_location_for_evidence"], v)} />
                        <BoolSetting label="Bloquear localização inválida" checked={geoConfig.block_invalid_coordinates !== false} onChange={(v) => patchAdvancedGeo(["block_invalid_coordinates"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Precisão mínima (m)" value={geoConfig.min_accuracy_m ?? 50} type="number" min={1} onChange={(v) => patchAdvancedGeo(["min_accuracy_m"], v)} />
                        <CommandField label="Intervalo GPS (s)" value={geoConfig.gps_interval_seconds ?? 30} type="number" min={1} onChange={(v) => patchAdvancedGeo(["gps_interval_seconds"], v)} />
                        <CommandField label="Zoom padrão" value={geoConfig.default_zoom ?? 15} type="number" min={1} max={22} onChange={(v) => patchAdvancedGeo(["default_zoom"], v)} />
                        <CommandField label="Raio de tolerância (m)" value={geoConfig.allowed_radius_m ?? 100} type="number" min={1} onChange={(v) => patchAdvancedGeo(["allowed_radius_m"], v)} />
                        <CommandSelect label="Provedor" value={geoConfig.provider || "osm"} onChange={(v) => patchAdvancedGeo(["provider"], v)} options={[["osm", "OpenStreetMap"], ["google", "Google Maps"], ["mapbox", "Mapbox"], ["custom", "Customizado"]]} />
                        <CommandField label="URL de tiles" value={geoConfig.tile_url || ""} onChange={(v) => patchAdvancedGeo(["tile_url"], v)} placeholder="https://.../{z}/{x}/{y}.png" />
                        <CommandField label="Attribution" value={geoConfig.attribution || ""} onChange={(v) => patchAdvancedGeo(["attribution"], v)} />
                        <CommandField label="URL de geocoding" value={geoConfig.geocoding_url || ""} onChange={(v) => patchAdvancedGeo(["geocoding_url"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <CommandArrayField label="Camadas padrão" value={geoConfig.default_layers || ["points", "routes", "heatmap"]} onChange={(v) => patchAdvancedGeo(["default_layers"], v)} />
                        <CommandArrayField label="Eventos que exigem GPS" value={geoConfig.required_gps_events || ["ocorrencia_criada", "monitoramento_concluido", "sala_eletrica_nc"]} onChange={(v) => patchAdvancedGeo(["required_gps_events"], v)} />
                        <CommandArrayField label="Campos exportados" value={geoConfig.export_fields || ["latitude", "longitude", "accuracy", "timestamp"]} onChange={(v) => patchAdvancedGeo(["export_fields"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("geo_config", "Configuração geoespacial salva.")}>Salvar geoespacial</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "aplicativos" && (
                  <>
                    <SettingGroup title="Aplicativo — parametrização por produto" description="Controle separado para aplicativo Técnico e aplicativo Cliente. O comportamento deve refletir o app sem precisar alterar código do app para ajustes simples.">
                      <div className="flex items-center gap-2 flex-wrap">
                        {[
                          ["tecnico", "App Técnico"],
                          ["cliente", "App Cliente"],
                        ].map(([key, label]) => (
                          <Button key={key} variant={appKey === key ? "default" : "outline"} onClick={() => setAppKey(key)}>{label}</Button>
                        ))}
                        <Badge variant="outline">{appKey}</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Aplicativo habilitado" checked={ac.enabled !== false} onChange={(v) => patchAdvancedApp(["enabled"], v)} />
                        <BoolSetting label="Login persistente" checked={ac.auth?.remember_me !== false} onChange={(v) => patchAdvancedApp(["auth", "remember_me"], v)} />
                        <BoolSetting label="Modo offline" checked={ac.offline?.enabled === true} onChange={(v) => patchAdvancedApp(["offline", "enabled"], v)} />
                        <BoolSetting label="Sincronização automática" checked={ac.sync?.auto === true} onChange={(v) => patchAdvancedApp(["sync", "auto"], v)} />
                        <BoolSetting label="Sincronização em segundo plano" checked={ac.sync?.background === true} onChange={(v) => patchAdvancedApp(["sync", "background"], v)} />
                        <BoolSetting label="Exigir atualização mínima" checked={ac.version?.enforce === true} onChange={(v) => patchAdvancedApp(["version", "enforce"], v)} />
                        <BoolSetting label="Captura automática de fotos" checked={!!ac.features?.captura_automatica} onChange={(v) => patchAdvancedApp(["features", "captura_automatica"], v)} />
                        <BoolSetting label="OCR" checked={!!ac.features?.ocr} onChange={(v) => patchAdvancedApp(["features", "ocr"], v)} />
                        <BoolSetting label="QR Code" checked={!!ac.features?.qr_code} onChange={(v) => patchAdvancedApp(["features", "qr_code"], v)} />
                        <BoolSetting label="Assinatura digital" checked={!!ac.features?.assinatura_digital} onChange={(v) => patchAdvancedApp(["features", "assinatura_digital"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Versão mínima" value={ac.version?.minimum || "1.0.0"} onChange={(v) => patchAdvancedApp(["version", "minimum"], v)} />
                        <CommandField label="URL de atualização" value={ac.version?.update_url || ""} onChange={(v) => patchAdvancedApp(["version", "update_url"], v)} />
                        <CommandField label="Intervalo sync (s)" value={ac.sync?.interval_seconds ?? 60} type="number" min={5} onChange={(v) => patchAdvancedApp(["sync", "interval_seconds"], v)} />
                        <CommandField label="Limite de cache (MB)" value={ac.offline?.max_cache_mb ?? 100} type="number" min={10} onChange={(v) => patchAdvancedApp(["offline", "max_cache_mb"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandArrayField label="Permissões de câmera" value={ac.permissions?.camera || ["capture"]} onChange={(v) => patchAdvancedApp(["permissions", "camera"], v)} />
                        <CommandArrayField label="Permissões de localização" value={ac.permissions?.location || ["foreground"]} onChange={(v) => patchAdvancedApp(["permissions", "location"], v)} />
                        <CommandArrayField label="Rotas liberadas no app" value={ac.navigation?.allowed_routes || Object.keys(MENU_LABELS)} onChange={(v) => patchAdvancedApp(["navigation", "allowed_routes"], v)} />
                        <CommandArrayField label="Módulos destacados" value={ac.navigation?.featured_modules || ["dashboard", "monitoramentos", "ocorrencias"]} onChange={(v) => patchAdvancedApp(["navigation", "featured_modules"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("app_config", `Configuração do ${appKey === "tecnico" ? "App Técnico" : "App Cliente"} salva.`)}>Salvar aplicativo</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "interface" && (
                  <>
                    <SettingGroup title="Governança de interface" description="A camada de UI deve ter regras previsíveis para evitar telas inconsistentes e estados órfãos.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Governança UI habilitada" checked={uiGovernance.enabled !== false} onChange={(v) => patchAdvancedUI(["enabled"], v)} />
                        <BoolSetting label="Modo compacto" checked={uiGovernance.compact_mode === true} onChange={(v) => patchAdvancedUI(["compact_mode"], v)} />
                        <BoolSetting label="Lembrar última aba" checked={uiGovernance.remember_last_tab !== false} onChange={(v) => patchAdvancedUI(["remember_last_tab"], v)} />
                        <BoolSetting label="Confirmar destrutivos" checked={uiGovernance.confirm_destructive_actions !== false} onChange={(v) => patchAdvancedUI(["confirm_destructive_actions"], v)} />
                        <BoolSetting label="Autosave" checked={uiGovernance.autosave_enabled === true} onChange={(v) => patchAdvancedUI(["autosave_enabled"], v)} />
                        <BoolSetting label="Controles avançados" checked={uiGovernance.show_advanced_controls !== false} onChange={(v) => patchAdvancedUI(["show_advanced_controls"], v)} />
                        <BoolSetting label="Diagnóstico visível" checked={uiGovernance.show_diagnostics !== false} onChange={(v) => patchAdvancedUI(["show_diagnostics"], v)} />
                        <BoolSetting label="Exigir confirmação de mudança global" checked={uiGovernance.confirm_global_changes !== false} onChange={(v) => patchAdvancedUI(["confirm_global_changes"], v)} />
                        <BoolSetting label="Bloquear navegação durante save" checked={uiGovernance.lock_navigation_while_saving !== false} onChange={(v) => patchAdvancedUI(["lock_navigation_while_saving"], v)} />
                        <BoolSetting label="Mostrar estado de sincronização" checked={uiGovernance.show_sync_state !== false} onChange={(v) => patchAdvancedUI(["show_sync_state"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Debounce de busca (ms)" value={uiGovernance.search_debounce_ms ?? 250} type="number" min={0} onChange={(v) => patchAdvancedUI(["search_debounce_ms"], v)} />
                        <CommandField label="Duração toast (ms)" value={uiGovernance.toast_duration_ms ?? 5000} type="number" min={500} onChange={(v) => patchAdvancedUI(["toast_duration_ms"], v)} />
                        <CommandField label="Itens por página" value={uiGovernance.default_page_size ?? 25} type="number" min={5} max={200} onChange={(v) => patchAdvancedUI(["default_page_size"], v)} />
                        <CommandField label="Limite de tabelas" value={uiGovernance.table_max_columns ?? 20} type="number" min={5} onChange={(v) => patchAdvancedUI(["table_max_columns"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandArrayField label="Rotas protegidas" value={uiGovernance.protected_routes || ["/plataforma", "/usuarios", "/auditoria", "/configuracao-operacional"]} onChange={(v) => patchAdvancedUI(["protected_routes"], v)} />
                        <CommandArrayField label="Componentes críticos" value={uiGovernance.critical_components || ["permissions", "notifications", "reports", "security"]} onChange={(v) => patchAdvancedUI(["critical_components"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("ui_governance_config", "Governança de interface salva.")}>Salvar UI</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "integracoes" && (
                  <>
                    <SettingGroup title="Integrações e propagação" description="Mapeia como a configuração administrativa se propaga para ERP, apps, notificações, relatórios e eventos.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="ERP" checked={integrationCfg.erp_sync_enabled !== false} onChange={(v) => patchAdvancedIntegration(["erp_sync_enabled"], v)} />
                        <BoolSetting label="Aplicativos" checked={integrationCfg.apps_sync_enabled !== false} onChange={(v) => patchAdvancedIntegration(["apps_sync_enabled"], v)} />
                        <BoolSetting label="Notificações" checked={integrationCfg.notification_sync_enabled !== false} onChange={(v) => patchAdvancedIntegration(["notification_sync_enabled"], v)} />
                        <BoolSetting label="Relatórios" checked={integrationCfg.report_sync_enabled !== false} onChange={(v) => patchAdvancedIntegration(["report_sync_enabled"], v)} />
                        <BoolSetting label="Eventos em tempo real" checked={integrationCfg.event_propagation_enabled !== false} onChange={(v) => patchAdvancedIntegration(["event_propagation_enabled"], v)} />
                        <BoolSetting label="Cache invalidation" checked={integrationCfg.invalidate_client_cache !== false} onChange={(v) => patchAdvancedIntegration(["invalidate_client_cache"], v)} />
                        <BoolSetting label="Sincronização agendada" checked={integrationCfg.scheduled_sync_enabled === true} onChange={(v) => patchAdvancedIntegration(["scheduled_sync_enabled"], v)} />
                        <BoolSetting label="Fallback local" checked={integrationCfg.local_fallback_enabled !== false} onChange={(v) => patchAdvancedIntegration(["local_fallback_enabled"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Evento de mudança" value={integrationCfg.event_name || "astral:platform-config-changed"} onChange={(v) => patchAdvancedIntegration(["event_name"], v)} />
                        <CommandField label="Canal" value={integrationCfg.event_channel || "platform"} onChange={(v) => patchAdvancedIntegration(["event_channel"], v)} />
                        <CommandField label="Timeout (ms)" value={integrationCfg.sync_timeout_ms ?? 15000} type="number" min={500} onChange={(v) => patchAdvancedIntegration(["sync_timeout_ms"], v)} />
                        <CommandField label="Tentativas" value={integrationCfg.sync_retries ?? 3} type="number" min={0} max={20} onChange={(v) => patchAdvancedIntegration(["sync_retries"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CommandArrayField label="Consumidores críticos" value={integrationCfg.critical_consumers || ["layout", "auth_context", "notifications", "report_engine"]} onChange={(v) => patchAdvancedIntegration(["critical_consumers"], v)} />
                        <CommandArrayField label="Eventos emitidos" value={integrationCfg.events || ["platform.config.changed", "modules.changed", "permissions.changed", "reports.config.changed"]} onChange={(v) => patchAdvancedIntegration(["events"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("integration_config", "Integrações e propagação salvas.")}>Salvar integrações</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "observabilidade" && (
                  <>
                    <SettingGroup title="Observabilidade administrativa" description="A plataforma deve informar ao Administrador o estado da configuração, persistência e propagação.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Health check administrativo" checked={governance.health_check_enabled !== false} onChange={(v) => patchAdvancedGovernance(["health_check_enabled"], v)} />
                        <BoolSetting label="Mostrar último sync" checked={governance.show_last_sync !== false} onChange={(v) => patchAdvancedGovernance(["show_last_sync"], v)} />
                        <BoolSetting label="Mostrar versão efetiva" checked={governance.show_effective_version !== false} onChange={(v) => patchAdvancedGovernance(["show_effective_version"], v)} />
                        <BoolSetting label="Registrar falhas de propagação" checked={governance.log_propagation_errors !== false} onChange={(v) => patchAdvancedGovernance(["log_propagation_errors"], v)} />
                        <BoolSetting label="Alertar drift de configuração" checked={governance.detect_configuration_drift !== false} onChange={(v) => patchAdvancedGovernance(["detect_configuration_drift"], v)} />
                        <BoolSetting label="Comparar frontend/backend" checked={governance.compare_client_server_config !== false} onChange={(v) => patchAdvancedGovernance(["compare_client_server_config"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Intervalo health check (s)" value={governance.health_check_interval_seconds ?? 60} type="number" min={5} onChange={(v) => patchAdvancedGovernance(["health_check_interval_seconds"], v)} />
                        <CommandField label="Limite de drift (%)" value={governance.drift_threshold_percent ?? 0} type="number" min={0} onChange={(v) => patchAdvancedGovernance(["drift_threshold_percent"], v)} />
                        <CommandField label="Retenção de eventos (dias)" value={governance.event_retention_days ?? 30} type="number" min={1} onChange={(v) => patchAdvancedGovernance(["event_retention_days"], v)} />
                        <CommandField label="Máximo de alertas" value={governance.max_active_alerts ?? 100} type="number" min={1} onChange={(v) => patchAdvancedGovernance(["max_active_alerts"], v)} />
                      </div>
                      <div className="rounded-lg border border-border p-3">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <CommandBadge label="Catálogo" value={`${Object.keys(moduleCatalog || {}).length} módulos`} />
                          <CommandBadge label="Perfis" value={`${normalizedRoles.length} perfis`} />
                          <CommandBadge label="Eventos" value={`${Object.keys(notificationConfig.events || {}).length} eventos`} />
                          <CommandBadge label="Categorias PDF" value={`${Object.keys(effectiveReportCategories || {}).length} categorias`} />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("governance_config", "Observabilidade administrativa salva.")}>Salvar observabilidade</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "qualidade" && (
                  <>
                    <SettingGroup title="Qualidade e contratos de configuração" description="Regras administrativas para impedir configurações incompletas, incoerentes ou incapazes de renderizar a tela.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Validar campos obrigatórios" checked={governance.validate_required_fields !== false} onChange={(v) => patchAdvancedGovernance(["validate_required_fields"], v)} />
                        <BoolSetting label="Rejeitar valores vazios críticos" checked={governance.reject_empty_critical_values !== false} onChange={(v) => patchAdvancedGovernance(["reject_empty_critical_values"], v)} />
                        <BoolSetting label="Validar catálogo de módulos" checked={governance.validate_module_catalog !== false} onChange={(v) => patchAdvancedGovernance(["validate_module_catalog"], v)} />
                        <BoolSetting label="Validar matriz por perfil" checked={governance.validate_role_matrix !== false} onChange={(v) => patchAdvancedGovernance(["validate_role_matrix"], v)} />
                        <BoolSetting label="Validar regras de notificação" checked={governance.validate_notification_rules !== false} onChange={(v) => patchAdvancedGovernance(["validate_notification_rules"], v)} />
                        <BoolSetting label="Validar categorias de relatório" checked={governance.validate_report_categories !== false} onChange={(v) => patchAdvancedGovernance(["validate_report_categories"], v)} />
                        <BoolSetting label="Validar Loading Page" checked={governance.validate_loading_page !== false} onChange={(v) => patchAdvancedGovernance(["validate_loading_page"], v)} />
                        <BoolSetting label="Validar referências internas" checked={governance.validate_references !== false} onChange={(v) => patchAdvancedGovernance(["validate_references"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <CommandArrayField label="Campos obrigatórios" value={governance.required_fields || ["name", "enabled", "updated_at"]} onChange={(v) => patchAdvancedGovernance(["required_fields"], v)} />
                        <CommandArrayField label="Seções críticas obrigatórias" value={governance.required_sections || ["security", "notifications_config", "reports_config", "loading_config"]} onChange={(v) => patchAdvancedGovernance(["required_sections"], v)} />
                        <CommandArrayField label="Valores proibidos" value={governance.forbidden_values || ["undefined", "NaN", "null"]} onChange={(v) => patchAdvancedGovernance(["forbidden_values"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("governance_config", "Contratos de qualidade salvos.")}>Salvar qualidade</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "dados" && (
                  <>
                    <SettingGroup title="Governança de dados administrativos" description="Define como a camada administrativa trata consistência, histórico e dados de configuração.">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <BoolSetting label="Histórico de versões" checked={governance.version_history_enabled !== false} onChange={(v) => patchAdvancedGovernance(["version_history_enabled"], v)} />
                        <BoolSetting label="Snapshots antes de salvar" checked={governance.snapshot_before_save !== false} onChange={(v) => patchAdvancedGovernance(["snapshot_before_save"], v)} />
                        <BoolSetting label="Deduplicação de configuração" checked={governance.deduplicate_configuration !== false} onChange={(v) => patchAdvancedGovernance(["deduplicate_configuration"], v)} />
                        <BoolSetting label="Normalização de strings" checked={governance.normalize_strings !== false} onChange={(v) => patchAdvancedGovernance(["normalize_strings"], v)} />
                        <BoolSetting label="Normalização de arrays" checked={governance.normalize_arrays !== false} onChange={(v) => patchAdvancedGovernance(["normalize_arrays"], v)} />
                        <BoolSetting label="Preservar campos desconhecidos" checked={governance.preserve_unknown_fields !== false} onChange={(v) => patchAdvancedGovernance(["preserve_unknown_fields"], v)} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandField label="Versões mantidas" value={governance.max_versions ?? 20} type="number" min={1} onChange={(v) => patchAdvancedGovernance(["max_versions"], v)} />
                        <CommandField label="Snapshot TTL (dias)" value={governance.snapshot_retention_days ?? 90} type="number" min={1} onChange={(v) => patchAdvancedGovernance(["snapshot_retention_days"], v)} />
                        <CommandField label="Máximo de payload (KB)" value={governance.max_configuration_kb ?? 1024} type="number" min={64} onChange={(v) => patchAdvancedGovernance(["max_configuration_kb"], v)} />
                        <CommandField label="Checksum" value={governance.checksum_algorithm || "sha256"} onChange={(v) => patchAdvancedGovernance(["checksum_algorithm"], v)} />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => saveCommandSection("governance_config", "Governança de dados salva.")}>Salvar dados</Button>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "presets" && (
                  <>
                    <SettingGroup title="Presets operacionais" description="Carregue conjuntos de parametrizações para acelerar uma configuração completa. O preset não publica automaticamente: ele apenas preenche a memória da tela.">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <CommandSelect label="Preset" value={advancedPreset} onChange={setAdvancedPreset} options={[["operacao_completa", "Operação completa"], ["seguranca_maxima", "Segurança máxima"], ["operacao_campo", "Operação de campo"]]} />
                        <div className="md:col-span-2 flex items-end">
                          <Button className="w-full" onClick={() => loadPresetIntoSections(advancedPreset)}>Aplicar preset à memória</Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                          ["Operação completa", "Ativa governança, segurança, loading, notificações, relatórios e geoespacial em modo robusto."],
                          ["Segurança máxima", "Eleva autenticação, auditoria, proteção de rotas e políticas de alteração."],
                          ["Operação de campo", "Prioriza GPS, sincronização, notificações e fluxo de execução em campo."],
                        ].map(([title, desc]) => (
                          <div key={title} className="rounded-lg border border-border p-3">
                            <p className="text-xs font-semibold">{title}</p>
                            <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{desc}</p>
                          </div>
                        ))}
                      </div>
                      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                        <p className="text-xs font-semibold">Regra</p>
                        <p className="text-[10px] text-muted-foreground mt-1">Presets são atalhos administrativos. A publicação continua obedecendo validação, auditoria e persistência da API oficial.</p>
                      </div>
                    </SettingGroup>
                  </>
                )}

                {advancedTab === "diagnostico" && (
                  <>
                    <SettingGroup title="Diagnóstico central do Platform Admin" description="Visão consolidada da configuração efetiva usada pela própria Central de Comando.">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <CommandBadge label="Admin global" value="SIM" tone="default" />
                        <CommandBadge label="Catálogo oficial" value={`${Object.keys(moduleCatalog || {}).length} módulos`} />
                        <CommandBadge label="Matriz de perfis" value={`${normalizedRoles.length} perfis`} />
                        <CommandBadge label="Eventos de notificação" value={`${Object.keys(notificationConfig.events || {}).length}`} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-lg border border-border p-3 space-y-2">
                          <p className="text-xs font-semibold">Perfil Admin</p>
                          <p className="text-[10px] text-muted-foreground">A matriz não deve restringir o Administrador. A autorização global permanece responsabilidade do motor central.</p>
                          <div className="grid grid-cols-2 gap-2">
                            <CommandBadge label="Módulos" value="TODOS" />
                            <CommandBadge label="Ações" value="TODAS" />
                            <CommandBadge label="Escopo" value="GLOBAL" />
                            <CommandBadge label="Configuração" value="GLOBAL" />
                          </div>
                        </div>
                        <div className="rounded-lg border border-border p-3 space-y-2">
                          <p className="text-xs font-semibold">Contrato do Platform Admin</p>
                          <p className="text-[10px] text-muted-foreground">O painel administra configuração; ele não cria autorização paralela para as rotas de negócio.</p>
                          <div className="space-y-1">
                            {["catálogo", "módulos", "ações", "features", "escopo", "notificações", "relatórios", "loading", "segurança", "geoespacial"].map((item) => (
                              <div key={item} className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-[10px]">
                                <span>{item}</span>
                                <Badge variant="outline">parametrizável</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <JsonSummary
                        value={{
                          governance,
                          loading: loadingCfg,
                          module_registry: moduleRegistry,
                          notifications: notificationConfig,
                          reports: reportConfig,
                          security: securityConfig,
                          geospatial: geoConfig,
                          ui: uiGovernance,
                          integrations: integrationCfg,
                          selected_role: selectedAdvancedRole,
                          selected_module: advancedModule,
                        }}
                        title="Snapshot atual da Central de Comando"
                        maxHeight={640}
                      />
                    </SettingGroup>
                  </>
                )}
              </div>
            </div>
          </RobustCommandPanel>
        </TabsContent>

        {/* ================= ERP (HUB) ================= */}
        <TabsContent value="erp" className="mt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { to: "/identidade-visual", icon: Palette, title: "Identidade Visual do ERP", desc: "Nome, logotipo, favicon, temas claro/escuro, cores institucionais, fontes e correção automática de contraste (IA)." },
              { to: "/administracao", icon: Settings, title: "Parametrizações Operacionais", desc: "Fluxos de ocorrência, SLA, reincidência, índices, Double Check, IA de evidências e visibilidade dos dashboards por perfil." },
              { to: "/configuracao-operacional", icon: SlidersHorizontal, title: "Configuração Operacional", desc: "Taxonomia de ocorrências, campos dinâmicos, espécies de roedores e vestígios das Salas Elétricas." },
              { to: "/usuarios", icon: ShieldCheck, title: "Usuários e Permissões", desc: "Categorias de usuários, matriz de permissões por módulo, cliente, unidade e área." },
              { to: "/admin-dados", icon: Layers, title: "Administração de Dados", desc: "Base demonstrativa, importações e manutenção de dados." },
              { to: "/auditoria", icon: FileText, title: "Auditoria", desc: "Toda alteração de configuração registra usuário, data, hora, valor anterior e novo valor." },
            ].map(({ to, icon: Icon, title, desc }) => (
              <Link key={to} to={to} className="rounded-lg border border-border bg-card p-4 hover:border-primary/50 transition-colors group" data-testid={`erp-hub-${to.slice(1)}`}>
                <Icon size={18} className="text-primary" />
                <p className="font-heading font-semibold text-sm mt-2 group-hover:text-primary transition-colors">{title}</p>
                <p className="text-xs text-muted-foreground mt-1">{desc}</p>
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
